import { CreateOrderModal } from '@/components/orders/CreateOrderModal';
import { getNativePickupPoints } from '../lib/pickup-points';
import DOMPurify from 'dompurify';
import { MessageTemplateEditor } from '../components/messages/MessageTemplateEditor';
import type { MessageTemplateItem } from '../components/messages/MessageTemplateChoices';
import { formatComplaintHistory } from '../lib/complaint-history';
import { formatResendHistory } from '../lib/resend-status';
import { ResendOrderStatus } from '../components/ResendOrderStatus';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { ComplaintCasePanel, ComplaintStatusControl, ComplaintCorrespondencePanel, ShipmentTimeline } from '../components/complaints/ComplaintCasePanel';
import { sortOrdersByStatusTree } from '../lib/status-label-order';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PDFDocument } from 'pdf-lib';
import {
  ShoppingCart,
  ShoppingBag,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  X,
  Mail,
  Phone,
  CreditCard,
  Truck,
  FileText,
  Package,
  PackageCheck,
  User,
  MessageSquare,
  History,
  Minus,
  ArrowUpDown,
  Tag,
  Download,
  Trash2,
  ArrowUp,
  ArrowDown,
  PackagePlus,
  Receipt,
  Filter,
  Edit3,
  Edit2,
  Copy,
  Zap,
  MapPin,
  ExternalLink,
  Check,
  Send,
  Loader2,
  RotateCcw,
  FileDown,
  Eye,
  CircleSlash,
  Plus,
  PenLine,
  Users,
  AlertTriangle,
  StickyNote,
  Hourglass,
} from 'lucide-react';
import { orderApi, storeApi, shipmentApi, courierApi, invoiceApi, emailApi, automationApi, refundApi, portalApi, userApi, customerNotesApi, messagesApi, blacklistApi, type Order, type Store, type OrderHistoryEntry, type EmailLog, type ManualAutomation, type RefundInfo, type CustomerPurchaseHistory, type AdminComplaint, type PortalActivity, type CustomerNote } from '../lib/api';
import api from '../lib/api';
import * as LucideIcons from 'lucide-react';
import { useBackgroundJobs } from '../contexts/BackgroundJobsContext';
import { useNotification } from '../contexts/NotificationContext';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { EmptyState } from '../components/EmptyState';
import { translateError } from '../utils/errorTranslations';
import { SkullIcon } from '../components/icons/SkullIcon';
import { Checkbox } from '../components/ui/Checkbox';
import { PageHeader } from '../components/ui/PageHeader';
import { useTheme } from '../contexts/ThemeContext';
import { statusColor, statusTextColor } from '../lib/statusColor';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { DataList } from '../components/ui/DataList';
import { FormField } from '../components/ui/FormField';
import { Select } from '../components/ui/Select';
import { FinanceOrderEvidence } from '../components/finance/FinanceOrderEvidence';
import { useAuthStore } from '../store/auth';

type SortField = 'externalCreatedAt' | 'orderNumber' | 'totalAmount' | 'shippingMethod' | 'status' | 'paymentMethod' | 'customerName' | 'statusChangedAt';
type SortOrder = 'asc' | 'desc';

const SORT_OPTIONS: { value: SortField; label: string }[] = [
  { value: 'externalCreatedAt', label: 'Data złożenia zamówienia' },
  { value: 'statusChangedAt', label: 'Data zmiany statusu' },
  { value: 'status', label: 'Status' },
  { value: 'orderNumber', label: 'ID zamówienia' },
  { value: 'totalAmount', label: 'Łączna cena' },
  { value: 'shippingMethod', label: 'Sposób wysyłki' },
  { value: 'paymentMethod', label: 'Płatności' },
  { value: 'customerName', label: 'Imię i nazwisko' },
];


const PER_PAGE_OPTIONS = [10, 20, 50, 100, 250, 500];

const OFFER_NOT_AVAILABLE_MESSAGE = 'Wybrana oferta InPost wygasła albo nie jest już dostępna. Ponów utworzenie przesyłki, aby pobrać nową ofertę. Jeśli błąd się powtórzy, sprawdź wybraną usługę, Paczkomat, adres odbiorcy oraz gabaryt i wagę paczki.';

function getEmailFailureMessage(error?: string): string {
  if (!error) return 'Nie udało się wysłać wiadomości. Otwórz szczegóły lub ponów próbę.';
  return translateError(error, 'email').message;
}

export interface StatusGroup {
  id: string;
  name: string;
  color: string;
  parentId: string | null;
  storeId: string | null;
  position: number;
  children: StatusGroup[];
  store?: {
    id: string;
    name: string;
  };
  _count?: {
    orders: number;
  };
  _totalCount?: number; // Cumulative count including children
  settings?: {
    customTextColor?: string;
  };
}

// LocalStorage keys for persisting state
const STORAGE_KEYS = {
  sortBy: 'ecomflow_orders_sortBy',
  sortOrder: 'ecomflow_orders_sortOrder',
  perPage: 'ecomflow_orders_perPage',
  advancedFilters: 'ecomflow_orders_advancedFilters',
  storeId: 'ecomflow_orders_storeId',
};

// Advanced filters type
interface AdvancedFilters {
  orderNumber: string;
  customerName: string;
  productName: string;
  dateFrom: string;
  dateTo: string;
  amountFrom: string;
  amountTo: string;
  paymentStatus: string;
  deliveryStatus: string;
}

// Default advanced filters
const DEFAULT_ADVANCED_FILTERS: AdvancedFilters = {
  orderNumber: '',
  customerName: '',
  productName: '',
  dateFrom: '',
  dateTo: '',
  amountFrom: '',
  amountTo: '',
  paymentStatus: '',
  deliveryStatus: '',
};

// L8: WooCommerce status translation map
const WOO_STATUS_MAP: Record<string, string> = {
  'completed': 'Zrealizowane',
  'processing': 'W realizacji',
  'pending': 'Oczekujące',
  'on-hold': 'Wstrzymane',
  'cancelled': 'Anulowane',
  'refunded': 'Zwrócone',
  'failed': 'Nieudane',
  'trash': 'Kosz',
  'checkout-draft': 'Szkic',
};

function translateWooStatus(status: string): string {
  return WOO_STATUS_MAP[status] || WOO_STATUS_MAP[status.replace('wc-', '')] || status;
}

export function OrdersPage() {
  const {
    startJob,
    addJobResult,
    completeJob,
    failJob,
    updateJob,
    activeJob,
    isJobCancelled,
    updateJobProgress,
  } = useBackgroundJobs();
  const { showSuccess, showError, showWarning, showInfo, showNotification } = useNotification();
  const confirmDialog = useConfirmDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const refreshAfterShipmentBatchRef = useRef<() => void>(() => undefined);

  // Read initial values from URL and localStorage
  const initialSearch = searchParams.get('search') || '';
  const initialStatus = searchParams.get('status') || null;
  const shipmentBatchFilter = searchParams.get('shipmentBatch') || undefined;
  const initialSortBy = (localStorage.getItem(STORAGE_KEYS.sortBy) as SortField) || 'externalCreatedAt';
  const initialSortOrder = (localStorage.getItem(STORAGE_KEYS.sortOrder) as SortOrder) || 'desc';
  const initialPerPage = parseInt(localStorage.getItem(STORAGE_KEYS.perPage) || '20', 10);
  const initialStoreId = localStorage.getItem(STORAGE_KEYS.storeId) || '';
  const initialAdvancedFilters: AdvancedFilters = (() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.advancedFilters);
      return stored ? JSON.parse(stored) as AdvancedFilters : DEFAULT_ADVANCED_FILTERS;
    } catch {
      return DEFAULT_ADVANCED_FILTERS;
    }
  })();

  // Capture ?id= param on mount for deep-linking from other pages (e.g. complaints)
  const deepLinkOrderIdRef = useRef(searchParams.get('id'));
  // Capture ?action= — np. "refund" z Messages karty klienta → otwórz refund form.
  const deepLinkActionRef = useRef(searchParams.get('action'));

  const [orders, setOrders] = useState<Order[]>([]);
  // N2 (2026-04-22): zbiory znormalizowanych identyfikatorów na czarnej liście,
  // żeby OrderRow mógł sprawdzać status lokalnie bez per-wiersz request.
  const [blacklistedEmails, setBlacklistedEmails] = useState<Set<string>>(new Set());
  const [blacklistedPhones, setBlacklistedPhones] = useState<Set<string>>(new Set());
  const [stores, setStores] = useState<Store[]>([]);
  const [statuses, setStatuses] = useState<StatusGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const syncTimestampsRef = useRef<number[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const selectedOrderRef = useRef<Order | null>(null);
  // Keep ref in sync — used by handleOrderUpdated to avoid stale closure issues
  useEffect(() => { selectedOrderRef.current = selectedOrder; }, [selectedOrder]);
  const isFirstPerPageRender = useRef(true);
  // Blokuje zapis pagination.limit do serwera dopóki nie załadujemy preferencji z
  // serwera. Bez tego race: 2× mount w React.StrictMode (dev) → 2. setup odpala
  // PATCH z initialPerPage (=20 jeśli localStorage wyczyszczone przez logout() w
  // auth store) ZANIM loadData pobierze server ordersPerPage=500. Efekt: server
  // nadpisany 20 i po każdym reloginie user widzi 20 zamiast zapisanych 500.
  const [serverPrefsLoaded, setServerPrefsLoaded] = useState(false);
  const [allOrderIds, setAllOrderIds] = useState<string[]>([]); // All order IDs in current filter for navigation
  const [currentOrderIndex, setCurrentOrderIndex] = useState<number>(-1); // Index in allOrderIds
  const [selectedStatus, setSelectedStatus] = useState<string | null>(initialStatus);
  const [expandedStatuses, setExpandedStatuses] = useState<Set<string>>(() => {
    // L2: Restore expanded statuses from sessionStorage
    try {
      const saved = sessionStorage.getItem('ecomflow_expandedStatuses');
      if (saved) return new Set(JSON.parse(saved));
    } catch { /* ignore */ }
    return new Set();
  });
  const [totalOrderCount, setTotalOrderCount] = useState(0);
  const [trashCount, setTrashCount] = useState(0);
  const [unassignedCount, setUnassignedCount] = useState(0);
  // tenantDefaultStatusId removed - now per-store in store settings
  const [pagination, setPagination] = useState({
    page: 1,
    limit: initialPerPage,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    search: initialSearch,
    storeId: initialStoreId,
    status: '',
  });

  // Debounced search to prevent race conditions
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const abortControllerRef = useRef<AbortController | null>(null);
  const syncRecoveryPromiseRef = useRef<ReturnType<typeof orderApi.recoverSync> | null>(null);
  const syncRecoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncRecoveryInfoShownRef = useRef(false);
  const syncRecoveryWarningShownRef = useRef(false);

  // Advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>(initialAdvancedFilters);

  // Selection state
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [selectAllInStatus, setSelectAllInStatus] = useState(false);
  const [excludedOrders, setExcludedOrders] = useState<Set<string>>(new Set());
  const [selectDropdownOpen, setSelectDropdownOpen] = useState(false);

  // Sorting state
  const [sortBy, setSortBy] = useState<SortField>(initialSortBy);
  const [sortOrder, setSortOrder] = useState<SortOrder>(initialSortOrder);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);

  // Bulk action dropdowns
  const [bulkStatusDropdownOpen, setBulkStatusDropdownOpen] = useState(false);
  const [bulkStatusSearch, setBulkStatusSearch] = useState('');
  const [bulkStatusLoading, setBulkStatusLoading] = useState(false);

  // Display settings
  const [showUnassignedStatus, setShowUnassignedStatus] = useState(false);
  const [toolbarIcons, setToolbarIcons] = useState({
    showStatusChange: true,
    showLabels: true,
    showInvoices: true,
  });

  // Create manual order modal
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [similarOrderSource, setSimilarOrderSource] = useState<Order | null>(null);

  // Refs for click-outside detection
  const selectDropdownRef = useRef<HTMLDivElement>(null);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const bulkStatusDropdownRef = useRef<HTMLDivElement>(null);
  const perPageDropdownRef = useRef<HTMLDivElement>(null);
  const [perPageDropdownOpen, setPerPageDropdownOpen] = useState(false);

  // Close all dropdowns
  const closeAllDropdowns = useCallback(() => {
    setSelectDropdownOpen(false);
    setSortDropdownOpen(false);
    setBulkStatusDropdownOpen(false);
    setBulkStatusSearch('');
    setPerPageDropdownOpen(false);
  }, []);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (selectDropdownRef.current && !selectDropdownRef.current.contains(target)) {
        setSelectDropdownOpen(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(target)) {
        setSortDropdownOpen(false);
      }
      if (bulkStatusDropdownRef.current && !bulkStatusDropdownRef.current.contains(target)) {
        setBulkStatusDropdownOpen(false);
        setBulkStatusSearch('');
      }
      if (perPageDropdownRef.current && !perPageDropdownRef.current.contains(target)) {
        setPerPageDropdownOpen(false);
      }
    };

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showCreateOrderModal) return;
        closeAllDropdowns();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [closeAllDropdowns, showCreateOrderModal]);

  useEffect(() => {
    // Clean up stale localStorage key from previous versions
    localStorage.removeItem('statusMenuExpandedState');
    loadData();
  }, []);

  // Deep-link: if ?id=<orderId> is present, open that order's modal
  useEffect(() => {
    const orderId = deepLinkOrderIdRef.current;
    if (!orderId) return;
    deepLinkOrderIdRef.current = null; // Only fire once
    (async () => {
      try {
        const { data } = await orderApi.getOne(orderId);
        setSelectedOrder(data);
      } catch (err) {
        console.error('Failed to open linked order:', err);
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          const { user, tenant } = useAuthStore.getState();
          const accountName = [user?.firstName, tenant?.name].filter(Boolean).join(' / ');
          showWarning(
            'Zamówienie niedostępne na tym koncie',
            `${accountName ? `Jesteś zalogowany jako ${accountName}. ` : ''}Nie znaleziono tego zamówienia na aktualnym koncie. Jeśli sklep obsługujesz na innym koncie, skopiuj oryginalny link z maila i otwórz go w oknie przeglądarki zalogowanym na właściwe konto.`,
          );
        } else {
          showNotification({
            type: 'error',
            title: 'Nie udało się otworzyć zamówienia',
            message: 'Nie udało się pobrać danych zamówienia. Spróbuj ponownie za chwilę, otwierając oryginalny link z wiadomości.',
            autoClose: false,
          });
        }
      }
    })();
  }, []);

  // Debounce search input to prevent race conditions
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    loadOrders();
  }, [pagination.page, pagination.limit, debouncedSearch, filters.storeId, filters.status, selectedStatus, shipmentBatchFilter, sortBy, sortOrder, advancedFilters]);

  useEffect(() => {
    if (!shipmentBatchFilter) return;
    setSelectedStatus(null);
    setPagination(prev => prev.page === 1 ? prev : { ...prev, page: 1 });
  }, [shipmentBatchFilter]);

  // Persist search and status to URL params
  useEffect(() => {
    const newParams = new URLSearchParams();
    if (filters.search) {
      newParams.set('search', filters.search);
    }
    if (selectedStatus && !shipmentBatchFilter) {
      newParams.set('status', selectedStatus);
    }
    if (shipmentBatchFilter) {
      newParams.set('shipmentBatch', shipmentBatchFilter);
    }
    setSearchParams(newParams, { replace: true });
  }, [filters.search, selectedStatus, shipmentBatchFilter, setSearchParams]);

  // Persist sorting, pagination, and filters to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.sortBy, sortBy);
  }, [sortBy]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.sortOrder, sortOrder);
  }, [sortOrder]);

  useEffect(() => {
    // Skip server sync until loadData pobrało preferencje. Inaczej race w
    // StrictMode nadpisze server dzisiejszą wartością fallback (20).
    if (!serverPrefsLoaded) return;
    if (isFirstPerPageRender.current) {
      isFirstPerPageRender.current = false;
      return;
    }
    localStorage.setItem(STORAGE_KEYS.perPage, pagination.limit.toString());
    // Persist to server so it survives logout
    userApi.updatePreferences({ ordersPerPage: pagination.limit }).catch(() => {});
  }, [pagination.limit, serverPrefsLoaded]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.storeId, filters.storeId);
  }, [filters.storeId]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.advancedFilters, JSON.stringify(advancedFilters));
  }, [advancedFilters]);

  const loadData = async () => {
    try {
      const [storesRes, statusesRes, ordersCountRes, trashCountRes, unassignedCountRes, settingsRes, prefsRes] = await Promise.all([
        storeApi.getAll(),
        api.get<StatusGroup[]>('/statuses/tree'), // Use tree endpoint for hierarchical structure with counts
        orderApi.getAll({ page: 1, limit: 1 }),
        orderApi.getAll({ page: 1, limit: 1, status: 'Kosz' }),
        orderApi.getAll({ page: 1, limit: 1, status: 'Nieprzypisany' }),
        api.get<{ storeOrder?: string[]; showUnassignedStatus?: boolean; toolbarIcons?: { showStatusChange: boolean; showLabels: boolean; showInvoices: boolean }; defaultManualOrderStatusId?: string | null }>('/settings'),
        userApi.getPreferences().catch(() => ({ data: {} as Record<string, unknown> })),
      ]);

      // Apply user preferences for ordersPerPage (from server, survives logout)
      const serverPerPage = (prefsRes.data as Record<string, unknown>)?.ordersPerPage;
      if (typeof serverPerPage === 'number' && serverPerPage > 0) {
        setPagination(prev => {
          if (prev.limit !== serverPerPage) {
            localStorage.setItem(STORAGE_KEYS.perPage, serverPerPage.toString());
            return { ...prev, limit: serverPerPage, page: 1 };
          }
          return prev;
        });
      }
      // Unlock server-persist effect — from now on any change to pagination.limit
      // is user-initiated and should be written to DB.
      setServerPrefsLoaded(true);

      // Sort stores according to saved order from settings
      const allStores = storesRes.data;
      const storeOrder = settingsRes.data.storeOrder || [];
      setShowUnassignedStatus(settingsRes.data.showUnassignedStatus ?? false);
      if (settingsRes.data.toolbarIcons) {
        setToolbarIcons(settingsRes.data.toolbarIcons);
      }
      // defaultManualOrderStatusId is now per-store (in store settings)

      if (storeOrder.length > 0) {
        const orderedStores: Store[] = [];
        const unorderedStores: Store[] = [];

        // First add stores in the saved order
        for (const storeId of storeOrder) {
          const store = allStores.find(s => s.id === storeId);
          if (store) {
            orderedStores.push(store);
          }
        }

        // Then add any stores not in the order (new stores)
        for (const store of allStores) {
          if (!storeOrder.includes(store.id)) {
            unorderedStores.push(store);
          }
        }

        setStores([...orderedStores, ...unorderedStores]);
      } else {
        setStores(allStores);
      }
      // Tree endpoint already returns hierarchical structure, no need to rebuild
      const statusData = statusesRes.data || [];
      setStatuses(statusData);

      setTotalOrderCount(ordersCountRes.data.pagination.total);
      setTrashCount(trashCountRes.data.pagination.total);
      setUnassignedCount(unassignedCountRes.data.pagination.total);

      // N2 (2026-04-22): czarna lista - wczytaj zbiory dla szybkiego matcha w OrderRow.
      await reloadBlacklistSets();
    } catch (err) {
      console.error('Failed to load data:', err);
    }
  };

  // R5.1 (2026-04-22): refresh blacklist sets - wywoływane po toggle w karcie
  // zamówienia (przez callback onBlacklistChanged).
  const reloadBlacklistSets = useCallback(async () => {
    try {
      const blacklistRes = await blacklistApi.list();
      const emails = new Set<string>();
      const phones = new Set<string>();
      for (const entry of blacklistRes.data.entries) {
        if (entry.identifierType === 'email') emails.add(entry.identifier);
        else if (entry.identifierType === 'phone') phones.add(entry.identifier);
      }
      setBlacklistedEmails(emails);
      setBlacklistedPhones(phones);
    } catch (blErr) {
      console.error('Failed to load blacklist:', blErr);
    }
  }, []);

  const loadOrders = async () => {
    // Cancel any pending request to prevent race conditions
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const currentController = abortControllerRef.current;

    try {
      setLoading(true);
      setSelectedOrders(new Set()); // Clear selection when loading new orders
      setSelectAllInStatus(false);
      setExcludedOrders(new Set());

      let recoveryResult: Awaited<ReturnType<typeof orderApi.recoverSync>>['data'] | null = null;
      let recoveryPromise = syncRecoveryPromiseRef.current;
      try {
        if (!recoveryPromise) {
          recoveryPromise = orderApi.recoverSync();
          syncRecoveryPromiseRef.current = recoveryPromise;
        }
        const recoveryResponse = await recoveryPromise;
        recoveryResult = recoveryResponse.data;
      } catch (recoveryError) {
        console.error('Emergency order sync check failed:', recoveryError);
      } finally {
        if (syncRecoveryPromiseRef.current === recoveryPromise) {
          syncRecoveryPromiseRef.current = null;
        }
      }

      const { data } = await orderApi.getAll({
        page: pagination.page,
        limit: pagination.limit,
        search: debouncedSearch || undefined,
        storeId: filters.storeId || undefined,
        shipmentBatchId: shipmentBatchFilter,
        status: shipmentBatchFilter ? undefined : selectedStatus || filters.status || undefined,
        sortBy,
        sortOrder,
        // Advanced filters
        orderNumber: advancedFilters.orderNumber || undefined,
        customerName: advancedFilters.customerName || undefined,
        productName: advancedFilters.productName || undefined,
        dateFrom: advancedFilters.dateFrom || undefined,
        dateTo: advancedFilters.dateTo || undefined,
        amountFrom: advancedFilters.amountFrom ? Number(advancedFilters.amountFrom) : undefined,
        amountTo: advancedFilters.amountTo ? Number(advancedFilters.amountTo) : undefined,
        paymentStatus: advancedFilters.paymentStatus || undefined,
        deliveryStatus: advancedFilters.deliveryStatus || undefined,
      });

      // Only update state if this request wasn't cancelled
      if (!currentController.signal.aborted) {
        setOrders(data.data);
        setPagination(prev => ({
          ...prev,
          total: data.pagination.total,
          totalPages: data.pagination.totalPages,
        }));
        // Update total count only when not filtering
        if (!selectedStatus && !filters.status && !debouncedSearch && !filters.storeId && !shipmentBatchFilter) {
          setTotalOrderCount(data.pagination.total);
        }

        if (recoveryResult && recoveryResult.workerStatus !== 'healthy') {
          if (
            recoveryResult.status === 'completed'
            && !syncRecoveryInfoShownRef.current
          ) {
            syncRecoveryInfoShownRef.current = true;
            showInfo(
              'Logistyka działa w trybie awaryjnym',
              recoveryResult.created > 0
                ? `Pobrano ${recoveryResult.created} nowych zamówień z WooCommerce.`
                : 'Sprawdzono najnowsze zamówienia w WooCommerce.',
            );
          }

          if (
            ['failed', 'unavailable'].includes(recoveryResult.status)
            && !syncRecoveryWarningShownRef.current
          ) {
            syncRecoveryWarningShownRef.current = true;
            showWarning(
              'Problem z synchronizacją Logistyki',
              'Worker jest niedostępny, a awaryjne pobranie zamówień nie zakończyło się poprawnie. Ecomflow spróbuje ponownie automatycznie.',
            );
          }

          if (!['disabled', 'no_stores'].includes(recoveryResult.status)) {
            if (syncRecoveryTimerRef.current) clearTimeout(syncRecoveryTimerRef.current);
            const retryDelay = ['running', 'failed', 'unavailable'].includes(recoveryResult.status)
              ? 10_000
              : 60_000;
            syncRecoveryTimerRef.current = setTimeout(() => {
              syncRecoveryTimerRef.current = null;
              void loadOrders();
            }, retryDelay);
          }
        }
      }
    } catch (err) {
      // Ignore errors from cancelled requests
      if (err instanceof Error && err.name === 'CanceledError') {
        return;
      }
      console.error('Failed to load orders:', err);
    } finally {
      // Only set loading false if this request wasn't cancelled
      if (!currentController.signal.aborted) {
        setLoading(false);
      }
    }
  };

  useEffect(() => () => {
    if (syncRecoveryTimerRef.current) clearTimeout(syncRecoveryTimerRef.current);
  }, []);

  refreshAfterShipmentBatchRef.current = () => {
    void loadOrders();
    void loadData();
  };

  useEffect(() => {
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    const handleShipmentBatchUpdate = () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => refreshAfterShipmentBatchRef.current(), 1200);
    };
    window.addEventListener('ecomflow:shipment-batch-updated', handleShipmentBatchUpdate);
    return () => {
      window.removeEventListener('ecomflow:shipment-batch-updated', handleShipmentBatchUpdate);
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, []);

  const prefillCreateOrderFromOrder = (order: Order) => {
    setSimilarOrderSource(order);
    setShowCreateOrderModal(true);
  };

  const SYNC_RATE_LIMIT = 3; // max syncs per hour
  const SYNC_RATE_WINDOW = 60 * 60 * 1000; // 1 hour in ms

  const syncAllStores = async () => {
    if (syncing) return;

    // Rate limit check
    const now = Date.now();
    syncTimestampsRef.current = syncTimestampsRef.current.filter(t => now - t < SYNC_RATE_WINDOW);
    if (syncTimestampsRef.current.length >= SYNC_RATE_LIMIT) {
      const oldestTs = syncTimestampsRef.current[0];
      const minutesLeft = Math.ceil((SYNC_RATE_WINDOW - (now - oldestTs)) / 60000);
      showWarning('Limit synchronizacji', `Wykorzystałeś limit ${SYNC_RATE_LIMIT} synchronizacji na godzinę. Spróbuj ponownie za ${minutesLeft} min.`);
      return;
    }

    try {
      setSyncing(true);
      syncTimestampsRef.current.push(now);
      // Get all active stores
      const activeStores = stores.filter(s => s.isActive);

      if (activeStores.length === 0) {
        showWarning('Brak sklepów', 'Brak aktywnych sklepów do synchronizacji. Sprawdź ustawienia sklepów.');
        setSyncing(false);
        return;
      }

      // Sync all active stores and collect results
      const syncResults = await Promise.all(
        activeStores.map(async store => {
          try {
            const { data } = await storeApi.sync(store.id);
            return { store: store.name, ...data, error: null };
          } catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Nieznany błąd';
            return { store: store.name, success: false, created: 0, updated: 0, total: 0, error: errorMsg };
          }
        })
      );

      // Aggregate results
      const totals = syncResults.reduce(
        (acc, r) => ({
          created: acc.created + (r.created || 0),
          updated: acc.updated + (r.updated || 0),
          total: acc.total + (r.total || 0),
          errors: acc.errors + (r.error ? 1 : 0),
        }),
        { created: 0, updated: 0, total: 0, errors: 0 }
      );

      // Reload data after sync
      await loadData();
      await loadOrders();

      // Show results to user - simplified report with counts only
      if (totals.errors > 0) {
        showWarning(
          'Synchronizacja z błędami',
          `Pobrano: ${totals.total}, Utworzono: ${totals.created}, Zaktualizowano: ${totals.updated}. ${totals.errors} sklepów zgłosiło błędy - sprawdź ustawienia sklepów.`
        );
      } else {
        showSuccess('sync', {
          details: `Pobrano: ${totals.total} | Utworzono: ${totals.created} | Zaktualizowano: ${totals.updated}`
        });
      }
    } catch (err) {
      console.error('Failed to sync:', err);
      showError(err, 'sync');
    } finally {
      setSyncing(false);
    }
  };

  const toggleStatusExpand = (id: string) => {
    setExpandedStatuses(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      // L2: Persist to sessionStorage
      try { sessionStorage.setItem('ecomflow_expandedStatuses', JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };

  const getTotalOrderCount = (): number => {
    return totalOrderCount;
  };

  const handleOrderUpdated = (updatedOrder: Order, options?: { closeModal?: boolean; navigateTo?: boolean }) => {
    console.log('[OrderUpdated] Called with order:', updatedOrder.orderNumber, 'status:', updatedOrder.status, 'navigateTo:', options?.navigateTo);

    // Guard: if the user has already navigated to a different order, do NOT overwrite selectedOrder.
    // This prevents race conditions where delayed refreshes (e.g., after shipment creation) reset the modal.
    // Exception: navigateTo=true means intentional navigation (e.g. clicking LTV history order)
    const currentlyViewing = selectedOrderRef.current;
    const isStaleUpdate = currentlyViewing && currentlyViewing.id !== updatedOrder.id;
    if (isStaleUpdate && !options?.navigateTo) {
      console.log('[OrderUpdated] Stale update ignored — user is now viewing', currentlyViewing.orderNumber, 'not', updatedOrder.orderNumber);
      // Still update the orders list in the background (if the order is on the current page)
      setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
      return;
    }

    // Find the old order to check if status changed
    const oldOrder = orders.find(o => o.id === updatedOrder.id);
    const statusChanged = oldOrder && oldOrder.status !== updatedOrder.status;
    console.log('[OrderUpdated] Old status:', oldOrder?.status, 'New status:', updatedOrder.status, 'Changed:', statusChanged);

    // Check if the order still matches the current filter
    const matchesFilter = !selectedStatus ||
      updatedOrder.status === selectedStatus ||
      (selectedStatus === 'Kosz' && updatedOrder.status === 'Kosz');

    if (statusChanged && !matchesFilter) {
      console.log('[OrderUpdated] Status changed and no longer matches filter - removing from list but keeping modal open');
      // Order no longer matches filter - remove from list
      setOrders(prev => prev.filter(o => o.id !== updatedOrder.id));
      // Keep modal open with updated order data - user can close manually
      if (!options?.closeModal) {
        setSelectedOrder(updatedOrder);
      }
      // Update counts
      loadData();
    } else {
      console.log('[OrderUpdated] Updating order in place, setting selectedOrder');
      // Update order in place
      setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
      if (!options?.closeModal) {
        setSelectedOrder(updatedOrder);
      }
      // If status changed, refresh counts
      if (statusChanged) {
        loadData();
      }
    }
  };

  // Load all order IDs for cross-page navigation
  const loadOrderIds = async () => {
    try {
      const { data } = await orderApi.getIds({
        search: filters.search || undefined,
        storeId: filters.storeId || undefined,
        shipmentBatchId: shipmentBatchFilter,
        status: shipmentBatchFilter ? undefined : selectedStatus || filters.status || undefined,
        sortBy,
        sortOrder,
        orderNumber: advancedFilters.orderNumber || undefined,
        customerName: advancedFilters.customerName || undefined,
        productName: advancedFilters.productName || undefined,
        dateFrom: advancedFilters.dateFrom || undefined,
        dateTo: advancedFilters.dateTo || undefined,
        amountFrom: advancedFilters.amountFrom ? Number(advancedFilters.amountFrom) : undefined,
        amountTo: advancedFilters.amountTo ? Number(advancedFilters.amountTo) : undefined,
        paymentStatus: advancedFilters.paymentStatus || undefined,
        deliveryStatus: advancedFilters.deliveryStatus || undefined,
      });
      setAllOrderIds(data.ids);
      return data.ids;
    } catch (err) {
      console.error('Failed to load order IDs:', err);
      return [];
    }
  };

  // Open order modal with navigation context
  const openOrderModal = async (order: Order) => {
    setSelectedOrder(order);
    const ids = await loadOrderIds();
    const index = ids.indexOf(order.id);
    setCurrentOrderIndex(index);
  };

  // Navigate to order by index (fetches order if not in current page)
  // Uses a navigation counter to discard stale responses when user navigates quickly
  // and an AbortController to cancel in-flight requests
  const navigationCounterRef = useRef(0);
  const navigationAbortRef = useRef<AbortController | null>(null);
  const navigateToOrder = async (index: number) => {
    if (index < 0 || index >= allOrderIds.length) return;

    // Abort any in-flight navigation request
    if (navigationAbortRef.current) {
      navigationAbortRef.current.abort();
    }

    const orderId = allOrderIds[index];
    const navId = ++navigationCounterRef.current;
    setCurrentOrderIndex(index);

    // Check if order is in current page
    const orderInPage = orders.find(o => o.id === orderId);
    if (orderInPage) {
      setSelectedOrder(orderInPage);
    } else {
      // Fetch order from server
      const abortController = new AbortController();
      navigationAbortRef.current = abortController;
      try {
        const { data } = await orderApi.getOne(orderId);
        // Only update if this is still the latest navigation request and not aborted
        if (navId === navigationCounterRef.current && !abortController.signal.aborted) {
          setSelectedOrder(data);
        }
      } catch (err: unknown) {
        // Ignore aborted requests
        if (abortController.signal.aborted) return;
        // Handle rate limiting (429) — retry once after delay
        const isRateLimit = err && typeof err === 'object' && 'response' in err &&
          (err as { response?: { status?: number } }).response?.status === 429;
        if (isRateLimit && navId === navigationCounterRef.current) {
          try {
            await new Promise(resolve => setTimeout(resolve, 1500));
            if (navId === navigationCounterRef.current && !abortController.signal.aborted) {
              const { data } = await orderApi.getOne(orderId);
              if (navId === navigationCounterRef.current && !abortController.signal.aborted) {
                setSelectedOrder(data);
              }
            }
          } catch {
            if (!abortController.signal.aborted) {
              console.error('Failed to load order after retry:', orderId);
            }
          }
        } else {
          console.error('Failed to load order:', err);
        }
      } finally {
        // Navigation complete — AbortController handles stale request cleanup
      }
    }
  };

  // Selection functions
  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
    // Track exclusions when "select all in status" is active
    if (selectAllInStatus) {
      setExcludedOrders(prev => {
        const next = new Set(prev);
        if (next.has(orderId)) {
          next.delete(orderId); // Re-selected → remove from excluded
        } else {
          next.add(orderId); // Deselected → add to excluded
        }
        return next;
      });
    }
  };

  const toggleSelectAll = () => {
    if (selectedOrders.size === orders.length && !selectAllInStatus) {
      setSelectedOrders(new Set());
      setSelectAllInStatus(false);
      setExcludedOrders(new Set());
    } else {
      setSelectedOrders(new Set(orders.map(o => o.id)));
    }
  };

  const toggleSelectAllInStatus = () => {
    setSelectAllInStatus(true);
    setExcludedOrders(new Set());
    setSelectedOrders(new Set(orders.map(o => o.id)));
    setSelectDropdownOpen(false);
  };

  const clearSelection = () => {
    setSelectedOrders(new Set());
    setSelectAllInStatus(false);
    setExcludedOrders(new Set());
    setSelectDropdownOpen(false);
  };

  const isAllSelected = orders.length > 0 && selectedOrders.size === orders.length;
  const isSomeSelected = selectedOrders.size > 0 && selectedOrders.size < orders.length;

  // Get count of selected orders - either page count or total in status
  const getSelectedCount = () => {
    if (selectAllInStatus) {
      return pagination.total - excludedOrders.size;
    }
    return selectedOrders.size;
  };

  // Helper to get all selected order IDs (fetches from server when "select all in status" is active)
  const getSelectedOrderIds = async (): Promise<string[] | null> => {
    if (selectAllInStatus) {
      try {
        const { data: idsData } = await orderApi.getIds({
          status: shipmentBatchFilter ? undefined : selectedStatus || undefined,
          storeId: filters.storeId || undefined,
          shipmentBatchId: shipmentBatchFilter,
          search: debouncedSearch || undefined,
          sortBy,
          sortOrder,
          orderNumber: advancedFilters.orderNumber || undefined,
          customerName: advancedFilters.customerName || undefined,
          productName: advancedFilters.productName || undefined,
          dateFrom: advancedFilters.dateFrom || undefined,
          dateTo: advancedFilters.dateTo || undefined,
          amountFrom: advancedFilters.amountFrom ? Number(advancedFilters.amountFrom) : undefined,
          amountTo: advancedFilters.amountTo ? Number(advancedFilters.amountTo) : undefined,
          paymentStatus: advancedFilters.paymentStatus || undefined,
          deliveryStatus: advancedFilters.deliveryStatus || undefined,
        });
        // Filter out manually deselected orders
        if (excludedOrders.size > 0) {
          return idsData.ids.filter((id: string) => !excludedOrders.has(id));
        }
        return idsData.ids;
      } catch (err) {
        console.error('Failed to fetch all order IDs:', err);
        showError('Błąd', 'Nie udało się pobrać listy zamówień');
        return null;
      }
    }
    return Array.from(selectedOrders);
  };

  // Helper to get full order data for all selected orders (fetches all pages when "select all" is active)
  const getSelectedOrdersData = async (): Promise<Order[] | null> => {
    if (selectAllInStatus) {
      try {
        const allOrders: Order[] = [];
        let page = 1;
        const limit = 250;
        let hasMore = true;

        while (hasMore) {
          const { data } = await orderApi.getAll({
            page,
            limit,
            status: shipmentBatchFilter ? undefined : selectedStatus || undefined,
            storeId: filters.storeId || undefined,
            shipmentBatchId: shipmentBatchFilter,
            search: debouncedSearch || undefined,
            sortBy,
            sortOrder,
            orderNumber: advancedFilters.orderNumber || undefined,
            customerName: advancedFilters.customerName || undefined,
            productName: advancedFilters.productName || undefined,
            dateFrom: advancedFilters.dateFrom || undefined,
            dateTo: advancedFilters.dateTo || undefined,
            amountFrom: advancedFilters.amountFrom ? Number(advancedFilters.amountFrom) : undefined,
            amountTo: advancedFilters.amountTo ? Number(advancedFilters.amountTo) : undefined,
            paymentStatus: advancedFilters.paymentStatus || undefined,
            deliveryStatus: advancedFilters.deliveryStatus || undefined,
          });
          allOrders.push(...data.data);
          hasMore = page < data.pagination.totalPages;
          page++;
        }
        // Filter out manually deselected orders
        if (excludedOrders.size > 0) {
          return allOrders.filter(o => !excludedOrders.has(o.id));
        }
        return allOrders;
      } catch (err) {
        console.error('Failed to fetch all orders:', err);
        showError('Błąd', 'Nie udało się pobrać danych zamówień');
        return null;
      }
    }
    return orders.filter(o => selectedOrders.has(o.id));
  };

  // Bulk actions
  const handleBulkStatusChange = async (statusId: string) => {
    if (selectedOrders.size === 0) return;
    if (activeJob) {
      showWarning('Operacja w toku', `Trwa już inna operacja w tle (${activeJob.title}). Poczekaj na jej zakończenie lub anuluj.`);
      return;
    }

    const orderIds = await getSelectedOrderIds();
    if (!orderIds) return;
    const BATCH_SIZE = 100;
    const totalOrders = orderIds.length;

    // For small batches (100 or less), use simple approach without progress bar
    if (totalOrders <= BATCH_SIZE) {
      setBulkStatusLoading(true);
      try {
        await orderApi.bulkUpdate(orderIds, statusId, statusId !== '');
        setBulkStatusDropdownOpen(false);
        setSelectedOrders(new Set());
        setSelectAllInStatus(false);
        setExcludedOrders(new Set());
        showSuccess('STATUS_UPDATE', { message: `Zmieniono status dla ${totalOrders} zamówień` });
        await loadOrders();
        await loadData();
      } catch (err) {
        console.error('Failed to update orders:', err);
        showError('Błąd', 'Nie udało się zmienić statusu zamówień');
      } finally {
        setBulkStatusLoading(false);
      }
      return;
    }

    // For large batches, use background job with progress
    const jobId = `bulk_status_${Date.now()}`;
    startJob({
      id: jobId,
      type: 'bulk_status',
      title: `Zmiana statusu (${totalOrders} zamówień)`,
      total: totalOrders,
      completed: 0,
      failed: 0,
    });

    setBulkStatusDropdownOpen(false);
    setSelectedOrders(new Set());
    setSelectAllInStatus(false);
    setExcludedOrders(new Set());

    // Split into batches of 100
    const batches: string[][] = [];
    for (let i = 0; i < orderIds.length; i += BATCH_SIZE) {
      batches.push(orderIds.slice(i, i + BATCH_SIZE));
    }

    let successCount = 0;
    let failCount = 0;

    for (const batch of batches) {
      if (isJobCancelled(jobId)) break;

      try {
        const result = await orderApi.bulkUpdate(batch, statusId, statusId !== '');
        successCount += result.data.updated || batch.length;
        updateJobProgress(jobId, { completed: successCount, failed: failCount });
      } catch (err) {
        console.error('Failed to update batch:', err);
        failCount += batch.length;
        updateJobProgress(jobId, { completed: successCount, failed: failCount });
      }
    }

    completeJob(jobId);

    if (failCount > 0) {
      showWarning('Operacja zakończona z błędami', `Zaktualizowano ${successCount} zamówień, ${failCount} nie powiodło się`);
    } else {
      showSuccess('STATUS_UPDATE', { message: `Pomyślnie zmieniono status dla ${successCount} zamówień` });
    }

    await loadOrders();
    await loadData();
  };

  const handleBulkDelete = async () => {
    if (selectedOrders.size === 0) return;
    if (activeJob) {
      showWarning('Operacja w toku', `Trwa już inna operacja w tle (${activeJob.title}). Poczekaj na jej zakończenie lub anuluj.`);
      return;
    }
    if (!(await confirmDialog({
      title: 'Przenieś zamówienia do kosza',
      message: `Czy na pewno chcesz przenieść ${getSelectedCount()} zamówień do kosza?`,
      subtitle: 'Zamówienia będzie można przywrócić z Kosza.',
      confirmLabel: 'Przenieś do kosza',
    }))) return;

    const orderIds = await getSelectedOrderIds();
    if (!orderIds) return;
    const BATCH_SIZE = 100;
    const totalOrders = orderIds.length;

    // For small batches (100 or less), use simple approach without progress bar
    if (totalOrders <= BATCH_SIZE) {
      try {
        await orderApi.bulkUpdate(orderIds, 'TRASH');
        setSelectedOrders(new Set());
        setSelectAllInStatus(false);
        setExcludedOrders(new Set());
        showSuccess('BULK_TRASH', { message: `Przeniesiono ${totalOrders} zamówień do kosza` });
        await loadOrders();
        await loadData();
      } catch (err) {
        console.error('Failed to delete orders:', err);
        showError('Błąd', 'Nie udało się przenieść zamówień do kosza');
      }
      return;
    }

    // For large batches, use background job with progress
    const jobId = `bulk_trash_${Date.now()}`;
    startJob({
      id: jobId,
      type: 'bulk_trash',
      title: `Przenoszenie do kosza (${totalOrders} zamówień)`,
      total: totalOrders,
      completed: 0,
      failed: 0,
    });

    setSelectedOrders(new Set());
    setSelectAllInStatus(false);
    setExcludedOrders(new Set());

    // Split into batches of 100
    const batches: string[][] = [];
    for (let i = 0; i < orderIds.length; i += BATCH_SIZE) {
      batches.push(orderIds.slice(i, i + BATCH_SIZE));
    }

    let successCount = 0;
    let failCount = 0;

    for (const batch of batches) {
      if (isJobCancelled(jobId)) break;

      try {
        const result = await orderApi.bulkUpdate(batch, 'TRASH');
        successCount += result.data.updated || batch.length;
        updateJobProgress(jobId, { completed: successCount, failed: failCount });
      } catch (err) {
        console.error('Failed to trash batch:', err);
        failCount += batch.length;
        updateJobProgress(jobId, { completed: successCount, failed: failCount });
      }
    }

    completeJob(jobId);

    if (failCount > 0) {
      showWarning('Operacja zakończona z błędami', `Przeniesiono ${successCount} zamówień, ${failCount} nie powiodło się`);
    } else {
      showSuccess('BULK_TRASH', { message: `Pomyślnie przeniesiono ${successCount} zamówień do kosza` });
    }

    await loadOrders();
    await loadData();
  };

  const handleBulkShipment = async () => {
    if (selectedOrders.size === 0) return;
    if (activeJob) {
      showWarning('Operacja w toku', `Trwa już inna operacja w tle (${activeJob.title}). Poczekaj na jej zakończenie lub anuluj.`);
      return;
    }
    if (!(await confirmDialog({
      title: 'Utwórz przesyłki',
      message: `Czy na pewno chcesz utworzyć przesyłki dla ${getSelectedCount()} zamówień?`,
      subtitle: 'Proces rusza natychmiast - można śledzić postęp w pasku u dołu.',
      confirmLabel: 'Utwórz przesyłki',
      danger: false,
    }))) return;

    // Pkt 5 (2026-04-22) — guard przed nadawaniem drugiego listu. Sprawdzamy
    // ile z zaznaczonych zamówień ma już aktywną przesyłkę z trackingNumber.
    // Ustawienie tenantowe w /settings.shipmentValidation (przeniesione z
    // courier settings do Ustawień → Walidacja danych, 2026-04-22).
    try {
      const { data: tenantSettings } = await api.get<{ shipmentValidation?: { warnOnDuplicateLabels?: boolean; duplicateLabelThreshold?: number } }>('/settings');
      const validation = tenantSettings.shipmentValidation;
      const warnEnabled = validation?.warnOnDuplicateLabels ?? true;
      const threshold = Math.max(1, validation?.duplicateLabelThreshold ?? 5);
      if (warnEnabled) {
        const selectedOrdersData = await getSelectedOrdersData();
        if (selectedOrdersData) {
          const alreadyShipped = selectedOrdersData.filter(
            o => o.shipments && o.shipments.some(s => !!s.trackingNumber)
          );
          if (alreadyShipped.length >= threshold) {
            const preview = alreadyShipped.slice(0, 10).map(o => `#${o.orderNumber}`).join(', ');
            const extra = alreadyShipped.length > 10 ? ` i ${alreadyShipped.length - 10} więcej` : '';
            const ok = await confirmDialog({
              title: 'Zamówienia mają już listy przewozowe',
              message: `Co najmniej ${alreadyShipped.length} z ${selectedOrdersData.length} zaznaczonych zamówień ma już nadany list przewozowy. Czy na pewno chcesz nadać nowy list dla tych zamówień?`,
              description: `${preview}${extra}`,
              subtitle: 'Poprzednie listy pozostaną aktywne, a klient dostanie dwa numery śledzenia.',
              confirmLabel: 'Tak, nadaj ponownie',
            });
            if (!ok) return;
          }
        }
      }
    } catch {
      // Brak ustawień / błąd pobrania — nie blokujemy nadawania, zachowanie jak było.
    }

    // N2 (2026-04-22): filtrowanie blacklisted klientów. Wykorzystuje już
    // załadowane zbiory blacklistedEmails/blacklistedPhones.
    let orderIds = await getSelectedOrderIds();
    if (!orderIds) return;

    // P10 (2026-04-30): pomijaj zamówienia w Poczekalni (isOnHold).
    {
      const selectedData = orders.filter(o => orderIds!.includes(o.id));
      const heldOrders = selectedData.filter(o => o.isOnHold);
      if (heldOrders.length > 0) {
        const preview = heldOrders.slice(0, 10).map(o => `#${o.orderNumber}`).join(', ');
        const extra = heldOrders.length > 10 ? ` i ${heldOrders.length - 10} więcej` : '';
        const proceed = await confirmDialog({
          title: 'Zamówienia w poczekalni',
          message: `${heldOrders.length} z ${selectedData.length} zaznaczonych zamówień jest w Poczekalni. Zostaną pominięte przy nadawaniu listów.`,
          description: `${preview}${extra}`,
          subtitle: 'Kontynuować nadawanie dla pozostałych zamówień?',
          confirmLabel: 'Kontynuuj bez nich',
          danger: false,
        });
        if (!proceed) return;
        const heldIds = new Set(heldOrders.map(o => o.id));
        orderIds = orderIds.filter(id => !heldIds.has(id));
        if (orderIds.length === 0) {
          showWarning('Nic do nadania', 'Wszystkie wybrane zamówienia są w Poczekalni.');
          return;
        }
      }
    }

    try {
      const { data: tenantSettings } = await api.get<{ customerBlacklist?: { enabled?: boolean; skipInBulkShipment?: boolean } }>('/settings');
      const blacklistSettings = tenantSettings.customerBlacklist;
      if (blacklistSettings?.enabled && blacklistSettings?.skipInBulkShipment) {
        const selectedData = orders.filter(o => orderIds!.includes(o.id));
        const blockedOrders = selectedData.filter(o => {
          const email = (o.customerEmail || '').trim().toLowerCase();
          const phone = (o.customerPhone || '').replace(/\D/g, '').replace(/^48/, '');
          return (email && blacklistedEmails.has(email)) || (phone && blacklistedPhones.has(phone));
        });
        if (blockedOrders.length > 0) {
          const preview = blockedOrders.slice(0, 10).map(o => `#${o.orderNumber}`).join(', ');
          const extra = blockedOrders.length > 10 ? ` i ${blockedOrders.length - 10} więcej` : '';
          const proceed = await confirmDialog({
            title: 'Klienci na czarnej liście',
            message: `${blockedOrders.length} z ${selectedData.length} zamówień należy do klientów na czarnej liście. Zostaną pominięte przy nadawaniu.`,
            description: `${preview}${extra}`,
            subtitle: 'Kontynuować nadawanie dla pozostałych zamówień?',
            confirmLabel: 'Kontynuuj bez nich',
            danger: false,
          });
          if (!proceed) return;
          const blockedIds = new Set(blockedOrders.map(o => o.id));
          orderIds = orderIds.filter(id => !blockedIds.has(id));
          if (orderIds.length === 0) {
            showWarning('Nic do nadania', 'Wszystkie wybrane zamówienia są na czarnej liście.');
            return;
          }
        }
      }
    } catch {
      // Brak settings / error — nie blokujemy
    }

    let shipmentBatch;
    try {
      const { data } = await shipmentApi.createBatch(orderIds);
      shipmentBatch = data.batch;
    } catch (err: unknown) {
      const translated = translateError(err, 'shipment');
      showNotification({
        type: 'error',
        title: 'Nie udało się rozpocząć nadawania',
        message: translated.message,
        details: translated.details,
        autoClose: false,
      });
      return;
    }

    const jobId = `bulk_shipment_${shipmentBatch.id}`;

    // Start background job
    startJob({
      id: jobId,
      type: 'bulk_shipment',
      title: `Tworzenie przesyłek (${orderIds.length})`,
      total: orderIds.length,
      completed: 0,
      failed: 0,
      serverBatchId: shipmentBatch.id,
      shipmentBatch,
    });

    setSelectedOrders(new Set());
    setSelectAllInStatus(false);
    setExcludedOrders(new Set());

    // Process orders with limited concurrency (3 parallel requests)
    const CONCURRENCY = 3;
    const results: Array<{ orderId: string; orderNumber?: string; success: boolean; error?: string }> = [];

    const processOrder = async (orderId: string) => {
      try {
        const { data } = await shipmentApi.create({ orderId, batchId: shipmentBatch.id });
        const success = data.success;
        const orderNumber = data.shipment?.order?.orderNumber || orderId;

        results.push({ orderId, orderNumber, success });
        addJobResult(jobId, { orderId, success });
      } catch (err: unknown) {
        const translated = translateError(err, 'shipment');
        const errorMessage = translated.details
          ? `${translated.message}\n${translated.details}`
          : translated.message;
        const orderNumber = orders.find(o => o.id === orderId)?.orderNumber || orderId;

        results.push({ orderId, orderNumber, success: false, error: errorMessage });
        addJobResult(jobId, { orderId, success: false, error: errorMessage });
      }
    };

    // Smart circuit breaker:
    // - Stops ONLY for systemic errors (same error 5 times in a row = API/account problem)
    // - Continues for data errors (each order has a different validation issue)
    // - Never stops if at least 1 order succeeded (proves the API works)
    const SYSTEMIC_THRESHOLD = 5;
    let hasAnySuccess = false;
    let abortedDueToErrors = false;
    let systemicErrorMessage = '';

    // Helper: check if we should stop (all recent errors are identical → systemic)
    const shouldAbort = (): boolean => {
      if (hasAnySuccess) return false; // API works — just bad data
      const failedResults = results.filter(r => !r.success);
      if (failedResults.length < SYSTEMIC_THRESHOLD) return false;
      // Check the last SYSTEMIC_THRESHOLD errors — if all identical, it's systemic
      const recentErrors = failedResults.slice(-SYSTEMIC_THRESHOLD).map(r => r.error);
      const allSame = recentErrors.every(e => e === recentErrors[0]);
      if (allSame) {
        systemicErrorMessage = recentErrors[0] || 'Nieznany błąd';
        return true;
      }
      return false;
    };

    for (let i = 0; i < orderIds.length; i += CONCURRENCY) {
      if (isJobCancelled(jobId)) break;
      if (abortedDueToErrors) break;

      const batch = orderIds.slice(i, i + CONCURRENCY);
      await Promise.all(batch.map(processOrder));

      // Update success flag
      if (results.some(r => r.success)) hasAnySuccess = true;

      // Check circuit breaker after each batch
      if (shouldAbort()) {
        abortedDueToErrors = true;
      }
    }

    const processedOrderIds = new Set(results.map(result => result.orderId));
    const skippedOrderIds = orderIds.filter(orderId => !processedOrderIds.has(orderId));
    try {
      const { data } = await shipmentApi.finishBatch(shipmentBatch.id, skippedOrderIds);
      updateJob(jobId, {
        shipmentBatch: data.batch,
      });
    } catch (err: unknown) {
      const translated = translateError(err, 'shipment');
      failJob(jobId, translated.message);
      showWarning(
        'Nie udało się odświeżyć dalszego postępu',
        'Listy utworzone przed błędem pozostają ważne. Odśwież stronę i sprawdź zamówienia przed pobraniem etykiet.',
      );
    }

    // Show combined report — single neutral modal with colored sections inside
    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;
    const failedResults = results.filter(r => !r.success);
    const remainingCount = abortedDueToErrors ? orderIds.length - results.length : 0;

    const reportSections: Array<{ type: 'success' | 'error'; title: string; message: string; items?: Array<{ label: string; detail?: string }> }> = [];

    if (successCount > 0) {
      reportSections.push({
        type: 'success',
        title: `Nadano: ${successCount}`,
        message: `Pomyślnie utworzono ${successCount === 1 ? 'przesyłkę' : 'przesyłek'}`,
      });
    }

    if (failCount > 0) {
      const failMessage = abortedDueToErrors
        ? `Przerwano - wykryto problem systemowy (${SYSTEMIC_THRESHOLD}x ten sam błąd): ${systemicErrorMessage}${remainingCount > 0 ? `. Pominięto ${remainingCount} zamówień` : ''}`
        : `Szczegóły błędu w historii zmian`;
      reportSections.push({
        type: 'error',
        title: `Nie nadano: ${failCount}`,
        message: failMessage,
        items: failedResults.slice(0, 20).map(r => ({
          label: r.orderNumber || r.orderId,
          detail: r.error,
        })),
      });
    }

    showNotification({
      type: 'report',
      title: 'Raport nadania przesyłek',
      message: `Przetworzono ${results.length} z ${orderIds.length} zamówień`,
      reportSections,
      autoClose: false,
    });

    // Refresh data without full page reload so the notification stays visible
    loadOrders();
    loadData();
  };

  const handleBulkDownloadLabels = async () => {
    if (selectedOrders.size === 0) return;
    if (activeJob) {
      if (activeJob.type === 'bulk_shipment' && activeJob.shipmentBatch) {
        const counts = activeJob.shipmentBatch.counts;
        const pendingCount = counts.waitingForShipment + counts.postProcessingPending;
        showWarning(
          'Zamówienia są jeszcze przygotowywane',
          `Pozostało ${pendingCount} zamówień. Pobieranie etykiet będzie dostępne, gdy zakończy się przenoszenie zamówień do ich statusów docelowych.`,
        );
      } else {
        showWarning('Operacja w toku', `Trwa już inna operacja w tle (${activeJob.title}). Poczekaj na jej zakończenie lub anuluj.`);
      }
      return;
    }

    // Get courier settings to check dynamicFilename, sortLabelsByStatusOrder and labelLayout options
    let useDynamicFilename = false;
    let sortByStatusOrder = false;
    let labelLayout: 'single' | '4_per_page' = 'single';
    let filenameIncludeStoreName = true;
    let filenameIncludeStatusLevel1 = true;
    let filenameIncludeStatusLevel2 = true;
    let filenameIncludeStatusLevel3 = true;
    try {
      const { data: courierConfig } = await courierApi.getInPost();
      const settings = courierConfig.settings as unknown as Record<string, unknown> | undefined;
      useDynamicFilename = (settings?.dynamicFilename as boolean) ?? false;
      sortByStatusOrder = (settings?.sortLabelsByStatusOrder as boolean) ?? false;
      labelLayout = (settings?.labelLayout as 'single' | '4_per_page') ?? 'single';
      filenameIncludeStoreName = (settings?.filenameIncludeStoreName as boolean) ?? true;
      filenameIncludeStatusLevel1 = (settings?.filenameIncludeStatusLevel1 as boolean) ?? true;
      filenameIncludeStatusLevel2 = (settings?.filenameIncludeStatusLevel2 as boolean) ?? true;
      filenameIncludeStatusLevel3 = (settings?.filenameIncludeStatusLevel3 as boolean) ?? true;
    } catch {
      // Ignore - use default filename
    }

    // Get selected orders with shipments (all pages when "select all" is active)
    const fetchedOrders = await getSelectedOrdersData();
    if (!fetchedOrders) return;
    let selectedOrdersList = fetchedOrders;

    // Folder downloads follow the sidebar tree, including repeated child names.
    if ((selectedStatus || sortByStatusOrder) && statuses.length > 0) {
      selectedOrdersList = sortOrdersByStatusTree(selectedOrdersList, statuses);
    }

    // Find orders with shipments - take the newest shipment (sorted ASC in backend, so newest is at the end)
    const shipmentsToDownload: Array<{
      orderId: string;
      orderNumber: string;
      shipmentId: string;
      trackingNumber: string;
      storeName?: string;
      statusName?: string;
      statusId?: string;
    }> = [];
    const ordersWithoutShipments: string[] = [];

    for (const order of selectedOrdersList) {
      if (order.shipments && order.shipments.length > 0) {
        // Take the last shipment (newest - sorted ASC in backend, so newest is at the end)
        const newestShipment = order.shipments[order.shipments.length - 1];
        if (newestShipment.trackingNumber) {
          shipmentsToDownload.push({
            orderId: order.id,
            orderNumber: order.orderNumber,
            shipmentId: newestShipment.id,
            trackingNumber: newestShipment.trackingNumber,
            storeName: order.store?.name,
            statusName: order.status,
            statusId: order.statusId || undefined,
          });
        } else {
          ordersWithoutShipments.push(order.orderNumber);
        }
      } else {
        ordersWithoutShipments.push(order.orderNumber);
      }
    }

    if (shipmentsToDownload.length === 0) {
      showWarning(
        'Brak przesyłek',
        'Brak przesyłek do pobrania.',
        `Zamówienia bez listów przewozowych: ${ordersWithoutShipments.join(', ')}`
      );
      return;
    }

    // Start background job for progress tracking
    const jobId = `labels_${Date.now()}`;
    startJob({
      id: jobId,
      type: 'bulk_labels',
      title: `Pobieranie etykiet (${shipmentsToDownload.length})`,
      total: shipmentsToDownload.length,
      completed: 0,
      failed: 0,
    });

    // Download labels
    try {
      const labels: Array<{
        orderNumber: string;
        trackingNumber: string;
        data: string;
        storeName?: string;
        statusName?: string;
        statusId?: string;
      }> = [];
      // Konkretny błąd per zamówienie żeby user mógł zobaczyć detal w rozwijanej liście raportu.
      const errors: Array<{ orderNumber: string; error: string }> = [];

      // Retry helper: 3 łączne próby z 1s delay między. Tylko dla 5xx/network/timeout.
      // 4xx (np. shipment nie istnieje) leci od razu — nie warto retry.
      const fetchLabelWithRetry = async (shipmentId: string): Promise<{ data: string }> => {
        const MAX_ATTEMPTS = 3;
        let lastError: unknown = null;
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
          try {
            const { data } = await shipmentApi.getLabel(shipmentId);
            return data;
          } catch (err: unknown) {
            lastError = err;
            const status = (err as { response?: { status?: number } })?.response?.status;
            const isNetworkOrServerError = !status || status >= 500 || status === 408;
            if (!isNetworkOrServerError || attempt === MAX_ATTEMPTS) {
              throw err;
            }
            console.warn(`[Labels] getLabel ${shipmentId} attempt ${attempt}/${MAX_ATTEMPTS} failed (status=${status ?? 'network'}). Retry in 1s.`);
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
        throw lastError;
      };

      for (const shipment of shipmentsToDownload) {
        // Check if job was cancelled
        if (isJobCancelled(jobId)) {
          break;
        }

        try {
          const labelData = await fetchLabelWithRetry(shipment.shipmentId);
          labels.push({
            orderNumber: shipment.orderNumber,
            trackingNumber: shipment.trackingNumber,
            data: labelData.data,
            storeName: shipment.storeName,
            statusName: shipment.statusName,
            statusId: shipment.statusId,
          });
          addJobResult(jobId, { orderId: shipment.orderId, success: true });
        } catch (err) {
          console.error(`Failed to get label for ${shipment.orderNumber}:`, err);
          const translated = translateError(err, 'shipment');
          const errorMessage = translated.details
            ? `${translated.message} ${translated.details}`
            : translated.message;
          errors.push({ orderNumber: shipment.orderNumber, error: errorMessage });
          addJobResult(jobId, { orderId: shipment.orderId, success: false, error: errorMessage });
        }
      }

      if (labels.length === 0) {
        completeJob(jobId);
        showError('Nie udało się pobrać żadnych etykiet.', 'labels');
        return;
      }

      // Helper to find status path from root to a specific status
      // If statusId is provided, search by ID for exact match (preferred)
      // Otherwise, fall back to name search with storeName filter
      const getStatusPath = (statusName: string, statusList: StatusGroup[], path: StatusGroup[] = [], storeName?: string, statusId?: string): StatusGroup[] | null => {
        for (const status of statusList) {
          const currentPath = [...path, status];

          // If searching by ID (more accurate), check ID match
          if (statusId && status.id === statusId) {
            return currentPath;
          }

          // If storeName filter is specified and not searching by ID, only search in matching store's subtree
          if (!statusId && storeName && path.length === 0) {
            // At root level - only descend into the matching store's branch
            if (status.store?.name !== storeName && status.name !== storeName) {
              continue;
            }
          }

          // Only match by name if not searching by ID
          if (!statusId && status.name === statusName) {
            return currentPath;
          }
          if (status.children && status.children.length > 0) {
            const foundPath = getStatusPath(statusName, status.children, currentPath, storeName, statusId);
            if (foundPath !== null) {
              return foundPath;
            }
          }
        }
        return null;
      };

      // Helper to generate dynamic filename
      const generateDynamicFilename = (labelsList: typeof labels): string => {
        const parts: string[] = [];

        // Get all unique store names from labels (if enabled)
        if (filenameIncludeStoreName) {
          const uniqueStoreNames = [...new Set(labelsList.map(l => l.storeName).filter((s): s is string => Boolean(s)))];
          if (uniqueStoreNames.length > 0) {
            const storeNamePart = uniqueStoreNames
              .map(s => s.toLowerCase().replace(/[^a-ząćęłńóśźż0-9]+/g, '_').replace(/^_|_$/g, ''))
              .filter(Boolean)
              .join('_');
            if (storeNamePart) {
              parts.push(storeNamePart);
            }
          }
        }

        // For each label, get its full status path and collect statuses at enabled levels
        // Use storeName to search in the correct store's subtree
        const statusesByLevel: Map<number, Set<string>> = new Map();
        const processedPaths = new Set<string>(); // Avoid processing same status+store combo twice

        for (const label of labelsList) {
          if (!label.statusName && !label.statusId) continue;

          // Create a unique key for this status+store combination (prefer statusId if available)
          const pathKey = label.statusId || `${label.storeName || ''}_${label.statusName}`;
          if (processedPaths.has(pathKey)) continue;
          processedPaths.add(pathKey);

          // Find the path, using statusId for exact match or storeName to search in the correct subtree
          const path = getStatusPath(label.statusName || '', statuses, [], label.storeName, label.statusId);
          if (path) {
            path.forEach((s, idx) => {
              const level = idx + 1; // 1-based level
              if (!statusesByLevel.has(level)) {
                statusesByLevel.set(level, new Set());
              }
              statusesByLevel.get(level)!.add(s.name);
            });
          }
        }

        // Build status part based on enabled levels
        const filteredStatusParts: string[] = [];

        // Add level 1 statuses (store-level, like "Lemon Glow")
        if (filenameIncludeStatusLevel1 && statusesByLevel.has(1)) {
          const level1Names = [...statusesByLevel.get(1)!].sort();
          filteredStatusParts.push(...level1Names);
        }

        // Add level 2 statuses (like "W realizacji")
        if (filenameIncludeStatusLevel2 && statusesByLevel.has(2)) {
          const level2Names = [...statusesByLevel.get(2)!].sort();
          filteredStatusParts.push(...level2Names);
        }

        // Add level 3+ statuses (like "1", "2", "3", "Pozostałe")
        if (filenameIncludeStatusLevel3) {
          const maxLevel = statusesByLevel.size > 0 ? Math.max(...statusesByLevel.keys()) : 3;
          for (let level = 3; level <= maxLevel; level++) {
            if (statusesByLevel.has(level)) {
              const levelNames = [...statusesByLevel.get(level)!].sort();
              filteredStatusParts.push(...levelNames);
            }
          }
        }

        if (filteredStatusParts.length > 0) {
          const statusPart = filteredStatusParts
            .map(s => s.toLowerCase().replace(/[^a-ząćęłńóśźż0-9]+/g, '_').replace(/^_|_$/g, ''))
            .join('_');
          if (statusPart) {
            parts.push(statusPart);
          }
        }

        const now = new Date();
        const dateStr = `${String(now.getDate()).padStart(2, '0')}.${String(now.getMonth() + 1).padStart(2, '0')}.${now.getFullYear()}`;
        parts.push(dateStr);

        // If no parts before date, add a default
        if (parts.length === 1) {
          return `etykiety_${dateStr}.pdf`;
        }

        return `${parts.join('_')}.pdf`;
      };

      // If single label and NOT 4-per-page layout, download directly
      if (labels.length === 1 && labelLayout !== '4_per_page') {
        const label = labels[0];
        const blob = new Blob([Uint8Array.from(atob(label.data), c => c.charCodeAt(0))], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Use dynamic filename for bulk actions (even single label)
        if (useDynamicFilename) {
          a.download = generateDynamicFilename(labels);
        } else {
          a.download = `etykieta_${label.orderNumber}_${label.trackingNumber}.pdf`;
        }
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        // Multiple labels - merge into single PDF
        try {
          const mergedPdf = await PDFDocument.create();

          if (labelLayout === '4_per_page') {
            // 4 labels per A4 page in 2x2 grid
            // A4 size in points: 595.28 x 841.89 (72 points per inch)
            const A4_WIDTH = 595.28;
            const A4_HEIGHT = 841.89;
            const MARGIN = 10; // Small margin
            const CELL_WIDTH = (A4_WIDTH - 3 * MARGIN) / 2;
            const CELL_HEIGHT = (A4_HEIGHT - 3 * MARGIN) / 2;

            // Debug: log labels order before PDF generation
            console.log('[Label PDF] Labels in order for 4-per-page:', labels.map((l, idx) => ({ idx, orderNumber: l.orderNumber, statusName: l.statusName })));

            // Process labels in groups of 4
            for (let i = 0; i < labels.length; i += 4) {
              // Create a new A4 page
              const page = mergedPdf.addPage([A4_WIDTH, A4_HEIGHT]);

              // Get up to 4 labels for this page
              const pageLabels = labels.slice(i, Math.min(i + 4, labels.length));

              for (let j = 0; j < pageLabels.length; j++) {
                const labelData = pageLabels[j];
                const pdfBytes = Uint8Array.from(atob(labelData.data), c => c.charCodeAt(0));
                const sourcePdf = await PDFDocument.load(pdfBytes);
                const sourcePage = sourcePdf.getPage(0);

                // Calculate position in the 2x2 grid
                // Grid positions: 0=top-left, 1=top-right, 2=bottom-left, 3=bottom-right
                const col = j % 2;
                const row = Math.floor(j / 2);

                // Debug: log label placement
                const srcWidth = sourcePage.getWidth();
                const srcHeight = sourcePage.getHeight();
                const positionName = ['top-left', 'top-right', 'bottom-left', 'bottom-right'][j] || `position-${j}`;
                console.log(`[Label PDF] Page ${Math.floor(i / 4) + 1}, Position ${j} (${positionName}): Order ${labelData.orderNumber}, Status: ${labelData.statusName}, src: ${srcWidth.toFixed(0)}x${srcHeight.toFixed(0)}`);

                // InPost returns labels in different page sizes depending on service type:
                // - Courier labels: typically A6 (~297x420pt), content fills the page
                // - Paczkomat labels: sometimes A4 (~595x842pt), with A6-sized content in one corner
                // To normalize: if the source page is significantly larger than A6,
                // crop to the top-left A6 portion where InPost places the label content.
                const A6_W = 297.64;
                const A6_H = 419.53;
                const isOversized = srcWidth > A6_W * 1.3 || srcHeight > A6_H * 1.3;

                let embeddedPage;
                let effectiveWidth: number;
                let effectiveHeight: number;

                if (isOversized) {
                  // Crop to A6 portion at the top-left of the page
                  const cropW = Math.min(srcWidth, A6_W);
                  const cropH = Math.min(srcHeight, A6_H);
                  embeddedPage = await mergedPdf.embedPage(sourcePage, {
                    left: 0,
                    bottom: srcHeight - cropH,
                    right: cropW,
                    top: srcHeight,
                  });
                  effectiveWidth = cropW;
                  effectiveHeight = cropH;
                  console.log(`[Label PDF]   Oversized page detected (${srcWidth.toFixed(0)}x${srcHeight.toFixed(0)}), cropped to ${cropW.toFixed(0)}x${cropH.toFixed(0)}`);
                } else {
                  [embeddedPage] = await mergedPdf.embedPdf(sourcePdf, [0]);
                  effectiveWidth = srcWidth;
                  effectiveHeight = srcHeight;
                }

                // Calculate scale to fit in cell while maintaining aspect ratio
                const scaleX = CELL_WIDTH / effectiveWidth;
                const scaleY = CELL_HEIGHT / effectiveHeight;
                const scale = Math.min(scaleX, scaleY);

                // Calculate position - PDF coordinates start from bottom-left
                const scaledWidth = effectiveWidth * scale;
                const scaledHeight = effectiveHeight * scale;

                // Center the label within its cell
                const cellX = MARGIN + col * (CELL_WIDTH + MARGIN);
                const cellY = A4_HEIGHT - MARGIN - (row + 1) * (CELL_HEIGHT + MARGIN) + MARGIN;
                const x = cellX + (CELL_WIDTH - scaledWidth) / 2;
                const y = cellY + (CELL_HEIGHT - scaledHeight) / 2;

                page.drawPage(embeddedPage, {
                  x,
                  y,
                  width: scaledWidth,
                  height: scaledHeight,
                });
              }
            }
          } else {
            // Single page per label (default behavior)
            for (const label of labels) {
              const pdfBytes = Uint8Array.from(atob(label.data), c => c.charCodeAt(0));
              const pdf = await PDFDocument.load(pdfBytes);
              const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
              pages.forEach(page => mergedPdf.addPage(page));
            }
          }

          const mergedPdfBytes = await mergedPdf.save();
          const blob = new Blob([new Uint8Array(mergedPdfBytes)], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;

          // Generate filename (use helper that collects all unique statuses)
          if (useDynamicFilename) {
            a.download = generateDynamicFilename(labels);
          } else {
            const now = new Date().toISOString().slice(0, 10);
            a.download = labels.length === 1
              ? `etykieta_${labels[0].orderNumber}_${labels[0].trackingNumber}.pdf`
              : `etykiety_${labels.length}_szt_${now}.pdf`;
          }

          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } catch (mergeErr) {
          console.error('Failed to merge PDFs:', mergeErr);
          completeJob(jobId);
          showError('Błąd podczas łączenia PDF-ów. Spróbuj pobrać pojedynczo.', 'labels');
          return;
        }
      }

      // Mark job as complete
      completeJob(jobId);

      // Show summary — pełny raport z rozwijaną listą niepobranych (jak bulk shipment).
      // Operator może rozwinąć i przepisać numery których nie udało się pobrać.
      if (errors.length > 0 || ordersWithoutShipments.length > 0) {
        const errorCount = errors.length + ordersWithoutShipments.length;
        const reportSections: Array<{
          type: 'success' | 'error';
          title: string;
          message: string;
          items?: Array<{ label: string; detail?: string }>;
        }> = [];

        if (labels.length > 0) {
          reportSections.push({
            type: 'success',
            title: `Pobrano: ${labels.length}`,
            message: `Pomyślnie pobrano ${labels.length === 1 ? 'etykietę' : 'etykiet'}`,
          });
        }

        if (errors.length > 0) {
          reportSections.push({
            type: 'error',
            title: `Nie pobrano: ${errors.length}`,
            message: 'Etykiety których nie udało się pobrać z InPost',
            items: errors.slice(0, 50).map(e => ({
              label: `#${e.orderNumber}`,
              detail: e.error,
            })),
          });
        }

        if (ordersWithoutShipments.length > 0) {
          reportSections.push({
            type: 'error',
            title: `Bez listów: ${ordersWithoutShipments.length}`,
            message: 'Zamówienia które nie mają jeszcze nadanego listu przewozowego',
            items: ordersWithoutShipments.slice(0, 50).map(orderNumber => ({
              label: `#${orderNumber}`,
            })),
          });
        }

        showNotification({
          type: 'report',
          title: 'Raport pobierania etykiet',
          message: `Pobrano ${labels.length} z ${labels.length + errorCount}`,
          reportSections,
          autoClose: false,
        });
      } else {
        showSuccess('labels', { counts: { success: labels.length } });
      }
    } catch (err) {
      console.error('Failed to download labels:', err);
      completeJob(jobId);
      showError(err, 'labels');
    }
  };

  // Bulk create invoices
  const handleBulkCreateInvoices = async () => {
    if (selectedOrders.size === 0) return;
    if (activeJob) {
      showWarning('Operacja w toku', `Trwa już inna operacja w tle (${activeJob.title}). Poczekaj na jej zakończenie lub anuluj.`);
      return;
    }

    // Get all selected orders (all pages when "select all" is active)
    const fetchedOrders = await getSelectedOrdersData();
    if (!fetchedOrders) return;

    // Filter out orders that already have active VAT invoices
    const selectedOrdersList = fetchedOrders;
    const ordersWithoutInvoice = selectedOrdersList.filter(order => {
      const activeInvoices = (order.invoices || []).filter(inv => inv.status !== 'DELETED');
      return !activeInvoices.some(inv => inv.type === 'VAT');
    });
    const ordersWithInvoice = selectedOrdersList.filter(order => {
      const activeInvoices = (order.invoices || []).filter(inv => inv.status !== 'DELETED');
      return activeInvoices.some(inv => inv.type === 'VAT');
    });

    if (ordersWithoutInvoice.length === 0) {
      showInfo('Faktury istnieją', `Wszystkie wybrane zamówienia (${selectedOrders.size}) mają już wystawione faktury.`);
      return;
    }

    const skippedInfo = ordersWithInvoice.length > 0
      ? `\n\nPominięte (mają już faktury): ${ordersWithInvoice.length}`
      : '';

    if (!(await confirmDialog({
      title: 'Wystaw faktury',
      message: `Czy na pewno chcesz wystawić faktury dla ${ordersWithoutInvoice.length} zamówień?`,
      description: skippedInfo ? skippedInfo.replace(/^\.\s*/, '') : undefined,
      subtitle: 'Faktury trafią do Fakturowni - operacja nieodwracalna.',
      confirmLabel: 'Wystaw faktury',
      danger: false,
    }))) return;

    const orderIds = ordersWithoutInvoice.map(o => o.id);
    const jobId = `bulk_invoice_${Date.now()}`;

    // Start background job
    startJob({
      id: jobId,
      type: 'bulk_invoice',
      title: `Wystawianie faktur (${orderIds.length})`,
      total: orderIds.length,
      completed: 0,
      failed: 0,
    });

    setSelectedOrders(new Set());

    // Process orders one by one for real-time progress
    const results: Array<{ orderId: string; orderNumber?: string; success: boolean; error?: string }> = [];

    for (const orderId of orderIds) {
      // Check if job was cancelled
      if (isJobCancelled(jobId)) {
        break;
      }

      try {
        const { data } = await invoiceApi.create({ orderId });
        const success = data.success;
        const invoiceNumber = data.invoice?.invoiceNumber || '';

        results.push({ orderId, orderNumber: invoiceNumber, success });
        addJobResult(jobId, { orderId, success });
      } catch (err: unknown) {
        const error = err as { response?: { data?: { error?: string; message?: string } } };
        const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Błąd wystawiania faktury';

        results.push({ orderId, success: false, error: errorMessage });
        addJobResult(jobId, { orderId, success: false, error: errorMessage });
      }

      // Small delay between orders
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Mark job as complete
    completeJob(jobId);

    // Show summary
    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;

    if (failCount > 0) {
      // Simplified report - just counts, details are in order history
      const skippedInfo = ordersWithInvoice.length > 0
        ? ` Pominięto ${ordersWithInvoice.length} zamówień (miały już faktury).`
        : '';
      showWarning(
        'Faktury z błędami',
        `Wystawiono ${successCount} faktur, ${failCount} nie powiodło się.${skippedInfo} Szczegóły błędów znajdziesz w historii zmian poszczególnych zamówień.`
      );
    } else if (successCount > 0) {
      const skippedDetails = ordersWithInvoice.length > 0
        ? `Pominięto ${ordersWithInvoice.length} zamówień (miały już faktury).`
        : undefined;
      showSuccess('invoices', { counts: { success: successCount }, details: skippedDetails });
    }

    await loadOrders();
  };

  // Bulk download invoice PDFs
  const handleBulkDownloadInvoices = async () => {
    if (selectedOrders.size === 0) return;
    if (activeJob) {
      showWarning('Operacja w toku', `Trwa już inna operacja w tle (${activeJob.title}). Poczekaj na jej zakończenie lub anuluj.`);
      return;
    }

    // Get all selected orders (all pages when "select all" is active)
    const fetchedOrders = await getSelectedOrdersData();
    if (!fetchedOrders) return;
    const selectedOrdersList = fetchedOrders;
    const invoicesToDownload: Array<{
      orderId: string;
      orderNumber: string;
      invoiceId: string;
      invoiceNumber: string;
    }> = [];
    const ordersWithoutInvoices: string[] = [];

    for (const order of selectedOrdersList) {
      if (order.invoices && order.invoices.length > 0) {
        const latestInvoice = order.invoices[order.invoices.length - 1];
        invoicesToDownload.push({
          orderId: order.id,
          orderNumber: order.orderNumber,
          invoiceId: latestInvoice.id,
          invoiceNumber: latestInvoice.invoiceNumber || `Faktura-${order.orderNumber}`,
        });
      } else {
        ordersWithoutInvoices.push(order.orderNumber);
      }
    }

    if (invoicesToDownload.length === 0) {
      showWarning(
        'Brak faktur',
        'Wybrane zamówienia nie mają wystawionych faktur.',
        `Zamówienia: ${ordersWithoutInvoices.join(', ')}`
      );
      return;
    }

    try {
      const pdfs: { data: Uint8Array; invoiceNumber: string }[] = [];
      const errors: string[] = [];

      for (const invoice of invoicesToDownload) {
        try {
          const { data } = await invoiceApi.getPdf(invoice.invoiceId);
          const binaryData = Uint8Array.from(atob(data.data), c => c.charCodeAt(0));
          pdfs.push({ data: binaryData, invoiceNumber: invoice.invoiceNumber });
        } catch {
          errors.push(invoice.orderNumber);
        }
      }

      if (pdfs.length === 0) {
        showError(`Nie udało się pobrać żadnych faktur. Błędy dla: ${errors.join(', ')}`, 'invoices');
        return;
      }

      // Merge PDFs
      if (pdfs.length === 1) {
        const blob = new Blob([pdfs[0].data.buffer as ArrayBuffer], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${pdfs[0].invoiceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        const mergedPdf = await PDFDocument.create();
        for (const pdf of pdfs) {
          try {
            const sourcePdf = await PDFDocument.load(pdf.data);
            const pages = await mergedPdf.copyPages(sourcePdf, sourcePdf.getPageIndices());
            pages.forEach(page => mergedPdf.addPage(page));
          } catch {
            errors.push(pdf.invoiceNumber);
          }
        }

        const mergedBytes = await mergedPdf.save();
        const blob = new Blob([mergedBytes.buffer as ArrayBuffer], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const now = new Date().toISOString().slice(0, 10);
        a.download = `faktury_${pdfs.length}_szt_${now}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }

      // Simplified report with counts only
      if (errors.length > 0 || ordersWithoutInvoices.length > 0) {
        const errorCount = errors.length + ordersWithoutInvoices.length;
        showWarning(
          'Pobrano z błędami',
          `Pobrano ${pdfs.length} faktur, ${errorCount} nie udało się pobrać.`
        );
      } else {
        showSuccess('invoices', { message: `Pobrano ${pdfs.length} faktur.` });
      }
    } catch (err) {
      console.error('Failed to download invoices:', err);
      showError(err, 'invoices');
    }
  };

  // Flatten statuses for bulk dropdown, filtering by selected store
  const getAllStatuses = (): { id: string; name: string; color: string; level: number; isTopLevel: boolean }[] => {
    const result: { id: string; name: string; color: string; level: number; isTopLevel: boolean }[] = [];
    const selectedStoreId = filters.storeId;

    // Filter top-level statuses by selected store
    const filteredStatuses = selectedStoreId
      ? statuses.filter(s => !s.storeId || s.storeId === selectedStoreId)
      : statuses;

    const addStatus = (status: StatusGroup, level: number, isTopLevel: boolean) => {
      // For children, also check store filter
      if (level > 0 && selectedStoreId && status.storeId && status.storeId !== selectedStoreId) return;

      result.push({ id: status.id, name: status.name, color: status.color, level, isTopLevel });
      if (status.children && status.children.length > 0) {
        status.children.forEach(child => addStatus(child, level + 1, false));
      }
    };
    filteredStatuses.forEach((s, idx) => addStatus(s, 0, idx > 0)); // idx > 0 means show separator before (except first)
    return result;
  };

  // Get statuses filtered by selected store for sidebar
  const getFilteredStatusesForSidebar = (): StatusGroup[] => {
    const selectedStoreId = filters.storeId;
    if (!selectedStoreId) return statuses;

    // Filter recursively - include status if it's global or matches the store
    const filterStatus = (status: StatusGroup): StatusGroup | null => {
      // Check if this status should be included
      if (status.storeId && status.storeId !== selectedStoreId) return null;

      // Filter children recursively
      const filteredChildren = status.children
        ? status.children.map(filterStatus).filter((c): c is StatusGroup => c !== null)
        : [];

      return {
        ...status,
        children: filteredChildren,
      };
    };

    return statuses.map(filterStatus).filter((s): s is StatusGroup => s !== null);
  };

  // Count active advanced filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (advancedFilters.orderNumber) count++;
    if (advancedFilters.customerName) count++;
    if (advancedFilters.productName) count++;
    if (advancedFilters.dateFrom || advancedFilters.dateTo) count++;
    if (advancedFilters.amountFrom || advancedFilters.amountTo) count++;
    if (advancedFilters.paymentStatus) count++;
    if (advancedFilters.deliveryStatus) count++;
    return count;
  };

  // Clear all advanced filters
  const clearAdvancedFilters = () => {
    setAdvancedFilters({
      orderNumber: '',
      customerName: '',
      productName: '',
      dateFrom: '',
      dateTo: '',
      amountFrom: '',
      amountTo: '',
      paymentStatus: '',
      deliveryStatus: '',
    });
  };

  const handleStatusSelect = (status: string | null) => {
    setSelectedStatus(status);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('shipmentBatch');
    if (status) nextParams.set('status', status);
    else nextParams.delete('status');
    setSearchParams(nextParams, { replace: true });
  };

  const clearShipmentBatchFilter = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('shipmentBatch');
    setSearchParams(nextParams, { replace: true });
    setPagination(prev => prev.page === 1 ? prev : { ...prev, page: 1 });
  };

  return (
    <div className="flex gap-4 -m-8 overflow-x-hidden">
      {/* Status Sidebar */}
      <div className="w-64 min-h-screen bg-surface-raised/30 border-r border-border-subtle p-4">
        <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-4 flex items-center gap-2">
          Statusy
          <button
            onClick={() => { loadOrders(); loadData(); }}
            disabled={loading}
            className="p-0.5 text-text-muted hover:text-accent-primary transition-colors"
            title="Odśwież"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </h3>

        {/* All orders - always visible */}
        <button
          onClick={() => handleStatusSelect(null)}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg mb-2 transition-all ${
            selectedStatus === null
              ? 'bg-accent-primary/20 text-accent-primary'
              : 'text-text-secondary hover:bg-surface-hover'
          }`}
        >
          <ShoppingCart className="w-4 h-4" />
          <span className="flex-1 text-left text-sm font-medium">Wszystkie</span>
          <span className="text-xs bg-surface-hover px-2 py-0.5 rounded">
            {getTotalOrderCount()}
          </span>
        </button>

        {/* Unassigned status - shown only when enabled in settings, right below "Wszystkie" */}
        {showUnassignedStatus && (
          <button
            onClick={() => handleStatusSelect('Nieprzypisany')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg mb-2 transition-all ${
              selectedStatus === 'Nieprzypisany'
                ? 'bg-gray-500/20 text-text-secondary'
                : 'text-text-secondary hover:bg-surface-hover hover:text-text-secondary'
            }`}
          >
            <CircleSlash className="w-4 h-4" />
            <span className="flex-1 text-left text-sm font-medium">Nieprzypisany</span>
            <span className="text-xs bg-surface-hover px-2 py-0.5 rounded">
              {unassignedCount}
            </span>
          </button>
        )}

        <div className="border-t border-border-subtle my-3" />

        {/* Status tree - filtered by selected store */}
        <div className="space-y-1">
          {getFilteredStatusesForSidebar().map(status => (
            <StatusTreeItem
              key={status.id}
              status={status}
              level={0}
              selectedStatus={selectedStatus}
              expandedStatuses={expandedStatuses}
              onSelect={handleStatusSelect}
              onToggleExpand={toggleStatusExpand}
            />
          ))}
        </div>

        {getFilteredStatusesForSidebar().length === 0 && (
          <p className="text-sm text-text-muted text-center py-4">
            {filters.storeId ? 'Brak statusów dla tego sklepu.' : 'Brak statusów. Utwórz je w Administracja → Statusy.'}
          </p>
        )}

        {/* Trash - system status at the bottom */}
        <div className="border-t border-border-subtle mt-6 pt-4 pb-2">
          <button
            onClick={() => handleStatusSelect('Kosz')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
              selectedStatus === 'Kosz'
                ? 'bg-danger/20 text-danger'
                : 'text-text-muted hover:bg-surface-hover hover:text-text-secondary'
            }`}
          >
            <Trash2 className="w-4 h-4" />
            <span className="flex-1 text-left text-sm font-medium">Kosz</span>
            <span className="text-xs bg-surface-hover px-2 py-0.5 rounded">
              {trashCount}
            </span>
          </button>
        </div>

        {/* Force sync - subtle button at bottom */}
        <div className="mt-4 px-2">
          <button
            onClick={syncAllStores}
            disabled={syncing}
            className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-[11px] text-text-disabled hover:text-text-secondary transition-colors rounded-lg hover:bg-surface-hover/30 border border-border-subtle disabled:opacity-50"
          >
            <Download className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Synchronizacja...' : 'Wymuś synchronizację'}
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 py-8 pl-6 pr-10">
        <div className="animate-fade-in">
          <PageHeader
            icon={ShoppingCart}
            title="Zamówienia"
            subtitle={shipmentBatchFilter
              ? 'Zamówienia z wybranej operacji nadawania'
              : selectedStatus
                ? 'Filtrowanie po statusie'
                : 'Wszystkie zamówienia'}
            actions={
              <>
                <Button
                  variant="primary"
                  leftIcon={<Plus />}
                  onClick={() => {
                    setSimilarOrderSource(null);
                    setShowCreateOrderModal(true);
                  }}
                >
                  Dodaj zamówienie
                </Button>
                <Button
                  variant="icon"
                  onClick={() => { loadOrders(); loadData(); }}
                  disabled={loading}
                  title="Odśwież listę zamówień i statusy"
                  leftIcon={<RefreshCw className={loading ? 'animate-spin' : ''} />}
                />
              </>
            }
          />
        </div>

        {shipmentBatchFilter && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-accent-primary/30 bg-accent-primary/10 px-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <PackageCheck className="h-5 w-5 shrink-0 text-accent-primary" />
              <div>
                <div className="text-sm font-medium text-text-primary">Widok konkretnej operacji nadawania</div>
                <div className="text-xs text-text-secondary">
                  Lista zawiera tylko zamówienia z tej partii, niezależnie od ich aktualnego statusu.
                </div>
              </div>
            </div>
            <Button size="sm" variant="secondary" onClick={clearShipmentBatchFilter}>
              Pokaż wszystkie
            </Button>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Szukaj po numerze, kliencie, emailu..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              leftIcon={<Search />}
              clearable
              onClear={() => setFilters(prev => ({ ...prev, search: '' }))}
            />
          </div>
          <div className="w-64">
            <Select
              value={filters.storeId}
              onChange={(e) => setFilters(prev => ({ ...prev, storeId: e.target.value }))}
            >
              <option value="">Wszystkie sklepy</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>{store.name}</option>
              ))}
            </Select>
          </div>
          <Button
            variant="secondary"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={getActiveFiltersCount() > 0 ? 'border-accent-primary text-accent-primary' : ''}
            leftIcon={<Filter />}
            rightIcon={showAdvancedFilters ? <ChevronUp /> : <ChevronDown />}
          >
            Filtry
            {getActiveFiltersCount() > 0 && (
              <span className="bg-accent-primary text-dark-900 text-xs font-bold px-1.5 py-0.5 rounded-full ml-1">
                {getActiveFiltersCount()}
              </span>
            )}
          </Button>
          {getActiveFiltersCount() > 0 && (
            <Button
              variant="secondary"
              onClick={clearAdvancedFilters}
              className="text-danger border-red-400/50 hover:border-red-400 hover:bg-red-400/10"
              title="Wyczyść wszystkie filtry"
              leftIcon={<RotateCcw />}
            >
              Reset
            </Button>
          )}
        </div>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div className="mb-6 p-4 bg-surface-raised/50 border border-border-subtle rounded-xl animate-fade-in overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider">
                Zaawansowane filtry
              </h3>
              {getActiveFiltersCount() > 0 && (
                <button
                  onClick={clearAdvancedFilters}
                  className="text-sm text-accent-primary hover:text-text-primary transition-colors"
                >
                  Wyczyść filtry
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
              {/* Order Number */}
              <div>
                <label className="label text-xs">Nr zamówienia</label>
                <Input
                  type="text"
                  placeholder="12345"
                  value={advancedFilters.orderNumber}
                  onChange={(e) => setAdvancedFilters(prev => ({ ...prev, orderNumber: e.target.value }))}
                  className="!py-2 text-sm"
                />
              </div>

              {/* Customer Name */}
              <div>
                <label className="label text-xs">Klient</label>
                <Input
                  type="text"
                  placeholder="Jan Kowalski"
                  value={advancedFilters.customerName}
                  onChange={(e) => setAdvancedFilters(prev => ({ ...prev, customerName: e.target.value }))}
                  className="!py-2 text-sm"
                />
              </div>

              {/* Product Name/SKU */}
              <div>
                <label className="label text-xs">Produkt/SKU</label>
                <Input
                  type="text"
                  placeholder="Koszulka XL"
                  value={advancedFilters.productName}
                  onChange={(e) => setAdvancedFilters(prev => ({ ...prev, productName: e.target.value }))}
                  className="!py-2 text-sm"
                />
              </div>

              {/* Amount Range */}
              <div>
                <label className="label text-xs">Kwota (PLN)</label>
                <div className="flex gap-1">
                  <Input
                    type="number"
                    placeholder="Od"
                    min="0"
                    value={advancedFilters.amountFrom}
                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, amountFrom: e.target.value }))}
                    className="!py-2 text-sm min-w-0"
                  />
                  <Input
                    type="number"
                    placeholder="Do"
                    min="0"
                    value={advancedFilters.amountTo}
                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, amountTo: e.target.value }))}
                    className="!py-2 text-sm min-w-0"
                  />
                </div>
              </div>

              {/* Date From */}
              <div>
                <label className="label text-xs">Data od</label>
                <Input
                  type="date"
                  value={advancedFilters.dateFrom}
                  onChange={(e) => setAdvancedFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                  className="!py-2 text-sm"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="label text-xs">Data do</label>
                <Input
                  type="date"
                  value={advancedFilters.dateTo}
                  onChange={(e) => setAdvancedFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                  className="!py-2 text-sm"
                />
              </div>

              {/* Payment Status */}
              <div>
                <label className="label text-xs">Płatność</label>
                <select
                  value={advancedFilters.paymentStatus}
                  onChange={(e) => setAdvancedFilters(prev => ({ ...prev, paymentStatus: e.target.value }))}
                  className="input !py-2 text-sm pr-8 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:16px] bg-[right_8px_center] bg-no-repeat"
                >
                  <option value="">Wszystkie</option>
                  <option value="paid">Opłacone</option>
                  <option value="unpaid">Nieopłacone</option>
                </select>
              </div>

              {/* Delivery Status */}
              <div>
                <label className="label text-xs">Dostawa</label>
                <select
                  value={advancedFilters.deliveryStatus}
                  onChange={(e) => setAdvancedFilters(prev => ({ ...prev, deliveryStatus: e.target.value }))}
                  className="input !py-2 text-sm pr-8 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:16px] bg-[right_8px_center] bg-no-repeat"
                >
                  <option value="">Wszystkie</option>
                  <option value="shipped">Wysłane</option>
                  <option value="not_shipped">Niewysłane</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Orders Toolbar */}
        <div className="flex items-center justify-between gap-4 mb-4 p-3 bg-surface-raised/50 border border-border-subtle rounded-xl">
          {/* Left side - Selection & Actions */}
          <div className="flex items-center gap-2">
            {/* Select All Checkbox with dropdown - R5.3 (2026-04-22): styl box jak
                Checkbox component z ui/ (rounded-[4px], border, bg accent gdy zaznaczony).
                Custom bo indeterminate (Minus) wymaga 3. stanu którego Checkbox nie ma. */}
            <div className="relative flex items-center" ref={selectDropdownRef}>
              <button
                onClick={toggleSelectAll}
                className="p-2 rounded-l-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all border-r border-border flex items-center justify-center"
                title={isAllSelected ? 'Odznacz wszystkie' : 'Zaznacz wszystkie na stronie'}
              >
                <span
                  className={`w-4 h-4 shrink-0 rounded-[4px] border transition-colors flex items-center justify-center ${
                    isAllSelected || selectAllInStatus
                      ? 'bg-accent-primary border-accent-primary'
                      : isSomeSelected
                        ? 'bg-accent-primary border-accent-primary'
                        : 'border-border bg-surface-base'
                  }`}
                >
                  {(isAllSelected || selectAllInStatus) && <Check className="w-3 h-3 text-text-primary" strokeWidth={3} />}
                  {!isAllSelected && !selectAllInStatus && isSomeSelected && <Minus className="w-3 h-3 text-text-primary" strokeWidth={3} />}
                </span>
              </button>
              <button
                onClick={() => setSelectDropdownOpen(!selectDropdownOpen)}
                className="p-2 rounded-r-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all"
              >
                <ChevronDown className="w-4 h-4" />
              </button>

              {selectDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-surface-hover border border-border rounded-xl shadow-xl z-20 py-2">
                  <button
                    onClick={toggleSelectAll}
                    className="w-full text-left px-3 py-2 text-sm text-text-secondary hover:bg-surface-muted transition-all"
                  >
                    Zaznacz wszystkie na stronie ({orders.length})
                  </button>
                  <button
                    onClick={toggleSelectAllInStatus}
                    className="w-full text-left px-3 py-2 text-sm text-text-secondary hover:bg-surface-muted transition-all"
                  >
                    Zaznacz wszystkie w statusie ({pagination.total})
                  </button>
                  {selectedOrders.size > 0 && (
                    <button
                      onClick={clearSelection}
                      className="w-full text-left px-3 py-2 text-sm text-danger hover:bg-surface-muted transition-all"
                    >
                      Odznacz wszystkie
                    </button>
                  )}
                </div>
              )}
            </div>

            {toolbarIcons.showStatusChange && <div className="h-6 w-px bg-surface-hover/60" />}

            {/* Bulk Status Change */}
            {toolbarIcons.showStatusChange && (
            <div className="relative" ref={bulkStatusDropdownRef}>
              <button
                onClick={() => setBulkStatusDropdownOpen(!bulkStatusDropdownOpen)}
                disabled={selectedOrders.size === 0 || bulkStatusLoading}
                className="p-2 rounded-lg text-text-secondary hover:text-accent-primary hover:bg-accent-primary/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                title="Zmień status zaznaczonych"
              >
                {bulkStatusLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin text-accent-primary" />
                ) : (
                  <Tag className="w-5 h-5" />
                )}
              </button>

              {bulkStatusDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-surface-hover border border-border rounded-xl shadow-xl z-20 py-2 max-h-96 flex flex-col">
                  <div className="px-3 py-2 text-xs text-text-muted uppercase tracking-wider border-b border-border-subtle flex items-center gap-2">
                    {bulkStatusLoading ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin text-accent-primary" />
                        <span>Zmiana statusu...</span>
                      </>
                    ) : (
                      <span>Zmień status ({getSelectedCount()} zaznaczonych)</span>
                    )}
                  </div>
                  {/* Search input */}
                  <div className="px-2 py-2 border-b border-border-subtle">
                    <input
                      type="text"
                      placeholder="Szukaj statusu..."
                      value={bulkStatusSearch}
                      onChange={(e) => setBulkStatusSearch(e.target.value)}
                      className="w-full px-3 py-1.5 bg-surface-raised border border-border rounded-lg text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-primary/50 disabled:opacity-50"
                      disabled={bulkStatusLoading}
                      autoFocus
                    />
                  </div>
                  <div className={`px-2 overflow-y-auto flex-1 ${bulkStatusLoading ? 'opacity-50 pointer-events-none' : ''}`}>
                    {/* Unassigned option at the top - show only if enabled in settings and no search or matches */}
                    {showUnassignedStatus && (!bulkStatusSearch || 'nieprzypisany'.includes(bulkStatusSearch.toLowerCase())) && (
                      <button
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { handleBulkStatusChange(''); setBulkStatusSearch(''); }}
                        disabled={bulkStatusLoading}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm text-text-secondary hover:bg-surface-muted transition-all flex items-center gap-2 mb-1 border-b border-border-subtle pb-2 disabled:cursor-not-allowed"
                      >
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-gray-500" />
                        Nieprzypisany
                      </button>
                    )}
                    {/* User-created statuses - filtered by search */}
                    {statuses.length > 0 ? (
                      <>
                        {getAllStatuses()
                          .filter(status => !bulkStatusSearch || status.name.toLowerCase().includes(bulkStatusSearch.toLowerCase()))
                          .map(status => (
                          <div key={status.id}>
                            {status.isTopLevel && !bulkStatusSearch && (
                              <div className="border-t border-border my-2" />
                            )}
                            <button
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => { handleBulkStatusChange(status.id); setBulkStatusSearch(''); }}
                              disabled={bulkStatusLoading}
                              className="w-full text-left px-3 py-2 rounded-lg text-sm text-text-secondary hover:bg-surface-muted transition-all flex items-center gap-2 disabled:cursor-not-allowed"
                              style={{ paddingLeft: `${12 + status.level * 12}px` }}
                            >
                              <div
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: status.color }}
                              />
                              {status.name}
                            </button>
                          </div>
                        ))}
                      </>
                    ) : (
                      <div className="px-1 py-3 text-center">
                        <p className="text-text-muted text-sm">Brak zdefiniowanych statusów</p>
                        <p className="text-text-disabled text-xs mt-1">Utwórz statusy w Administracja → Statusy zamówień</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            )}

            {toolbarIcons.showLabels && <div className="h-6 w-px bg-surface-hover/60" />}

            {/* Shipping Labels Section */}
            {toolbarIcons.showLabels && (
              <>
                <button
                  onClick={handleBulkShipment}
                  disabled={selectedOrders.size === 0}
                  className="p-2 rounded-lg text-text-secondary hover:text-accent-primary hover:bg-accent-primary/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Nadaj przesyłki"
                >
                  <PackagePlus className="w-5 h-5" />
                </button>
                <button
                  onClick={handleBulkDownloadLabels}
                  disabled={selectedOrders.size === 0}
                  className="p-2 rounded-lg text-text-secondary hover:text-accent-primary hover:bg-accent-primary/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Pobierz listy przewozowe PDF"
                >
                  <Download className="w-5 h-5" />
                </button>
              </>
            )}

            {toolbarIcons.showInvoices && <div className="h-6 w-px bg-surface-hover/60" />}

            {/* Invoice Section */}
            {toolbarIcons.showInvoices && (
              <>
                <button
                  onClick={handleBulkCreateInvoices}
                  disabled={selectedOrders.size === 0}
                  className="p-2 rounded-lg text-text-secondary hover:text-accent-secondary hover:bg-accent-secondary/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Wystaw faktury"
                >
                  <Receipt className="w-5 h-5" />
                </button>
                <button
                  onClick={handleBulkDownloadInvoices}
                  disabled={selectedOrders.size === 0}
                  className="p-2 rounded-lg text-text-secondary hover:text-accent-secondary hover:bg-accent-secondary/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Pobierz faktury PDF"
                >
                  <FileDown className="w-5 h-5" />
                </button>
              </>
            )}

            <div className="h-6 w-px bg-surface-hover/60" />

            {/* Delete (Move to Trash) */}
            <button
              onClick={handleBulkDelete}
              disabled={selectedOrders.size === 0}
              className="p-2 rounded-lg text-text-secondary hover:text-danger hover:bg-danger/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              title="Przenieś do kosza"
            >
              <Trash2 className="w-5 h-5" />
            </button>

            {(selectedOrders.size > 0 || selectAllInStatus) && (
              <span className="text-sm text-text-secondary ml-2">
                Zaznaczono: {getSelectedCount()}
                {selectAllInStatus && <span className="text-accent-primary ml-1">(wszystkie)</span>}
              </span>
            )}
          </div>

          {/* Right side - Sorting */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-text-muted">Sortuj:</span>

            {/* Sort Field Dropdown */}
            <div className="relative" ref={sortDropdownRef}>
              <button
                onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                className="flex items-center gap-2 px-3 py-1.5 bg-surface-hover hover:bg-surface-muted border border-border rounded-lg text-sm text-text-secondary transition-all"
              >
                <ArrowUpDown className="w-4 h-4 text-text-muted" />
                {SORT_OPTIONS.find(o => o.value === sortBy)?.label}
                <ChevronDown className={`w-4 h-4 text-text-muted transition-transform ${sortDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {sortDropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-56 bg-surface-hover border border-border rounded-xl shadow-xl z-20 py-2">
                  <div className="px-3 py-2 text-xs text-text-muted uppercase tracking-wider border-b border-border-subtle mb-2">
                    Sortuj według
                  </div>
                  <div className="px-2">
                    {SORT_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSortBy(option.value);
                          setPagination(prev => ({ ...prev, page: 1 }));
                          setSortDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                          sortBy === option.value
                            ? 'bg-accent-primary/20 text-accent-primary'
                            : 'text-text-secondary hover:bg-surface-muted'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sort Order Toggle */}
            <button
              onClick={() => { setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc'); setPagination(prev => ({ ...prev, page: 1 })); }}
              className="flex items-center gap-1 px-3 py-1.5 bg-surface-hover hover:bg-surface-muted border border-border rounded-lg text-sm text-text-secondary transition-all"
              title={sortOrder === 'desc' ? 'Malejąco' : 'Rosnąco'}
            >
              {sortOrder === 'desc' ? (
                <>
                  <ArrowDown className="w-4 h-4 text-accent-primary" />
                  <span>Malejąco</span>
                </>
              ) : (
                <>
                  <ArrowUp className="w-4 h-4 text-accent-primary" />
                  <span>Rosnąco</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Orders table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="loader" />
          </div>
        ) : orders.length === 0 ? (
          <Card>
            <EmptyState
              icon={ShoppingCart}
              title="Brak zamówień"
              description={
                filters.search || filters.storeId || selectedStatus
                  ? 'Nie znaleziono zamówień dla wybranych filtrów.'
                  : 'Zsynchronizuj sklep, aby pobrać zamówienia.'
              }
            />
          </Card>
        ) : (
          <>
            {/* Top Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-text-secondary">
                  Pokazano {(pagination.page - 1) * pagination.limit + 1} -{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} z {pagination.total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="!p-2"
                    leftIcon={<ChevronLeft />}
                  />
                  <span className="text-sm text-text-secondary px-3">
                    {pagination.page} / {pagination.totalPages}
                  </span>
                  <Button
                    variant="secondary"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.totalPages}
                    className="!p-2"
                    leftIcon={<ChevronRight />}
                  />
                </div>
              </div>
            )}

            <Card padding="none" className="overflow-x-auto">
              <table className="table w-full min-w-[800px]">
                <thead>
                  <tr>
                    <th className="!w-12 !px-3"></th>
                    {([
                      { label: 'Nr zamówienia', field: 'orderNumber' as SortField | null, className: '!pl-1' },
                      { label: 'Klient', field: 'customerName' as SortField | null, className: '' },
                      { label: 'Produkty', field: null as SortField | null, className: '' },
                      { label: 'Kwota', field: 'totalAmount' as SortField | null, className: '' },
                      { label: 'Status', field: 'status' as SortField | null, className: '' },
                      { label: 'Data', field: 'externalCreatedAt' as SortField | null, className: '' },
                    ]).map(col => (
                      <th
                        key={col.label}
                        className={`${col.className} ${col.field ? 'cursor-pointer select-none hover:text-accent-primary transition-colors' : ''}`}
                        onClick={col.field ? () => {
                          if (sortBy === col.field) {
                            setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
                          } else {
                            setSortBy(col.field!);
                            setSortOrder('desc');
                          }
                          setPagination(prev => ({ ...prev, page: 1 }));
                        } : undefined}
                      >
                        <span className="inline-flex items-center gap-1">
                          {col.label}
                          {col.field && sortBy === col.field ? (
                            sortOrder === 'desc'
                              ? <ArrowDown className="w-3.5 h-3.5 text-accent-primary" />
                              : <ArrowUp className="w-3.5 h-3.5 text-accent-primary" />
                          ) : col.field ? (
                            <ArrowUpDown className="w-3.5 h-3.5 text-text-disabled" />
                          ) : null}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => {
                    const normEmail = (order.customerEmail || '').trim().toLowerCase();
                    const normPhone = (order.customerPhone || '').replace(/\D/g, '').replace(/^48/, '');
                    const isBlacklisted =
                      (normEmail && blacklistedEmails.has(normEmail)) ||
                      (normPhone && blacklistedPhones.has(normPhone));
                    return (
                      <OrderRow
                        key={order.id}
                        order={order}
                        isSelected={selectedOrders.has(order.id)}
                        onToggleSelect={() => toggleOrderSelection(order.id)}
                        onView={() => openOrderModal(order)}
                        statuses={statuses}
                        isBlacklisted={!!isBlacklisted}
                      />
                    );
                  })}
                </tbody>
              </table>
            </Card>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-6">
              <div className="flex items-center gap-4">
                <p className="text-sm text-text-secondary">
                  Pokazano {(pagination.page - 1) * pagination.limit + 1} -{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} z {pagination.total}
                </p>
                {/* Per-page selector */}
                <div className="relative" ref={perPageDropdownRef}>
                  <button
                    onClick={() => setPerPageDropdownOpen(!perPageDropdownOpen)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-surface-hover hover:bg-surface-muted border border-border rounded-lg text-sm text-text-secondary transition-all"
                  >
                    <span>{pagination.limit} / str.</span>
                    <ChevronDown className={`w-4 h-4 text-text-muted transition-transform ${perPageDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {perPageDropdownOpen && (
                    <div className="absolute bottom-full left-0 mb-2 w-28 bg-surface-hover border border-border rounded-xl shadow-xl z-20 py-2">
                      {PER_PAGE_OPTIONS.map(n => (
                        <button
                          key={n}
                          onClick={() => {
                            setPagination(prev => ({ ...prev, limit: n, page: 1 }));
                            setPerPageDropdownOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm transition-all ${
                            pagination.limit === n
                              ? 'bg-accent-primary/20 text-accent-primary'
                              : 'text-text-secondary hover:bg-surface-muted'
                          }`}
                        >
                          {n} / str.
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {pagination.totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="!p-2"
                    leftIcon={<ChevronLeft />}
                  />
                  <span className="text-sm text-text-secondary px-3">
                    {pagination.page} / {pagination.totalPages}
                  </span>
                  <Button
                    variant="secondary"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.totalPages}
                    className="!p-2"
                    leftIcon={<ChevronRight />}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <OrderDetailModal
          key={selectedOrder.id}
          order={selectedOrder}
          statuses={statuses}
          onClose={() => {
            setSelectedOrder(null);
            setAllOrderIds([]);
            setCurrentOrderIndex(-1);
          }}
          onOrderUpdated={handleOrderUpdated}
          onStatusRefresh={loadData}
          currentIndex={currentOrderIndex >= 0 ? currentOrderIndex : orders.findIndex(o => o.id === selectedOrder.id)}
          totalOrders={allOrderIds.length > 0 ? allOrderIds.length : orders.length}
          onPrevious={currentOrderIndex > 0 ? () => navigateToOrder(currentOrderIndex - 1) : undefined}
          onNext={currentOrderIndex >= 0 && currentOrderIndex < allOrderIds.length - 1 ? () => navigateToOrder(currentOrderIndex + 1) : undefined}
          showUnassignedStatus={showUnassignedStatus}
          onCreateSimilarOrder={prefillCreateOrderFromOrder}
          autoOpenRefund={deepLinkActionRef.current === 'refund'}
          onBlacklistChanged={reloadBlacklistSets}
        />
      )}

      {/* Create Manual Order Modal */}
      {showCreateOrderModal && <CreateOrderModal
        sourceOrder={similarOrderSource || undefined}
        initialStoreId={similarOrderSource?.store?.id || filters.storeId || (stores.filter(store => store.isActive !== false).length === 1 ? stores.find(store => store.isActive !== false)!.id : '')}
        onClose={() => setShowCreateOrderModal(false)}
        onCreated={() => { void loadOrders(); void loadData(); }}
      />}

    </div>
  );
}

function StatusTreeItem({
  status,
  level,
  selectedStatus,
  expandedStatuses,
  onSelect,
  onToggleExpand,
}: {
  status: StatusGroup;
  level: number;
  selectedStatus: string | null;
  expandedStatuses: Set<string>;
  onSelect: (statusId: string) => void;
  onToggleExpand: (id: string) => void;
}) {
  const isExpanded = expandedStatuses.has(status.id);
  const hasChildren = status.children && status.children.length > 0;
  // Compare by status ID, not name (to avoid selecting multiple statuses with same name)
  const isSelected = selectedStatus === status.id;

  return (
    <div>
      <div
        className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-all ${
          isSelected
            ? 'bg-surface-hover/60'
            : 'hover:bg-surface-hover'
        }`}
        style={{ paddingLeft: `${8 + level * 12}px` }}
      >
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(status.id);
            }}
            className="p-0.5"
          >
            {isExpanded ? (
              <ChevronDown className="w-3 h-3 text-text-muted" />
            ) : (
              <ChevronRight className="w-3 h-3 text-text-muted" />
            )}
          </button>
        ) : (
          <div className="w-4" />
        )}

        <div
          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: status.color }}
        />

        <button
          onClick={() => onSelect(status.id)}
          className="flex-1 text-left text-sm text-text-secondary hover:text-text-primary truncate"
          title={status.name}
        >
          {status.name}
        </button>

        <span className="text-xs text-text-muted bg-surface-hover px-1.5 py-0.5 rounded min-w-[20px] text-center">
          {status._totalCount ?? status._count?.orders ?? 0}
        </span>
      </div>

      {isExpanded && hasChildren && (
        <div>
          {status.children.map(child => (
            <StatusTreeItem
              key={child.id}
              status={child}
              level={level + 1}
              selectedStatus={selectedStatus}
              expandedStatuses={expandedStatuses}
              onSelect={onSelect}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function OrderRow({
  order,
  isSelected,
  onToggleSelect,
  onView,
  statuses,
  isBlacklisted,
}: {
  order: Order;
  isSelected: boolean;
  onToggleSelect: () => void;
  onView: () => void;
  statuses: StatusGroup[];
  isBlacklisted: boolean;
}) {
  const { theme } = useTheme();
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: currency || 'PLN',
    }).format(amount);
  };

  // Check actual payment status from WooCommerce order data
  const rawData = (order as unknown as { rawData?: Record<string, unknown> }).rawData || {};
  const wooStatus = (rawData.status as string) || (order as unknown as { externalStatus?: string }).externalStatus || '';
  const datePaid = rawData.date_paid as string | null;
  const paymentMethodLower = (order.paymentMethod || '').toLowerCase();

  // Check if this is a Cash on Delivery (COD) order
  // Keywords matching backend's inpost.service.ts COD detection
  const isCOD = paymentMethodLower.includes('pobranie') ||
    paymentMethodLower.includes('pobraniem') ||
    paymentMethodLower.includes('przy odbiorze') ||
    paymentMethodLower.includes('przy dostawie') ||
    paymentMethodLower.includes('płatne kurierowi') ||
    paymentMethodLower.includes('płatne u kuriera') ||
    paymentMethodLower.includes('gotówka') ||
    paymentMethodLower.includes('cash on delivery') ||
    /(?:^|[^a-z])cod(?:$|[^a-z])/.test(paymentMethodLower);

  // Order is paid if:
  // 1. NOT a COD order AND has date_paid set (not null/empty)
  // 2. Or NOT a COD order AND status is processing/completed
  // COD orders are NEVER considered automatically paid — they must be marked manually
  const isPaid = !isCOD && (
    Boolean(datePaid && datePaid !== '0000-00-00T00:00:00') ||
    ['processing', 'completed'].includes(wooStatus.toLowerCase())
  );

  // Check for active shipments (not FAILED or RETURNED)
  const activeShipments = (order.shipments || []).filter(
    s => !['FAILED', 'RETURNED'].includes(s.status)
  );
  const hasShipment = activeShipments.length > 0;
  // Check for active invoices (non-deleted)
  const activeInvoices = (order.invoices || []).filter(inv => inv.status !== 'DELETED');
  const deletedInvoices = (order.invoices || []).filter(inv => inv.status === 'DELETED');
  const hasActiveInvoice = activeInvoices.length > 0;
  const hasDeletedInvoice = deletedInvoices.length > 0;

  // PB2 (2026-04-30): customer notes flag - notatki dodane przez UI idą do osobnej tabeli CustomerNote.
  // Backend zwraca licznik w order.customerNotesCount.
  const hasCustomerNotes = ((order as { customerNotesCount?: number }).customerNotesCount || 0) > 0;

  // Get products from order items
  const items = order.items as Array<{ name: string; quantity: number; total: string; sku?: string }> || [];
  const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 1), 0);

  // Find custom status color from statuses tree
  const findStatusInTree = (statusList: StatusGroup[], statusName: string): StatusGroup | null => {
    for (const s of statusList) {
      if (s.name === statusName) return s;
      if (s.children?.length) {
        const found = findStatusInTree(s.children, statusName);
        if (found) return found;
      }
    }
    return null;
  };
  const customStatus = findStatusInTree(statuses, order.status);

  return (
    <tr
      onClick={onView}
      className={`group cursor-pointer hover:bg-surface-hover/30 transition-colors ${isSelected ? 'bg-accent-primary/5' : ''}`}
    >
      {/* Checkbox kolumna - Checkbox component z ui/ (konwencja branding).
          Wycentrowana w dostępnej przestrzeni przez flex items-center justify-center.
          stopPropagation na td - żeby kliknięcie w checkbox nie trafiało do row onClick. */}
      <td className="!w-12 !px-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-center">
          <Checkbox
            checked={isSelected}
            onChange={onToggleSelect}
            aria-label={`Zaznacz zamówienie #${order.orderNumber}`}
          />
        </div>
      </td>
      <td className="!pl-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-text-primary text-sm group-hover:text-accent-primary transition-colors">
            #{order.orderNumber}
          </span>
          {/* Status icons (info-only) */}
          <div className="flex items-center gap-0.5">
            <div
              className={`p-0.5 rounded ${isPaid ? 'text-success' : 'text-text-disabled'}`}
              title={isPaid ? 'Opłacone' : 'Nieopłacone / Za pobraniem'}
            >
              <CreditCard className="w-3 h-3" />
            </div>
            <div
              className={`p-0.5 rounded ${hasShipment ? 'text-accent-primary' : 'text-text-disabled'}`}
              title={hasShipment ? 'Przesyłka nadana' : 'Brak przesyłki'}
            >
              <Truck className="w-3 h-3" />
            </div>
            <div
              className={`p-0.5 rounded ${hasActiveInvoice ? 'text-accent-secondary' : hasDeletedInvoice ? 'text-text-muted line-through' : 'text-text-disabled'}`}
              title={hasActiveInvoice ? 'Faktura wystawiona' : hasDeletedInvoice ? 'Faktura usunięta' : 'Brak faktury'}
            >
              <FileText className={`w-3 h-3 ${hasDeletedInvoice && !hasActiveInvoice ? 'opacity-50' : ''}`} />
            </div>
          </div>
          {/* Separator: status info | markery zamówienia.
              Kolor i grubość matchują linie wierszy tabeli (border-subtle), żeby
              wyglądało spójnie w obu motywach (jasny/ciemny). */}
          {(order.source === 'MANUAL' || order.source === 'RESEND' || !!order.notes || hasCustomerNotes || isBlacklisted || (order as { isOnHold?: boolean }).isOnHold) && (
            <span className="inline-block w-[1px] h-4 bg-border-subtle mx-0.5" aria-hidden="true" />
          )}
          {/* Marker icons (per-order flagi) */}
          <div className="flex items-center gap-0.5">
            {order.source === 'MANUAL' && (
              <span className="p-0.5 rounded text-text-muted" title="Zamówienie ręczne">
                <PenLine className="w-3 h-3" />
              </span>
            )}
            {order.source === 'RESEND' && !hasShipment && (
              <span
                className="p-0.5 rounded text-warning animate-pulse"
                title="Opłacona ponowna wysyłka - wymaga nadania"
                aria-label="Opłacona ponowna wysyłka wymaga nadania"
              >
                <PackagePlus className="w-3.5 h-3.5" />
              </span>
            )}
            {order.source === 'RESEND' && hasShipment && (
              <span className="p-0.5 rounded text-accent-primary" title="Ponowna wysyłka nadana">
                <Send className="w-3 h-3" />
              </span>
            )}
            {(!!order.notes || hasCustomerNotes) && (
              <span className="p-0.5 rounded text-amber-400" title={hasCustomerNotes ? `Zamówienie ma ${(order as { customerNotesCount?: number }).customerNotesCount || 1} notat${((order as { customerNotesCount?: number }).customerNotesCount || 1) === 1 ? 'kę' : 'ek'}` : 'Zamówienie ma notatkę'}>
                <StickyNote className="w-3 h-3" />
              </span>
            )}
            {/* N2 (2026-04-22): marker klienta na czarnej liście */}
            {isBlacklisted && (
              <span className="p-0.5 rounded text-danger" title="Klient na czarnej liście - pomijany przy masowym nadawaniu">
                <SkullIcon className="w-3 h-3" />
              </span>
            )}
            {/* P10 (2026-04-30): poczekalnia - zamówienie pomijane przy bulk nadawaniu */}
            {(order as { isOnHold?: boolean }).isOnHold && (
              <span className="p-0.5 rounded text-info" title="Poczekalnia - pomijane przy masowym nadawaniu listów">
                <Hourglass className="w-3 h-3" />
              </span>
            )}
          </div>
        </div>
        <span className="text-xs text-text-muted">{order.store?.name}</span>
      </td>
      <td>
        <div className="text-text-primary text-sm">{order.customerName || '-'}</div>
        <div className="text-xs text-text-muted truncate max-w-[150px]">{order.customerEmail}</div>
      </td>
      <td>
        {items.length > 0 ? (
          <div className="max-w-[180px]">
            <div className="text-sm text-text-primary truncate" title={items[0]?.name}>
              {items[0]?.name}
              <span className="text-accent-primary font-medium ml-1">x{items[0]?.quantity || 1}</span>
            </div>
            {items.length > 1 && (
              <div className="text-xs text-text-muted">
                +{items.length - 1} {items.length === 2 ? 'produkt' : items.length <= 4 ? 'produkty' : 'produktów'}
                <span className="text-accent-primary ml-1">({totalQuantity} szt.)</span>
              </div>
            )}
            {items.length === 1 && totalQuantity > 1 && (
              <div className="text-xs text-text-muted">
                <span className="text-accent-primary">({totalQuantity} szt. łącznie)</span>
              </div>
            )}
          </div>
        ) : (
          <span className="text-text-muted text-sm">-</span>
        )}
      </td>
      <td>
        <span className="font-semibold text-success text-sm">
          {formatCurrency(order.totalAmount, order.currency)}
        </span>
      </td>
      <td>
        {order.status === 'Kosz' ? (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-danger/20 text-danger border border-danger/40">
            <span className="w-1.5 h-1.5 rounded-full bg-danger" />
            Kosz
          </span>
        ) : customStatus ? (() => {
          const resolvedColor = statusColor(customStatus, theme);
          const resolvedText = statusTextColor(customStatus, theme);
          return (
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap border border-border-strong"
              style={{
                backgroundColor: `${resolvedColor}20`,
                color: resolvedText,
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: resolvedColor }}
              />
              {order.status}
            </span>
          );
        })() : (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-surface-muted text-text-secondary border border-border-strong">
            <span className="w-1.5 h-1.5 rounded-full bg-text-muted" />
            Nieprzypisany
          </span>
        )}
      </td>
      <td>
        {/* Order creation date */}
        <div className="text-xs text-text-secondary">
          {new Date(order.externalCreatedAt).toLocaleTimeString('pl-PL', {
            timeZone: 'Europe/Warsaw',
            hour: '2-digit',
            minute: '2-digit',
          })}{' '}
          {new Date(order.externalCreatedAt).toLocaleDateString('pl-PL', { timeZone: 'Europe/Warsaw' })}
        </div>
        {/* Status change date */}
        {order.statusChangedAt && (
          <div
            className="text-xs text-text-muted cursor-help"
            title="Data przeniesienia zamówienia do aktualnego statusu"
          >
            {new Date(order.statusChangedAt).toLocaleTimeString('pl-PL', {
              timeZone: 'Europe/Warsaw',
              hour: '2-digit',
              minute: '2-digit',
            })}{' '}
            {new Date(order.statusChangedAt).toLocaleDateString('pl-PL', { timeZone: 'Europe/Warsaw' })}
          </div>
        )}
      </td>
    </tr>
  );
}

export function OrderDetailModal({
  order,
  statuses,
  onClose,
  onOrderUpdated,
  onStatusRefresh,
  onPrevious,
  onNext,
  currentIndex,
  totalOrders,
  showUnassignedStatus,
  onCreateSimilarOrder,
  autoOpenRefund,
  initialComplaintId,
  onComplaintUpdated,
  onBlacklistChanged,
}: {
  order: Order;
  statuses: StatusGroup[];
  onClose: () => void;
  onOrderUpdated: (order: Order, options?: { closeModal?: boolean; navigateTo?: boolean }) => void;
  onStatusRefresh?: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  currentIndex?: number;
  totalOrders?: number;
  showUnassignedStatus: boolean;
  onCreateSimilarOrder?: (order: Order) => void;
  autoOpenRefund?: boolean;
  initialComplaintId?: string;
  onComplaintUpdated?: () => void;
  // R5.1 (2026-04-22): rodzic reloaduje zbiory blacklistedEmails/Phones po zmianie.
  onBlacklistChanged?: () => void;
}) {
  const notesQueryClient = useQueryClient();
  const [activeComplaintId, setActiveComplaintId] = useState(initialComplaintId);
  const financeRole = useAuthStore(state => state.user?.role);
  // Debug: log when component renders with new order
  console.log('[OrderDetailModal] Rendering with order:', order.orderNumber, 'status:', order.status);

  const { showSuccess, showError, showWarning } = useNotification();
  const { theme } = useTheme();
  const confirmDialog = useConfirmDialog();
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusSearch, setStatusSearch] = useState('');
  const [history, setHistory] = useState<OrderHistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [customerHistory, setCustomerHistory] = useState<CustomerPurchaseHistory | null>(null);
  const [loadingCustomerHistory, setLoadingCustomerHistory] = useState(true);
  const [creatingShipment, setCreatingShipment] = useState(false);
  const [shipmentError, setShipmentError] = useState<string | null>(null);

  // Shipment configuration state
  const [selectedShipmentService, setSelectedShipmentService] = useState<'inpost_locker_standard' | 'inpost_courier_standard' | null>(null);
  const [shipmentConfig, setShipmentConfig] = useState({
    parcelSize: 'small' as 'small' | 'medium' | 'large',
    codAmount: '',
    insurance: '',
    comments: '',
    commentsModified: false, // Track if user manually edited comments
  });

  // Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editedCustomer, setEditedCustomer] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [editedShipping, setEditedShipping] = useState({
    firstName: '',
    lastName: '',
    company: '',
    address1: '',
    address2: '',
    city: '',
    postcode: '',
    country: '',
    phone: '',
  });
  const [editedPaczkomat, setEditedPaczkomat] = useState('');
  const [editedBilling, setEditedBilling] = useState({
    firstName: '',
    lastName: '',
    company: '',
    address1: '',
    address2: '',
    city: '',
    postcode: '',
    country: '',
    phone: '',
    email: '',
  });
  const [savingOrder, setSavingOrder] = useState(false);

  // Invoice state
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState<string | null>(null);
  const [creatingCorrectionInvoice, setCreatingCorrectionInvoice] = useState<string | null>(null);

  // Navigate do innych modułów (np. deeplink do Wiadomości z filtrem customer).
  // UWAGA: deeplink do Wiadomości otwieramy teraz w NOWEJ karcie (target=_blank),
  // więc w praktyce navigate() nie jest używany dla tego konkretnego flow.
  // Zachowany — może się przydać dla innych integracji w przyszłości.
  const navigate = useNavigate();
  void navigate;

  // Licznik konwersacji z tym klientem — używany na przycisku "Zobacz konwersację".
  // Tanie Prisma.count z OR(from LIKE, to LIKE). Pobieramy raz po otwarciu
  // karty, bez refetchu przy każdej mutacji — jeśli user doda maila i wróci,
  // remount karty i tak pobierze świeży licznik.
  const [conversationCount, setConversationCount] = useState<number | null>(null);
  useEffect(() => {
    if (!order.customerEmail) { setConversationCount(null); return; }
    let cancelled = false;
    messagesApi.countByCustomer(order.customerEmail).then(res => {
      if (!cancelled) setConversationCount(res.data.count);
    }).catch(() => {
      if (!cancelled) setConversationCount(null);
    });
    return () => { cancelled = true; };
  }, [order.customerEmail]);

  // Email state
  const [orderEmails, setOrderEmails] = useState<EmailLog[]>([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [customEmailSubject, setCustomEmailSubject] = useState('');
  const [customEmailBody, setCustomEmailBody] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [templateNotice, setTemplateNotice] = useState('');
  const messageTemplates = useQuery({
    queryKey: ['message-templates', order.store?.id],
    enabled: showEmailModal && !activeComplaintId && Boolean(order.store?.id),
    queryFn: async () => (await messagesApi.getTemplates({ storeId: order.store?.id })).data.data as MessageTemplateItem[],
  });
  const applyMessageTemplate = useMutation({
    mutationFn: async (id: string) => (await messagesApi.applyTemplate(id, { orderId: order.id, aiPersonalize: false })).data,
    onSuccess: data => {
      if (data.subject) setCustomEmailSubject(data.subject);
      setTemplateNotice(data.missingVariables.length ? `Uzupełnij brakujące dane: ${data.missingVariables.map(field => field.label).join(', ')}.` : 'Szablon wstawiony. Sprawdź treść przed wysłaniem.');
    },
  });
  const [loadingEmails, setLoadingEmails] = useState(true);
  const [previewingEmail, setPreviewingEmail] = useState<EmailLog | null>(null);

  // Manual automations state
  const [manualAutomations, setManualAutomations] = useState<ManualAutomation[]>([]);
  const [executingAutomation, setExecutingAutomation] = useState<string | null>(null);
  const [automationError, setAutomationError] = useState<string | null>(null);
  const [automationSuccess, setAutomationSuccess] = useState<string | null>(null);

  // Notes state
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null); // null = adding new, string = editing existing
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [editedNote, setEditedNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Refund state
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [showRefundHistory, setShowRefundHistory] = useState(false);
  const [refundInfo, setRefundInfo] = useState<RefundInfo | null>(null);
  const [loadingRefundInfo, setLoadingRefundInfo] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundDescription, setRefundDescription] = useState('');
  const [processingRefund, setProcessingRefund] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);

  // Portal activities state
  const [portalActivities, setPortalActivities] = useState<PortalActivity[]>([]);
  const [loadingPortalActivities, setLoadingPortalActivities] = useState(false);

  // N2 (2026-04-22): blacklist state dla customera z tego zamówienia.
  // null = nie sprawdzono, true/false = wynik z /blacklist/check.
  const [isCustomerBlacklisted, setIsCustomerBlacklisted] = useState<boolean | null>(null);
  const [togglingBlacklist, setTogglingBlacklist] = useState(false);

  // P10 (2026-04-30): Poczekalnia toggle - zamówienie pomijane przy bulk nadawaniu listów.
  const [togglingHold, setTogglingHold] = useState(false);

  // Detect which address fields caused the last shipment error — for red highlighting
  const shipmentErrorFields = useMemo(() => {
    const fields = new Set<string>();
    // Find the most recent shipment_error in history
    const lastError = [...history].reverse().find(e => e.action === 'shipment_error');
    if (!lastError) return fields;

    // If a successful shipment was created AFTER the error — problem resolved
    const errorTime = new Date(lastError.createdAt).getTime();
    const hasNewerShipment = (order.shipments || []).some(
      (s: { createdAt: string }) => new Date(s.createdAt).getTime() > errorTime
    );
    if (hasNewerShipment) return fields;

    // If order was edited AFTER the error — address likely corrected
    const hasNewerEdit = [...history].reverse().find(
      e => e.action === 'ORDER_UPDATE' && new Date(e.createdAt).getTime() > errorTime
    );
    if (hasNewerEdit) return fields;

    const metadata = lastError.metadata as Record<string, unknown> | null;
    const errorType = metadata?.errorType as string || '';
    const errorMessage = (metadata?.errorMessage as string || '').toLowerCase();
    // Map errorType to field names — authoritative source when backend tagged the error.
    const typeFieldMap: Record<string, string[]> = {
      'invalid_phone': ['phone'],
      'missing_phone': ['phone'],
      'invalid_email': ['email'],
      'missing_email': ['email'],
      'invalid_postal_code': ['postcode'],
      'missing_postal_code': ['postcode'],
      'invalid_city': ['city'],
      'missing_city': ['city'],
      'invalid_street': ['address1'],
      'missing_street': ['address1'],
      'missing_building_number': ['address1'],
      'invalid_locker': ['paczkomat'],
      'missing_locker': ['paczkomat'],
      'locker_full': ['paczkomat'],
      'locker_disabled': ['paczkomat'],
      'invalid_name': ['firstName', 'lastName'],
      'missing_name': ['firstName', 'lastName'],
    };
    // If backend tagged a known errorType, trust it exclusively.
    // The message heuristic below used to run unconditionally and "adres email"
    // fired the 'adres' rule for address1 — so an invalid email highlighted both
    // email AND the physical address. Regression user reported 2026-04-22.
    if (typeFieldMap[errorType]) {
      typeFieldMap[errorType].forEach(f => fields.add(f));
      return fields;
    }
    // Fallback heuristic (no typed error) — guard 'adres' so "adres email" doesn't
    // leak into address1.
    if (errorMessage.includes('phone') || errorMessage.includes('telefon')) fields.add('phone');
    if (errorMessage.includes('email') || errorMessage.includes('e-mail')) fields.add('email');
    if (errorMessage.includes('post_code') || errorMessage.includes('kod pocztowy') || errorMessage.includes('postcode')) fields.add('postcode');
    if (errorMessage.includes('city') || errorMessage.includes('miasto')) fields.add('city');
    const mentionsEmail = errorMessage.includes('email') || errorMessage.includes('e-mail');
    const mentionsAddress = !mentionsEmail && errorMessage.includes('adres');
    if (errorMessage.includes('street') || errorMessage.includes('ulic') || mentionsAddress || errorMessage.includes('building') || errorMessage.includes('budynk')) fields.add('address1');
    if (errorMessage.includes('paczkomat') || errorMessage.includes('target_point') || errorMessage.includes('locker')) fields.add('paczkomat');
    if (errorMessage.includes('name') || errorMessage.includes('imię') || errorMessage.includes('nazwisk')) { fields.add('firstName'); fields.add('lastName'); }
    return fields;
  }, [history, order.shipments]);

  // During editing: clear red highlight for fields the user has already changed
  const activeErrorFields = useMemo(() => {
    if (shipmentErrorFields.size === 0) return shipmentErrorFields;
    if (!isEditing) return shipmentErrorFields;

    const shipping = order.shippingAddress as Record<string, string> || {};
    const active = new Set(shipmentErrorFields);

    // Compare edited values vs original — if user changed a red field, clear it
    const originals: Record<string, string> = {
      firstName: shipping.first_name || shipping.firstName || '',
      lastName: shipping.last_name || shipping.lastName || '',
      address1: (() => {
        const a1 = shipping.address_1 || shipping.address1 || '';
        const a2 = shipping.address_2 || shipping.address2 || '';
        return a2 ? `${a1} ${a2}`.trim() : a1;
      })(),
      postcode: shipping.postcode || shipping.postal_code || '',
      city: shipping.city || '',
      phone: shipping.phone || order.customerPhone || '',
    };
    const edited: Record<string, string> = {
      firstName: editedShipping.firstName,
      lastName: editedShipping.lastName,
      address1: editedShipping.address1,
      postcode: editedShipping.postcode,
      city: editedShipping.city,
      phone: editedShipping.phone,
    };

    for (const field of active) {
      if (field in originals && edited[field] !== originals[field]) {
        active.delete(field);
      }
    }

    if (active.has('email') && editedCustomer.email !== (order.customerEmail || '')) {
      active.delete('email');
    }

    return active;
  }, [shipmentErrorFields, isEditing, order.shippingAddress, order.customerPhone, order.customerEmail, editedShipping, editedCustomer.email]);

  // Unified CustomerNote. W szufladzie zamówienia pokazujemy tylko notatki
  // przypięte do tego konkretnego orderId, bez automatycznego scalania po emailu.
  const [customerNotes, setCustomerNotes] = useState<CustomerNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);

  const loadCustomerNotes = useCallback(async () => {
    if (!order.id) return;
    setNotesLoading(true);
    try {
      const res = await customerNotesApi.list({
        orderId: order.id,
      });
      setCustomerNotes(res.data.data || []);
      notesQueryClient.setQueryData(['customer-notes', 'order', order.id], res.data.data || []);
    } catch {
      setCustomerNotes([]);
    } finally {
      setNotesLoading(false);
    }
  }, [order.id, notesQueryClient]);

  useEffect(() => {
    loadCustomerNotes();
  }, [loadCustomerNotes]);

  // Fetch order history
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoadingHistory(true);
        const { data } = await orderApi.getHistory(order.id);
        setHistory(data);
      } catch (err) {
        console.error('Failed to fetch order history:', err);
      } finally {
        setLoadingHistory(false);
      }
    };
    fetchHistory();
  }, [order.id]);

  // N2 (2026-04-22): sprawdzenie czy customer z tego zamówienia jest na czarnej liście.
  useEffect(() => {
    const check = async () => {
      try {
        const shipping = (order.shippingAddress as Record<string, string> | null) || {};
        const addressStr = [shipping.address_1 || shipping.address1 || '', shipping.postcode || shipping.postal_code || '', shipping.city || ''].filter(Boolean).join(' ');
        const { data } = await blacklistApi.check({
          email: order.customerEmail || undefined,
          phone: order.customerPhone || undefined,
          address: addressStr || undefined,
        });
        setIsCustomerBlacklisted(data.blacklisted);
      } catch (err) {
        console.error('Failed to check blacklist:', err);
      }
    };
    check();
  }, [order.id, order.customerEmail, order.customerPhone, order.shippingAddress]);

  const handleToggleBlacklist = async () => {
    if (togglingBlacklist) return;
    setTogglingBlacklist(true);
    try {
      if (isCustomerBlacklisted) {
        // Usunięcie - potrzebne są ID rekordów, ale mamy tylko status. Wywołujemy check
        // żeby dostać matches, potem usuwamy każdy (z orderId dla wpisu w OrderHistory).
        const shipping = (order.shippingAddress as Record<string, string> | null) || {};
        const addressStr = [shipping.address_1 || shipping.address1 || '', shipping.postcode || shipping.postal_code || '', shipping.city || ''].filter(Boolean).join(' ');
        const { data } = await blacklistApi.check({
          email: order.customerEmail || undefined,
          phone: order.customerPhone || undefined,
          address: addressStr || undefined,
        });
        await Promise.all(data.matches.map(m => blacklistApi.remove(m.id, order.id)));
        setIsCustomerBlacklisted(false);
        showSuccess('shipment', { message: 'Klient usunięty z czarnej listy' });
      } else {
        // Dodanie - robimy wpis dla każdego dostępnego identyfikatora (email i phone).
        // Pierwsze wywołanie (index 0) dostaje orderId - zostawia tylko jeden wpis
        // w OrderHistory zamiast duplikatów per identyfikator.
        const requests: Promise<unknown>[] = [];
        let addedAnchor = false;
        if (order.customerEmail) {
          requests.push(blacklistApi.add({
            identifier: order.customerEmail,
            identifierType: 'email',
            displayName: order.customerName || undefined,
            reason: `Dodane ręcznie z zamówienia #${order.orderNumber}`,
            orderId: order.id,
          }));
          addedAnchor = true;
        }
        if (order.customerPhone) {
          requests.push(blacklistApi.add({
            identifier: order.customerPhone,
            identifierType: 'phone',
            displayName: order.customerName || undefined,
            reason: `Dodane ręcznie z zamówienia #${order.orderNumber}`,
            orderId: addedAnchor ? undefined : order.id,
          }));
        }
        if (requests.length === 0) {
          showWarning('Brak danych klienta', 'Zamówienie nie ma ani email ani telefonu - nie można dodać do czarnej listy.');
          return;
        }
        await Promise.all(requests);
        setIsCustomerBlacklisted(true);
        showSuccess('shipment', { message: 'Klient dodany na czarną listę' });
      }
      // R5.1 (2026-04-22): odśwież rodzica (OrdersPage) żeby OrderRow dostał zaktualizowany
      // zbiór blacklistedEmails/Phones i pokazał ikonkę przy #numerze zamówienia.
      onBlacklistChanged?.();
      // Refresh historii zamówienia żeby wpis "Klient dodany/usunięty z czarnej listy" pojawił się od razu
      try {
        const { data: historyData } = await orderApi.getHistory(order.id);
        setHistory(historyData);
      } catch { /* silent */ }
    } catch (err) {
      console.error('Failed to toggle blacklist:', err);
      showError(err, 'save');
    } finally {
      setTogglingBlacklist(false);
    }
  };

  // P10 (2026-04-30): Toggle Poczekalnia - zamówienie pomijane przy bulk nadawaniu.
  const handleToggleHold = async () => {
    if (togglingHold) return;
    setTogglingHold(true);
    try {
      const { data } = await orderApi.toggleHold(order.id);
      onOrderUpdated(data);
      showSuccess('save', { message: data.isOnHold ? 'Zamówienie wstrzymane (Poczekalnia)' : 'Zamówienie wznowione' });
    } catch (err) {
      console.error('Failed to toggle hold:', err);
      showError(err, 'save');
    } finally {
      setTogglingHold(false);
    }
  };

  // Fetch customer purchase history
  useEffect(() => {
    const fetchCustomerHistory = async () => {
      try {
        setLoadingCustomerHistory(true);
        const { data } = await orderApi.getCustomerHistory(order.id);
        setCustomerHistory(data);
      } catch (err) {
        console.error('Failed to fetch customer history:', err);
      } finally {
        setLoadingCustomerHistory(false);
      }
    };
    fetchCustomerHistory();
  }, [order.id]);

  // Fetch refund info when order drawer opens
  useEffect(() => {
    const fetchRefundInfo = async () => {
      try {
        setLoadingRefundInfo(true);
        const { data: info } = await refundApi.getInfo(order.id);
        setRefundInfo(info);
      } catch (err) {
        console.error('Failed to fetch refund info:', err);
      } finally {
        setLoadingRefundInfo(false);
      }
    };
    fetchRefundInfo();
  }, [order.id]);

  // Fetch portal activities
  useEffect(() => {
    const loadActivities = async () => {
      setLoadingPortalActivities(true);
      try {
        const { data } = await portalApi.getActivities(order.id);
        setPortalActivities(data);
      } catch (err) {
        console.error('Failed to load portal activities:', err);
      } finally {
        setLoadingPortalActivities(false);
      }
    };
    loadActivities();
  }, [order.id]);

  // Fetch order email history
  useEffect(() => {
    const fetchEmailData = async () => {
      try {
        setLoadingEmails(true);
        const emailsRes = await emailApi.getOrderEmails(order.id);
        setOrderEmails(emailsRes.data.logs);
      } catch (err) {
        console.error('Failed to fetch email data:', err);
      } finally {
        setLoadingEmails(false);
      }
    };
    fetchEmailData();
  }, [order.id]);

  // Fetch manual automations (filtered by order's store)
  useEffect(() => {
    const fetchManualAutomations = async () => {
      try {
        const { data } = await automationApi.getManualAutomations(order.store?.id);
        setManualAutomations(data.automations);
      } catch (err) {
        console.error('Failed to fetch manual automations:', err);
      }
    };
    fetchManualAutomations();
  }, [order.store?.id]);

  // Initialize editing state from order data
  useEffect(() => {
    const shipping = order.shippingAddress as Record<string, string> || {};
    const billing = order.billingAddress as Record<string, string> || {};
    setEditedCustomer({
      name: order.customerName || '',
      email: order.customerEmail || '',
      phone: order.customerPhone || shipping.phone || '',
    });
    const _sAddr1 = shipping.address_1 || shipping.address1 || '';
    const _sAddr2 = shipping.address_2 || shipping.address2 || '';
    const _bAddr1 = billing.address_1 || billing.address1 || '';
    const _bAddr2 = billing.address_2 || billing.address2 || '';
    setEditedShipping({
      firstName: shipping.first_name || shipping.firstName || '',
      lastName: shipping.last_name || shipping.lastName || '',
      company: shipping.company || '',
      address1: _sAddr2 ? `${_sAddr1} ${_sAddr2}`.trim() : _sAddr1,
      address2: '',
      city: shipping.city || '',
      postcode: shipping.postcode || shipping.postal_code || '',
      country: shipping.country || 'PL',
      phone: shipping.phone || order.customerPhone || '',
    });
    setEditedBilling({
      firstName: billing.first_name || billing.firstName || '',
      lastName: billing.last_name || billing.lastName || '',
      company: billing.company || '',
      address1: _bAddr2 ? `${_bAddr1} ${_bAddr2}`.trim() : _bAddr1,
      address2: '',
      city: billing.city || '',
      postcode: billing.postcode || billing.postal_code || '',
      country: billing.country || 'PL',
      phone: billing.phone || '',
      email: billing.email || order.customerEmail || '',
    });
  }, [order]);

  // Start editing
  const handleStartEdit = () => {
    setIsEditing(true);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    // Reset to original values
    const shipping = order.shippingAddress as Record<string, string> || {};
    const billing = order.billingAddress as Record<string, string> || {};
    setEditedCustomer({
      name: order.customerName || '',
      email: order.customerEmail || '',
      phone: order.customerPhone || shipping.phone || '',
    });
    const _sAddr1 = shipping.address_1 || shipping.address1 || '';
    const _sAddr2 = shipping.address_2 || shipping.address2 || '';
    const _bAddr1 = billing.address_1 || billing.address1 || '';
    const _bAddr2 = billing.address_2 || billing.address2 || '';
    setEditedShipping({
      firstName: shipping.first_name || shipping.firstName || '',
      lastName: shipping.last_name || shipping.lastName || '',
      company: shipping.company || '',
      address1: _sAddr2 ? `${_sAddr1} ${_sAddr2}`.trim() : _sAddr1,
      address2: '',
      city: shipping.city || '',
      postcode: shipping.postcode || shipping.postal_code || '',
      country: shipping.country || 'PL',
      phone: shipping.phone || order.customerPhone || '',
    });
    setEditedBilling({
      firstName: billing.first_name || billing.firstName || '',
      lastName: billing.last_name || billing.lastName || '',
      company: billing.company || '',
      address1: _bAddr2 ? `${_bAddr1} ${_bAddr2}`.trim() : _bAddr1,
      address2: '',
      city: billing.city || '',
      postcode: billing.postcode || billing.postal_code || '',
      country: billing.country || 'PL',
      phone: billing.phone || '',
      email: billing.email || order.customerEmail || '',
    });
    setIsEditing(false);
  };

  // Copy customer data to billing
  const handleCopyCustomerToBilling = () => {
    setEditedBilling({
      firstName: editedCustomer.name.split(' ')[0] || '',
      lastName: editedCustomer.name.split(' ').slice(1).join(' ') || '',
      company: '',
      address1: editedShipping.address1,
      address2: editedShipping.address2,
      city: editedShipping.city,
      postcode: editedShipping.postcode,
      country: editedShipping.country,
      phone: editedCustomer.phone,
      email: editedCustomer.email,
    });
  };

  // Save edited order
  const handleSaveEdit = async () => {
    if (savingOrder) return;
    setSavingOrder(true);
    try {
      const { data } = await orderApi.update(order.id, {
        customerName: editedCustomer.name,
        customerEmail: editedCustomer.email,
        customerPhone: editedCustomer.phone,
        shippingAddress: {
          first_name: editedShipping.firstName,
          last_name: editedShipping.lastName,
          company: editedShipping.company,
          address_1: editedShipping.address1,
          address_2: '',
          city: editedShipping.city,
          postcode: editedShipping.postcode,
          country: editedShipping.country,
          phone: editedShipping.phone,
          // Persist paczkomat code in shipping address
          ...(editedPaczkomat ? { paczkomat_code: editedPaczkomat } : {}),
        },
        billingAddress: {
          first_name: editedBilling.firstName,
          last_name: editedBilling.lastName,
          company: editedBilling.company,
          address_1: editedBilling.address1,
          address_2: '',
          city: editedBilling.city,
          postcode: editedBilling.postcode,
          country: editedBilling.country,
          phone: editedBilling.phone,
          email: editedBilling.email,
        },
      });
      onOrderUpdated(data);
      setIsEditing(false);
      // Refresh history after update
      const historyRes = await orderApi.getHistory(order.id);
      setHistory(historyRes.data);

      // Delayed refresh to catch automation status changes (e.g., triggers that change status after order edit)
      setTimeout(async () => {
        try {
          console.log('[Order Edit Refresh] Fetching order after delay, current status:', order.status);
          const refreshedOrder = await orderApi.getOne(order.id);
          console.log('[Order Edit Refresh] New status from server:', refreshedOrder.data.status);
          if (refreshedOrder.data.status !== order.status) {
            console.log('[Order Edit Refresh] Status changed! Updating popup...');
            onOrderUpdated(refreshedOrder.data);
            onStatusRefresh?.();
          }
        } catch (e) {
          console.error('[Order Edit Refresh] Error:', e);
        }
      }, 1500);
    } catch (err) {
      console.error('Failed to save order:', err);
      showError(err, 'save');
    } finally {
      setSavingOrder(false);
    }
  };

  // Add new note — zapisuje do unified CustomerNote dla konkretnego zamówienia.
  // Backend automatycznie wstawia wpis do OrderHistory gdy orderId podany.
  const handleAddNote = async () => {
    if (savingNote || !editedNote.trim()) return;
    setSavingNote(true);
    try {
      await customerNotesApi.create({
        content: editedNote.trim(),
        orderId: order.id,
      });
      setIsAddingNote(false);
      setEditedNote('');
      await loadCustomerNotes();
      const refreshedOrder = await orderApi.getOne(order.id);
      onOrderUpdated(refreshedOrder.data);
      // Refresh history — OrderHistory ma wpis "note_added" z backendu.
      const historyRes = await orderApi.getHistory(order.id);
      setHistory(historyRes.data);
    } catch (err) {
      console.error('Failed to add note:', err);
      showError(err, 'save');
    } finally {
      setSavingNote(false);
    }
  };

  const handleUpdateNote = async (noteId: string) => {
    if (savingNote || !editedNote.trim()) return;
    setSavingNote(true);
    try {
      await customerNotesApi.update(noteId, { content: editedNote.trim() });
      setEditingNoteId(null);
      setEditedNote('');
      await loadCustomerNotes();
    } catch (err) {
      console.error('Failed to update note:', err);
      showError(err, 'save');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!(await confirmDialog({
      title: 'Usuń notatkę',
      message: 'Czy na pewno chcesz usunąć tę notatkę?',
      confirmLabel: 'Usuń notatkę',
    }))) return;
    setSavingNote(true);
    try {
      await customerNotesApi.remove(noteId);
      await loadCustomerNotes();
      const refreshedOrder = await orderApi.getOne(order.id);
      onOrderUpdated(refreshedOrder.data);
    } catch (err) {
      console.error('Failed to delete note:', err);
      showError(err, 'save');
    } finally {
      setSavingNote(false);
    }
  };

  // Create similar order (pre-fill form)
  const handleCreateSimilarOrder = () => {
    if (onCreateSimilarOrder) {
      onCreateSimilarOrder(order);
      onClose();
    }
  };

  // Delete order (move to trash)
  const handleDeleteOrder = async () => {
    if (!(await confirmDialog({
      title: 'Przenieś do kosza',
      message: 'Czy na pewno chcesz przenieść to zamówienie do kosza?',
      subtitle: 'Zamówienie będzie można przywrócić z Kosza.',
      confirmLabel: 'Przenieś do kosza',
    }))) return;
    try {
      const { data } = await orderApi.update(order.id, { status: 'TRASH' });
      // onOrderUpdated will handle closing the modal and removing from list
      onOrderUpdated(data);
    } catch (err) {
      console.error('Failed to delete order:', err);
      showError(err, 'delete');
    }
  };

  // Helper function to find status in tree
  const findStatusInTree = (statusList: StatusGroup[], statusName: string): StatusGroup | null => {
    for (const s of statusList) {
      if (s.name === statusName) return s;
      if (s.children?.length) {
        const found = findStatusInTree(s.children, statusName);
        if (found) return found;
      }
    }
    return null;
  };

  // Product editing state
  const [editingProductIndex, setEditingProductIndex] = useState<number | null>(null);
  const [editedProduct, setEditedProduct] = useState<{
    name: string;
    quantity: number;
    price: string;
    total: string;
    totalTax: string;
    sku: string;
  }>({ name: '', quantity: 1, price: '0', total: '0', totalTax: '0', sku: '' });

  // Shipping cost editing state (for editing shipping price, not address)
  const [isEditingShippingCost, setIsEditingShippingCost] = useState(false);
  const [editedShippingCost, setEditedShippingCost] = useState({ total: '0', tax: '0' });

  // Get shipping cost - first from edited totals, then from rawData
  const getShippingCost = () => {
    if (order.totals?.shippingTotal) {
      return {
        total: parseFloat(order.totals.shippingTotal) || 0,
        tax: parseFloat(order.totals.shippingTax || '0') || 0,
      };
    }
    if (order.rawData?.shipping_lines?.[0]) {
      return {
        total: parseFloat(order.rawData.shipping_lines[0].total) || 0,
        tax: parseFloat(order.rawData.shipping_lines[0].total_tax || '0') || 0,
      };
    }
    if (order.rawData?.shipping_total) {
      return {
        total: parseFloat(order.rawData.shipping_total) || 0,
        tax: parseFloat(order.rawData.shipping_tax || '0') || 0,
      };
    }
    return { total: 0, tax: 0 };
  };

  const handleEditShippingCost = () => {
    const shipping = getShippingCost();
    setEditedShippingCost({
      total: shipping.total.toFixed(2),
      tax: shipping.tax.toFixed(2),
    });
    setIsEditingShippingCost(true);
  };

  const handleSaveShippingCost = async () => {
    try {
      const { data } = await orderApi.updateShipping(order.id, {
        shippingTotal: editedShippingCost.total,
        shippingTax: editedShippingCost.tax,
      });
      onOrderUpdated(data);
      setIsEditingShippingCost(false);
    } catch (err) {
      console.error('Failed to update shipping cost:', err);
    }
  };

  const handleCancelShippingCostEdit = () => {
    setIsEditingShippingCost(false);
  };

  // Product actions handlers - now handles VAT properly
  const handleEditProduct = (index: number, item: {
    name: string;
    quantity: number;
    price?: string | number;
    total: string;
    total_tax?: string;
    sku?: string;
  }) => {
    setEditingProductIndex(index);
    // Calculate unit price from total if not provided
    const qty = item.quantity || 1;
    const totalNet = parseFloat(String(item.total).replace(/[^\d.,]/g, '').replace(',', '.') || '0');
    const unitPrice = item.price
      ? parseFloat(String(item.price).replace(/[^\d.,]/g, '').replace(',', '.'))
      : totalNet / qty;

    setEditedProduct({
      name: item.name,
      quantity: qty,
      price: unitPrice.toFixed(2),
      total: totalNet.toFixed(2),
      totalTax: item.total_tax || '0',
      sku: item.sku || '',
    });
  };

  const handleSaveProduct = async () => {
    if (editingProductIndex === null) return;
    try {
      const itemData = {
        name: editedProduct.name.trim(),
        quantity: Number(editedProduct.quantity) || 1,
        price: editedProduct.price || '0',
        total: editedProduct.total || '0',
        total_tax: editedProduct.totalTax || '0',
        sku: editedProduct.sku || '',
      };
      console.log('[Product Edit] Sending:', itemData);
      const { data } = await orderApi.updateItem(order.id, editingProductIndex, itemData);
      onOrderUpdated(data);
      setEditingProductIndex(null);
      // Refresh history
      const historyRes = await orderApi.getHistory(order.id);
      setHistory(historyRes.data);
    } catch (err: unknown) {
      console.error('Failed to update product:', err);
      showError(err, 'product');
    }
  };

  const handleCancelProductEdit = () => {
    setEditingProductIndex(null);
  };

  const handleDuplicateProduct = async (index: number, productName: string) => {
    if (!(await confirmDialog({
      title: 'Zduplikuj produkt',
      message: <>Czy na pewno chcesz zduplikować produkt <strong>{productName}</strong>?</>,
      subtitle: 'Powstanie nowa pozycja zamówienia z tą samą ilością.',
      confirmLabel: 'Zduplikuj',
      danger: false,
    }))) return;
    try {
      const { data } = await orderApi.duplicateItem(order.id, index);
      onOrderUpdated(data);
      // Refresh history
      const historyRes = await orderApi.getHistory(order.id);
      setHistory(historyRes.data);
    } catch (err) {
      console.error('Failed to duplicate product:', err);
      showError(err, 'product');
    }
  };

  const handleDeleteProduct = async (index: number, productName: string) => {
    if (!(await confirmDialog({
      title: 'Usuń produkt',
      message: <>Czy na pewno chcesz usunąć produkt <strong>{productName}</strong> z zamówienia?</>,
      confirmLabel: 'Usuń produkt',
    }))) return;
    try {
      const { data } = await orderApi.deleteItem(order.id, index);
      onOrderUpdated(data);
      // Refresh history
      const historyRes = await orderApi.getHistory(order.id);
      setHistory(historyRes.data);
    } catch (err) {
      console.error('Failed to delete product:', err);
      showError(err, 'product');
    }
  };

  // Process shortcodes in label description template
  const processShortcodes = (template: string): string => {
    if (!template) return '';

    const items = (order.items || []) as Array<{ name?: string; sku?: string; quantity?: number }>;
    const rawLineItems = ((order.rawData as Record<string, unknown>)?.line_items || []) as Array<{ name?: string; sku?: string; quantity?: number }>;
    const allItems = items.length > 0 ? items : rawLineItems;

    let result = template;

    // {order_id} - order number
    result = result.replace(/\{order_id\}/g, order.orderNumber || '');

    // {order_date} - formatted date
    const orderDate = order.createdAt ? new Date(order.createdAt).toISOString().split('T')[0] : '';
    result = result.replace(/\{order_date\}/g, orderDate);

    // {customer_name}
    result = result.replace(/\{customer_name\}/g, order.customerName || '');

    // {products} - list of products
    const productsList = allItems.map(item => {
      const qty = item.quantity || 1;
      return qty > 1 ? `${item.name} x${qty}` : item.name;
    }).filter(Boolean).join(', ');
    result = result.replace(/\{products\}/g, productsList);

    // {products_sku}
    const skuList = allItems.map(item => item.sku).filter(Boolean).join(', ');
    result = result.replace(/\{products_sku\}/g, skuList);

    // {products_count}
    const totalCount = allItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
    result = result.replace(/\{products_count\}/g, String(totalCount));

    // {products_sku_qty}
    const skuQtyList = allItems
      .filter(item => item.sku)
      .map(item => `${item.sku} - ${item.quantity || 1}`)
      .join(', ');
    result = result.replace(/\{products_sku_qty\}/g, skuQtyList);

    // {total}
    const totalFormatted = `${(order.totalAmount || 0).toFixed(2)} ${order.currency || 'PLN'}`;
    result = result.replace(/\{total\}/g, totalFormatted);

    // {payment_method}
    result = result.replace(/\{payment_method\}/g, order.paymentMethod || 'Nieznana');

    // {store_name}
    const storeName = order.store?.name || '';
    result = result.replace(/\{store_name\}/g, storeName);

    return result;
  };

  // Open shipment config panel with pre-filled values
  const openShipmentConfig = async (service: 'inpost_locker_standard' | 'inpost_courier_standard') => {
    // CRITICAL: Set service FIRST so the config panel always opens,
    // even if COD computation or settings loading fails
    setSelectedShipmentService(service);
    setShipmentError(null);

    // Safely compute COD amount - wrapped in try-catch to never block panel opening
    let autoCodAmount = '';
    try {
      const pmLower = (order.paymentMethod || '').toLowerCase();
      const raw = ((order as unknown as { rawData?: Record<string, unknown> }).rawData || {}) as Record<string, unknown>;
      const rawPm = typeof raw.payment_method === 'string' ? raw.payment_method.toLowerCase() : '';
      const rawPmTitle = typeof raw.payment_method_title === 'string' ? raw.payment_method_title.toLowerCase() : '';

      const orderIsCOD = rawPm === 'cod' ||
        [pmLower, rawPm, rawPmTitle].some(text =>
          text.includes('pobranie') ||
          text.includes('pobraniem') ||
          text.includes('przy odbiorze') ||
          text.includes('przy dostawie') ||
          text.includes('płatne kurierowi') ||
          text.includes('płatne u kuriera') ||
          text.includes('gotówka') ||
          text.includes('cash on delivery') ||
          text.includes('płatność przy odbiorze') ||
          text.includes('zapłata przy odbiorze') ||
          text.includes('płacę przy odbiorze') ||
          text.includes('płatne gotówką') ||
          text.includes('płatność gotówką') ||
          /(?:^|[^a-z])cod(?:$|[^a-z])/.test(text)
        );

      // Lockers don't support COD - only courier does
      if (orderIsCOD && service !== 'inpost_locker_standard') {
        autoCodAmount = (order.totalAmount || 0).toFixed(2);
      }
    } catch (e) {
      console.error('COD detection failed:', e);
    }

    // Pre-fill COD amount synchronously
    setShipmentConfig(prev => ({
      ...prev,
      codAmount: autoCodAmount,
    }));

    // Load InPost settings asynchronously (parcel size, insurance, comments)
    try {
      const { data: inpostData } = await courierApi.getInPost();
      const settings = (inpostData.settings || {}) as unknown as Record<string, unknown>;

      let defaultSize: 'small' | 'medium' | 'large' = 'small';
      if (service === 'inpost_locker_standard') {
        const gabaryt = settings.lockerGabaryt as string;
        if (gabaryt === 'A') defaultSize = 'small';
        else if (gabaryt === 'B') defaultSize = 'medium';
        else if (gabaryt === 'C') defaultSize = 'large';
      } else {
        const preset = settings.courierDimensionPreset as string;
        if (preset === 'small') defaultSize = 'small';
        else if (preset === 'medium') defaultSize = 'medium';
        else if (preset === 'large') defaultSize = 'large';
      }

      const labelTemplate = (settings.labelDescription as string) || '';
      const processedComments = processShortcodes(labelTemplate);

      setShipmentConfig({
        parcelSize: defaultSize,
        codAmount: autoCodAmount,
        insurance: settings.insuranceEnabled ? String(settings.fixedInsuranceAmount || '') : '',
        comments: processedComments,
        commentsModified: false,
      });
    } catch (err) {
      console.error('Failed to load InPost settings:', err);
      setShipmentConfig({
        parcelSize: 'small',
        codAmount: autoCodAmount,
        insurance: '',
        comments: order.orderNumber || '',
        commentsModified: false,
      });
    }
  };

  // Cancel shipment config
  const cancelShipmentConfig = () => {
    setSelectedShipmentService(null);
    setShipmentError(null);
  };

  // Portal (Panel klienta) state
  const [portalLink, setPortalLink] = useState<string | null>(null);
  const [portalComplaints, setPortalComplaints] = useState<AdminComplaint[]>([]);

  // Auto-generate/get portal token on load
  useEffect(() => {
    if (!order.customerEmail) return;
    portalApi.generateToken(order.id)
      .then(({ data }) => {
        setPortalLink(data.link);
      })
      .catch(() => {});
  }, [order.id, order.customerEmail]);

  // Load portal complaints
  useEffect(() => {
    portalApi.getComplaints(order.id)
      .then(({ data }) => setPortalComplaints(data))
      .catch(() => {});
  }, [order.id]);

  const handleOpenRefundForm = async () => {
    setShowRefundForm(true);
    setRefundError(null);
    setRefundAmount('');
    // Pre-fill description with auto-generated one from cached info
    if (refundInfo?.productName && refundInfo?.storeName) {
      const desc = `Zwrot za ${refundInfo.productName.substring(0, 15)} - ${refundInfo.storeName}`.substring(0, 35);
      setRefundDescription(desc);
    } else {
      setRefundDescription('');
    }
    // Refresh refund info to get latest data
    try {
      setLoadingRefundInfo(true);
      const { data: info } = await refundApi.getInfo(order.id);
      setRefundInfo(info);
      // Update description if we didn't have cached info
      if (!refundInfo && info.productName && info.storeName) {
        const desc = `Zwrot za ${info.productName.substring(0, 15)} - ${info.storeName}`.substring(0, 35);
        setRefundDescription(desc);
      }
    } catch (err) {
      console.error('Failed to load refund info:', err);
      setRefundError('Nie udało się załadować informacji o zwrocie');
    } finally {
      setLoadingRefundInfo(false);
    }
  };

  const handleCloseRefundForm = () => {
    setShowRefundForm(false);
    setRefundError(null);
    setRefundAmount('');
    setRefundDescription('');
  };

  // Chargeback oznaczany recznie (bramki nie maja API dispute); kalkulator
  // finansow doliczy wowczas oplate chargeback z pasujacej reguly prowizji.
  const handleToggleChargeback = async (refundId: string, isChargeback: boolean) => {
    try {
      await refundApi.markChargeback(order.id, refundId, isChargeback);
      setRefundInfo((prev) => prev
        ? { ...prev, refunds: prev.refunds.map((r) => (r.id === refundId ? { ...r, isChargeback } : r)) }
        : prev);
    } catch (err) {
      console.error('Failed to update chargeback flag:', err);
    }
  };

  // Deep-link ?action=refund z Messages karty klienta → po załadowaniu
  // modala auto-otwórz formularz zwrotu. useRef żeby odpaliło się tylko raz
  // przy pierwszym mount (nie przy każdej zmianie order prop).
  const autoOpenRefundHandled = useRef(false);
  useEffect(() => {
    if (autoOpenRefund && !autoOpenRefundHandled.current) {
      autoOpenRefundHandled.current = true;
      handleOpenRefundForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenRefund]);

  const handleRefundFullAmount = () => {
    if (refundInfo) {
      setRefundAmount(refundInfo.availableForRefund.toFixed(2));
    }
  };

  const handleProcessRefund = async () => {
    if (!refundInfo || processingRefund) return;
    const amount = parseFloat(refundAmount);
    if (isNaN(amount) || amount <= 0) {
      setRefundError('Podaj poprawną kwotę zwrotu');
      return;
    }
    if (amount > refundInfo.availableForRefund) {
      setRefundError(`Kwota nie może przekraczać ${refundInfo.availableForRefund.toFixed(2)} ${refundInfo.currency}`);
      return;
    }
    setProcessingRefund(true);
    setRefundError(null);
    try {
      const { data: result } = await refundApi.create(order.id, {
        amount,
        description: refundDescription || undefined,
      });
      showSuccess('refund', { message: result.message });
      // Refresh order and refund info
      const updatedOrder = await orderApi.getOne(order.id);
      onOrderUpdated(updatedOrder.data);
      // Refresh refund info to update the list
      const { data: updatedInfo } = await refundApi.getInfo(order.id);
      setRefundInfo(updatedInfo);
      // Reset form but keep it open
      setRefundAmount('');
      setRefundDescription('');
      // Close form if no more funds available
      if (updatedInfo.availableForRefund <= 0) {
        setShowRefundForm(false);
      }
    } catch (err: unknown) {
      console.error('Refund failed:', err);
      // Extract error message - check axios response first, then Error.message
      const axiosError = err as { response?: { data?: { error?: string } } };
      const errorMessage = axiosError?.response?.data?.error ||
        (err instanceof Error ? err.message : null) ||
        'Błąd podczas przetwarzania zwrotu';
      setRefundError(errorMessage);
      // Also refresh refund info to show the failed refund in history
      try {
        const { data: updatedInfo } = await refundApi.getInfo(order.id);
        setRefundInfo(updatedInfo);
      } catch {
        // Ignore - main error is already shown
      }
    } finally {
      setProcessingRefund(false);
    }
  };

  // Shipment creation handler (called when user clicks "Nadaj etykietę")
  const handleCreateShipment = async (skipPreValidation = false) => {
    if (creatingShipment || !selectedShipmentService) return;
    setCreatingShipment(true);
    setShipmentError(null);
    try {
      // Use manually edited paczkomat code (editedPaczkomat) if set, otherwise use auto-detected code
      const targetPointToUse = editedPaczkomat || paczkomatNumber || undefined;
      const { data } = await shipmentApi.create({
        orderId: order.id,
        service: selectedShipmentService,
        // Pass paczkomat code - prefer user-edited value over auto-detected
        targetPoint: targetPointToUse,
        // Pass configuration options
        parcelSize: shipmentConfig.parcelSize,
        codAmount: shipmentConfig.codAmount ? Number(shipmentConfig.codAmount) : undefined,
        insurance: shipmentConfig.insurance ? Number(shipmentConfig.insurance) : undefined,
        // Only send reference if user manually modified it, otherwise let backend use its template
        reference: shipmentConfig.commentsModified ? (shipmentConfig.comments || undefined) : undefined,
        // Skip pre-validation if user chose "Nadaj mimo to"
        skipPreValidation: skipPreValidation || undefined,
      });
      // Refresh order to show new shipment
      const updatedOrder = await orderApi.getOne(order.id);
      onOrderUpdated(updatedOrder.data);
      const trackingInfo = data.shipment.trackingNumber
        ? `Numer listu: ${data.shipment.trackingNumber}`
        : undefined;
      showSuccess('shipment', { details: trackingInfo });

      // Close the config panel on success
      setSelectedShipmentService(null);

      // Delayed re-fetch to catch automation status changes (automations run asynchronously)
      setTimeout(async () => {
        try {
          console.log('[Shipment Refresh] Fetching order after delay, current status:', order.status);
          const refreshedOrder = await orderApi.getOne(order.id);
          console.log('[Shipment Refresh] New status from server:', refreshedOrder.data.status);
          onOrderUpdated(refreshedOrder.data);
          // Also refresh statuses in sidebar
          onStatusRefresh?.();
        } catch (e) {
          console.error('[Shipment Refresh] Error:', e);
        }
      }, 1500);
    } catch (err: unknown) {
      const translated = translateError(err, 'shipment');
      const message = translated.details
        ? `${translated.message}\n${translated.details}`
        : translated.message;
      setShipmentError(message);
      console.error('Failed to create shipment:', err);
    } finally {
      setCreatingShipment(false);
    }
  };

  // Invoice creation handler
  const handleCreateInvoice = async (type: 'VAT' | 'RECEIPT' = 'VAT') => {
    if (creatingInvoice) return;
    setCreatingInvoice(true);
    setInvoiceError(null);
    try {
      const { data } = await invoiceApi.create({
        orderId: order.id,
      });
      // Refresh order to show new invoice
      const updatedOrder = await orderApi.getOne(order.id);
      onOrderUpdated(updatedOrder.data);
      showSuccess('invoice', {
        message: `${type === 'VAT' ? 'Faktura' : 'Paragon'} wystawiona!`,
        details: `Numer: ${data.invoice.invoiceNumber || 'Brak numeru'}`
      });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } }; message?: string };
      const message = error.response?.data?.error || error.message || 'Nie udało się wystawić faktury';
      setInvoiceError(message);
      console.error('Failed to create invoice:', err);
    } finally {
      setCreatingInvoice(false);
    }
  };

  // Download invoice PDF
  const handleDownloadInvoice = async (invoiceId: string, invoiceNumber: string) => {
    setDownloadingInvoice(invoiceId);
    try {
      const { data } = await invoiceApi.getPdf(invoiceId);
      const binaryData = Uint8Array.from(atob(data.data), c => c.charCodeAt(0));
      const blob = new Blob([binaryData], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber || 'faktura'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download invoice:', err);
      showError(err, 'invoice');
    } finally {
      setDownloadingInvoice(null);
    }
  };

  // Mark invoice as paid
  const handleMarkInvoiceAsPaid = async (invoiceId: string) => {
    if (!(await confirmDialog({
      title: 'Oznacz fakturę jako opłaconą',
      message: 'Czy na pewno chcesz oznaczyć fakturę jako opłaconą?',
      subtitle: 'Zmieni się status w Fakturowni.',
      confirmLabel: 'Oznacz jako opłaconą',
      danger: false,
    }))) return;
    try {
      await invoiceApi.markAsPaid(invoiceId);
      // Refresh order to show updated status
      const updatedOrder = await orderApi.getOne(order.id);
      onOrderUpdated(updatedOrder.data);
      showSuccess('paid');
    } catch (err) {
      console.error('Failed to mark invoice as paid:', err);
      showError(err, 'invoice');
    }
  };

  // Create correction invoice
  const handleCreateCorrectionInvoice = async (originalInvoiceId: string) => {
    if (!(await confirmDialog({
      title: 'Wystaw korektę',
      message: 'Czy na pewno chcesz wystawić korektę do tej faktury?',
      description: 'Korekta wyzeruje wszystkie pozycje.',
      confirmLabel: 'Wystaw korektę',
    }))) return;
    setCreatingCorrectionInvoice(originalInvoiceId);
    setInvoiceError(null);
    try {
      await invoiceApi.createCorrection(originalInvoiceId);
      // Refresh order to show new correction invoice
      const updatedOrder = await orderApi.getOne(order.id);
      onOrderUpdated(updatedOrder.data);
      showSuccess('correction');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      const message = error.response?.data?.error || 'Nie udało się wystawić korekty';
      console.error('Failed to create correction invoice:', err);
      setInvoiceError(message);
    } finally {
      setCreatingCorrectionInvoice(null);
    }
  };

  // Delete invoice
  const handleDeleteInvoice = async (invoiceId: string, invoiceNumber: string) => {
    if (!(await confirmDialog({
      title: 'Usuń fakturę',
      message: <>Czy na pewno chcesz usunąć fakturę <strong>{invoiceNumber}</strong>?</>,
      description: 'Ta operacja może być nieodwracalna (jeśli faktura została zarejestrowana w KSeF, usunięcie w Fakturowni nie wycofa jej z KSeF).',
      confirmLabel: 'Usuń fakturę',
    }))) return;
    try {
      await invoiceApi.delete(invoiceId);
      // Refresh order to remove invoice
      const updatedOrder = await orderApi.getOne(order.id);
      onOrderUpdated(updatedOrder.data);
      showSuccess('delete', { message: 'Faktura usunięta.' });
    } catch (err: unknown) {
      console.error('Failed to delete invoice:', err);
      showError(err, 'invoice');
    }
  };

  // Execute manual automation
  const handleExecuteManualAutomation = async (automationId: string, actionName: string) => {
    setExecutingAutomation(automationId);
    setAutomationError(null);
    setAutomationSuccess(null);
    try {
      const { data } = await automationApi.executeManualAutomation(automationId, order.id);
      if (data.success) {
        setAutomationSuccess(`"${actionName}" wykonana pomyślnie`);
        // Refresh order, history and emails to show any changes (shipments, invoices, status, emails, etc.)
        const [updatedOrder, historyRes, emailsRes] = await Promise.all([
          orderApi.getOne(order.id),
          orderApi.getHistory(order.id),
          emailApi.getOrderEmails(order.id),
        ]);
        onOrderUpdated(updatedOrder.data);
        setHistory(historyRes.data);
        setOrderEmails(emailsRes.data.logs);
        // Clear success message after 5 seconds
        setTimeout(() => setAutomationSuccess(null), 5000);
      } else {
        setAutomationError(data.message || 'Akcja pominięta');
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string; message?: string } } };
      const message = error.response?.data?.error || error.response?.data?.message || 'Nie udało się wykonać akcji';
      console.error('Failed to execute automation:', err);
      setAutomationError(message);
    } finally {
      setExecutingAutomation(null);
    }
  };

  // Email handlers

  const handleSendCustomEmail = async () => {
    if (!order.customerEmail) return;
    const subject = customEmailSubject.trim();
    const body = customEmailBody.trim();
    if (!subject || !body.replace(/<[^>]+>/g, '').trim()) {
      setEmailError('Podaj temat i treść wiadomości.');
      return;
    }

    setSendingEmail(true);
    setEmailError(null);

    try {
      const bodyHtml = DOMPurify.sanitize(body);
      const result = await emailApi.sendCustom({
        orderId: order.id,
        toEmail: order.customerEmail,
        subject,
        bodyHtml,
      });

      if (result.data.success) {
        const dbg = result.data.debug;
        // Jeśli IMAP append padł, backend to zgłasza w debug.sentFolder.error.
        // Nie blokujemy wysyłki (SMTP już poszedł), ale ostrzegamy usera żeby
        // wiedział czemu nie zobaczy maila w swojej skrzynce Wysłane.
        if (dbg?.sentFolder && !dbg.sentFolder.appended) {
          setEmailError(
            `Mail wysłany przez SMTP (${dbg.smtpHost ?? '?'} jako ${dbg.fromEmail ?? '?'}), ale NIE zapisał się w "Wysłane" na IMAP: ${dbg.sentFolder.error ?? 'nieznany błąd'}. Sprawdź czy odbiorca go otrzymał.`,
          );
          // Zostaw modal otwarty — user widzi komunikat i może zdecydować
          return;
        }
        setShowEmailModal(false);
        setCustomEmailSubject('');
        setCustomEmailBody('');
        const [emailsRes, historyRes] = await Promise.all([
          emailApi.getOrderEmails(order.id),
          orderApi.getHistory(order.id),
        ]);
        setOrderEmails(emailsRes.data.logs);
        setHistory(historyRes.data);
        showSuccess('email');
      } else {
        setEmailError(result.data.error || 'Nie udało się wysłać emaila');
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setEmailError(error.response?.data?.error || 'Nie udało się wysłać emaila');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleResendEmail = async (logId: string) => {
    try {
      await emailApi.resend(logId);
      // Refresh emails list and history
      const [emailsRes, historyRes] = await Promise.all([
        emailApi.getOrderEmails(order.id),
        orderApi.getHistory(order.id),
      ]);
      setOrderEmails(emailsRes.data.logs);
      setHistory(historyRes.data);
      showSuccess('email', { message: 'Email wysłany ponownie.' });
    } catch (err) {
      console.error('Failed to resend email:', err);
      showError(err, 'email');
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: currency || 'PLN',
    }).format(amount);
  };

  // Ujednolicona jednostka waluty (PLN → "zł") — convention 2026-04-21.
  // Dla innych walut zwraca kod ISO (EUR, USD), zachowujemy obsługę
  // wielowalutową dla sklepów EU.
  const currencyUnit = (order.currency || 'PLN') === 'PLN' ? 'zł' : order.currency;

  const shipping = order.shippingAddress as Record<string, string>;
  const billing = order.billingAddress as Record<string, string>;
  const items = order.items as Array<{
    name: string;
    quantity: number;
    total: string;
    total_tax?: string;
    price?: string | number;
    sku?: string;
    weight?: number;
    tax?: string;
  }>;

  // Extract paczkomat/pickup point number from WooCommerce order
  // The full WooCommerce data is stored in rawData field
  // Supports multiple plugins: Furgonetka, Apaczka, InPost WooCommerce, Flexible Shipping, etc.
  const rawData = (order as unknown as { rawData?: Record<string, unknown> }).rawData || {};
  const rawMetaData = (rawData.meta_data as Array<{ key: string; value: unknown }>) || [];
  const nativePickupPoints = getNativePickupPoints(rawData);
  const orderMeta = (order as unknown as { meta_data?: Array<{ key: string; value: string }> }).meta_data || [];
  const metaData = (order as unknown as { metadata?: Record<string, string> }).metadata || {};
  const orderRaw = order as unknown as Record<string, unknown>;

  // InPost point patterns - matching backend patterns
  // IMPORTANT: Paczkomat codes MUST contain at least one digit!
  // Valid examples: KRA01A, WAW123M, GDA02B, POZ15C, RBR01BAPP, OSS03BAPP
  // Some codes have APP suffix (from InPost mobile app)
  // Invalid examples: "Mieszkanie", "Apartament" (no digits)
  const PACZKOMAT_PATTERN = /^[A-Z]{2,4}\d{1,3}[A-Z0-9]{0,5}$/i;
  const POP_PATTERN = /^POP-[A-Z0-9]{2,10}$/i;
  const isValidPointCode = (value: string): boolean => {
    // Must be reasonable length (3-12 chars) and match pattern
    if (value.length < 3 || value.length > 12) return false;
    return PACZKOMAT_PATTERN.test(value) || POP_PATTERN.test(value);
  };

  // Helper to find value in meta_data array by partial key match
  const findInMeta = (metaArray: Array<{ key: string; value: unknown }>, keywords: string[]): string | null => {
    for (const meta of metaArray) {
      const keyLower = meta.key.toLowerCase();
      if (keywords.some(kw => keyLower.includes(kw)) && meta.value) {
        const value = meta.value;
        // If value looks like a locker ID (e.g., KRA01A, WAW123M, POP-CBI2) return it
        if (typeof value === 'string' && isValidPointCode(value)) {
          return value;
        }
        // Try parsing JSON if value is an object string
        try {
          const parsed = typeof value === 'string' && value.startsWith('{')
            ? JSON.parse(value)
            : value;
          if (typeof parsed === 'object' && parsed !== null) {
            // Look for common field names in the object
            const obj = parsed as Record<string, string>;
            // IMPORTANT: Validate each candidate against point code pattern
            const candidates = [obj.name, obj.id, obj.point_id, obj.locker_id, obj.machine_id, obj.machine];
            for (const candidate of candidates) {
              if (candidate && isValidPointCode(candidate)) {
                return candidate;
              }
            }
          }
        } catch {
          // Not JSON - only return if it's a valid point code
          if (typeof value === 'string' && isValidPointCode(value)) {
            return value;
          }
        }
      }
    }
    return null;
  };

  // Helper to search in any object for locker-related fields
  const findInObject = (obj: Record<string, unknown>, depth = 0): string | null => {
    if (depth > 3 || !obj || typeof obj !== 'object') return null;

    const keywords = ['paczkomat', 'locker', 'point', 'inpost', 'machine', 'pickup'];
    for (const [key, value] of Object.entries(obj)) {
      const keyLower = key.toLowerCase();
      if (keywords.some(kw => keyLower.includes(kw))) {
        // ONLY return values that match valid point code patterns
        if (typeof value === 'string' && isValidPointCode(value)) {
          return value;
        }
        // Search nested objects
        if (typeof value === 'object' && value !== null) {
          const nested = findInObject(value as Record<string, unknown>, depth + 1);
          if (nested) return nested;
        }
      }
    }
    return null;
  };

  // Extract paczkomat code from meta_data ONLY - NOT from address_2!
  // WooCommerce InPost plugins store paczkomat code in address_2 which causes it to
  // appear as part of the delivery address. We should only use meta_data sources.
  const paczkomatNumber =
    // HIGHEST PRIORITY: Check our custom paczkomat_code field (explicitly saved from order edit)
    (shipping?.paczkomat_code && isValidPointCode(shipping.paczkomat_code) ? shipping.paczkomat_code : null) ||
    (nativePickupPoints.length ? (new Set(nativePickupPoints.map(point => point.code)).size === 1 ? nativePickupPoints[0].code : null) : (
    // Then check rawData.meta_data (full WooCommerce data)
    // Furgonetka plugin
    findInMeta(rawMetaData, ['furgonetka_point', 'furgonetka_locker', 'furgonetka_machine', 'furgonetka']) ||
    // Apaczka plugin
    findInMeta(rawMetaData, ['apaczka_point', 'apaczka_locker', 'apaczka']) ||
    // InPost WooCommerce plugin
    findInMeta(rawMetaData, ['inpost_locker', 'inpost_point', 'inpost_machine', '_point_id', 'inpost']) ||
    // Flexible Shipping plugin
    findInMeta(rawMetaData, ['fs_inpost', 'fs_paczkomat', 'flexible_shipping']) ||
    // Generic paczkomat/locker fields
    findInMeta(rawMetaData, ['paczkomat', 'locker_id', 'pickup_point', 'parcel_locker', 'machine_id', 'locker', 'point']) ||
    // Also check order-level meta_data (if available differently)
    findInMeta(orderMeta as Array<{ key: string; value: unknown }>, ['paczkomat', 'locker', 'inpost', 'furgonetka', 'apaczka', 'point']) ||
    // Check order-level metadata object
    findInObject(metaData) ||
    // Check other shipping address fields (but NOT address_2!)
    (shipping?.paczkomat && isValidPointCode(shipping.paczkomat) ? shipping.paczkomat : null) ||
    (shipping?.locker_id && isValidPointCode(shipping.locker_id) ? shipping.locker_id : null) ||
    (shipping?.pickup_point && isValidPointCode(shipping.pickup_point) ? shipping.pickup_point : null) ||
    (shipping?.inpost_locker && isValidPointCode(shipping.inpost_locker) ? shipping.inpost_locker : null) ||
    (shipping?.machine_id && isValidPointCode(shipping.machine_id) ? shipping.machine_id : null) ||
    // NOTE: We explicitly DO NOT check address_2 - it causes issues with courier orders
    // Check direct order properties and rawData
    findInObject(rawData) ||
    findInObject(orderRaw) ||
    null));

  // Determine if this is a paczkomat/locker shipment based on shipping method name
  // Note: the backend resolver handles accurate mapping-based detection for shipment creation;
  // this is just for UI display hints (icon/label)
  const isPaczkomatShipment = (() => {
    const shippingMethodLower = (order.shippingMethod || '').toLowerCase();
    return shippingMethodLower.includes('paczkomat') ||
      shippingMethodLower.includes('locker') ||
      shippingMethodLower.includes('automat');
  })();

  // Clean address_1 from "ul./ulica" prefix for Google Maps search
  const cleanedAddressForMaps = (() => {
    const addr = shipping?.address_1 || '';
    if (!addr) return '';

    const lowerAddr = addr.toLowerCase();
    const prefixes = ['ul. ', 'ul.', 'ulica ', 'ulica'];
    for (const prefix of prefixes) {
      if (lowerAddr.startsWith(prefix)) {
        return addr.substring(prefix.length).trim();
      }
    }
    return addr;
  })();

  // Initialize paczkomat from detected value - sync when order changes or paczkomat data updates
  useEffect(() => {
    setEditedPaczkomat(paczkomatNumber || '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id, paczkomatNumber]);

  // Keyboard navigation - Ctrl/Cmd + Arrow to navigate between orders
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
      const isCtrlOrCmd = e.ctrlKey || e.metaKey;
      if (!isCtrlOrCmd) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

      // Always swallow CMD/Ctrl + Arrow on the order card — even when we have no
      // neighbour to navigate to. Without preventDefault the browser treats it
      // as "back/forward tab" and the whole page navigates away (regression user
      // reported 2026-04-22).
      e.preventDefault();

      if (e.key === 'ArrowLeft' && onPrevious) {
        onPrevious();
      } else if (e.key === 'ArrowRight' && onNext) {
        onNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onPrevious, onNext]);

  // Check actual payment status from WooCommerce order data
  const wooStatus = (rawData.status as string) || (order as unknown as { externalStatus?: string }).externalStatus || '';
  const datePaid = rawData.date_paid as string | null;
  const paymentMethodLower = (order.paymentMethod || '').toLowerCase();

  // Check if this is a Cash on Delivery (COD) order
  // Keywords matching backend's inpost.service.ts COD detection
  const isCOD = paymentMethodLower.includes('pobranie') ||
    paymentMethodLower.includes('pobraniem') ||
    paymentMethodLower.includes('przy odbiorze') ||
    paymentMethodLower.includes('przy dostawie') ||
    paymentMethodLower.includes('płatne kurierowi') ||
    paymentMethodLower.includes('płatne u kuriera') ||
    paymentMethodLower.includes('gotówka') ||
    paymentMethodLower.includes('cash on delivery') ||
    /(?:^|[^a-z])cod(?:$|[^a-z])/.test(paymentMethodLower);

  // Order is paid if:
  // 1. NOT a COD order AND has date_paid set (not null/empty)
  // 2. Or NOT a COD order AND status is processing/completed
  // COD orders are NEVER considered automatically paid — they must be marked manually
  const isPaid = !isCOD && (
    Boolean(datePaid && datePaid !== '0000-00-00T00:00:00') ||
    ['processing', 'completed'].includes(wooStatus.toLowerCase())
  );

  const handleStatusChange = async (statusId: string) => {
    if (updatingStatus) return;
    try {
      setUpdatingStatus(true);
      // Use statusId to ensure correct status assignment even for duplicate names across stores
      const { data } = await orderApi.update(order.id, statusId ? { statusId } : { status: '' });
      onOrderUpdated(data);
      setStatusDropdownOpen(false);
      setStatusSearch('');
      // Refresh history after status change
      const historyRes = await orderApi.getHistory(order.id);
      setHistory(historyRes.data);
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Flatten statuses for dropdown, filtering by order's store
  // Only show statuses that are global (storeId=null) or linked to this order's store
  const getAllStatuses = (): { id: string; name: string; color: string; level: number; isTopLevel: boolean }[] => {
    const result: { id: string; name: string; color: string; level: number; isTopLevel: boolean }[] = [];
    const orderStoreId = order.store?.id;

    // Filter top-level statuses by store
    const filteredStatuses = statuses.filter(s => !s.storeId || s.storeId === orderStoreId);

    const addStatus = (status: StatusGroup, level: number, isTopLevel: boolean) => {
      // For children, also check store filter
      if (level > 0 && status.storeId && status.storeId !== orderStoreId) return;

      result.push({ id: status.id, name: status.name, color: status.color, level, isTopLevel });
      if (status.children && status.children.length > 0) {
        status.children.forEach(child => addStatus(child, level + 1, false));
      }
    };
    filteredStatuses.forEach((s, idx) => addStatus(s, 0, idx > 0)); // idx > 0 means show separator before (except first)
    return result;
  };

  const refreshComplaint = () => {
    void portalApi.getComplaints(order.id).then(({ data }) => setPortalComplaints(data)).catch(error => showError(error, 'save'));
    void orderApi.getHistory(order.id).then(({ data }) => setHistory(data)).catch(error => showError(error, 'save'));
    onComplaintUpdated?.();
  };

  // Handle ESC key - close shipment config first, then modal
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (document.querySelector('[role="listbox"]')) return;
        if (selectedShipmentService) {
          cancelShipmentConfig();
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose, selectedShipmentService]);

  // Handle click outside dropdown
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) {
        setStatusDropdownOpen(false);
        setStatusSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-[12000] bg-surface-base/95 backdrop-blur-md flex items-start justify-center overflow-hidden p-3 sm:p-4 animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-surface-raised border border-border rounded-2xl w-full max-w-5xl max-h-[calc(100dvh-1.5rem)] sm:max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
        {/* Header and section navigation share one sticky surface. */}
        <div className="sticky top-0 z-20 bg-surface-raised">
        <div className="flex items-center justify-between p-6 border-b border-border-subtle bg-surface-raised">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-accent-primary">
              <Package className="w-6 h-6 text-text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-text-primary flex items-center gap-2">
                Zamówienie #{order.orderNumber}
                {/* Manual order indicator */}
                {order.source === 'MANUAL' && (
                  <span className="text-text-muted text-xs font-normal bg-surface-muted px-2 py-0.5 rounded flex items-center gap-1">
                    <PenLine className="w-3 h-3" />
                    Ręczne
                  </span>
                )}
              </h2>
              <p className="text-text-secondary text-sm">
                {order.store?.name} • {new Date(order.externalCreatedAt).toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw' })}
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            {/* Navigation arrows */}
            {(onPrevious || onNext) && (
              <>
                <div className="flex items-center gap-1">
                  <button
                    onClick={onPrevious}
                    disabled={!onPrevious}
                    className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Poprzednie zamówienie"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  {currentIndex !== undefined && totalOrders !== undefined && (
                    <span className="text-xs text-text-muted mx-1">
                      {currentIndex + 1}/{totalOrders}
                    </span>
                  )}
                  <button
                    onClick={onNext}
                    disabled={!onNext}
                    className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Następne zamówienie"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
                {/* Vertical separator */}
                <div className="w-px h-6 bg-surface-hover/60 mx-1" />
              </>
            )}
            {isEditing ? (
              <>
                <button
                  onClick={handleCancelEdit}
                  className="px-3 py-1.5 rounded-lg text-sm text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all"
                  disabled={savingOrder}
                >
                  Anuluj
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="px-3 py-1.5 rounded-lg text-sm bg-accent-primary text-text-primary hover:bg-accent-primary/80 transition-all flex items-center gap-1"
                  disabled={savingOrder}
                >
                  {savingOrder ? 'Zapisywanie...' : 'Zapisz zmiany'}
                </button>
              </>
            ) : order.status === 'Kosz' ? (
              // Order in trash - show restore button only
              <button
                onClick={async () => {
                  if (!(await confirmDialog({
                    title: 'Przywróć z kosza',
                    message: 'Czy na pewno chcesz przywrócić to zamówienie z kosza?',
                    confirmLabel: 'Przywróć',
                    danger: false,
                  }))) return;
                  try {
                    const { data } = await orderApi.update(order.id, { status: '' });
                    onOrderUpdated(data);
                  } catch (err) {
                    console.error('Failed to restore order:', err);
                    showError(err, 'restore');
                  }
                }}
                className="px-3 py-1.5 rounded-lg text-sm bg-success/20 text-success hover:bg-success/30 transition-all"
              >
                Przywróć z kosza
              </button>
            ) : (
              <>
                {/* N2 (2026-04-22): Blacklist toggle zastępuje globalny Edit3.
                    Edycja konkretnych sekcji (adres, klient, produkty) dostępna
                    z ikon Edit3 w nagłówkach tych sekcji - globalny nie był
                    potrzebny. */}
                <button
                  onClick={handleToggleBlacklist}
                  disabled={togglingBlacklist || isCustomerBlacklisted === null}
                  className={`p-2 rounded-lg transition-all disabled:opacity-40 disabled:cursor-wait ${
                    isCustomerBlacklisted
                      ? 'text-danger bg-danger/10 hover:bg-danger/20'
                      : 'text-text-secondary hover:text-danger hover:bg-surface-hover'
                  }`}
                  title={isCustomerBlacklisted ? 'Klient na czarnej liście - kliknij, by usunąć' : 'Dodaj klienta do czarnej listy'}
                >
                  {togglingBlacklist ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <SkullIcon className="w-5 h-5" />
                  )}
                </button>
                {/* P10 (2026-04-30): Poczekalnia - pomijane przy bulk nadawaniu listów */}
                <button
                  onClick={handleToggleHold}
                  disabled={togglingHold}
                  className={`p-2 rounded-lg transition-all disabled:opacity-40 disabled:cursor-wait ${
                    order.isOnHold
                      ? 'text-info bg-info/10 hover:bg-info/20'
                      : 'text-text-secondary hover:text-info hover:bg-surface-hover'
                  }`}
                  title={order.isOnHold ? 'Poczekalnia - kliknij, by wznowić nadawanie' : 'Wstrzymaj zamówienie (Poczekalnia) - pomija przy masowym nadawaniu'}
                >
                  {togglingHold ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Hourglass className="w-5 h-5" />
                  )}
                </button>
                <button
                  onClick={handleCreateSimilarOrder}
                  className="p-2 rounded-lg text-text-secondary hover:text-accent-primary hover:bg-surface-hover transition-all"
                  title="Utwórz podobne zamówienie"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <button
                  onClick={handleDeleteOrder}
                  className="p-2 rounded-lg text-text-secondary hover:text-danger hover:bg-surface-hover transition-all"
                  title="Usuń zamówienie"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all ml-2"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {activeComplaintId && <nav aria-label="Sekcje zgłoszenia" className="flex gap-1 overflow-x-auto px-6 py-3 border-b border-border-subtle">
          {[
            ['Szczegóły', `order-${order.id}-case-top`],
            ['Rozliczenie', `order-${order.id}-settlement`],
            ['Korespondencja', `complaint-${activeComplaintId}-correspondence`],
            ['Notatki', `order-${order.id}-notes`],
            ['Historia', `order-${order.id}-history`],
          ].map(([label, target]) => <Button key={target} size="sm" variant="ghost" className="shrink-0" onClick={() => document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>{label}</Button>)}
        </nav>}
        </div>

        <div className="p-6 space-y-6">
          {/* Status bar */}
          <div id={`order-${order.id}-case-top`} className="flex flex-wrap items-center gap-4 p-4 bg-surface-hover/50 rounded-xl scroll-mt-48">
            {activeComplaintId && <ComplaintStatusControl complaintId={activeComplaintId} onUpdated={refreshComplaint} />}
            {/* Status change dropdown */}
            <div className="relative" ref={statusDropdownRef}>
              {(() => {
                const currentStatus = findStatusInTree(statuses, order.status);
                return (
                  <button
                    onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                    className="flex items-center gap-2 px-4 py-2 bg-surface-raised hover:bg-surface-hover/40 border border-border rounded-lg transition-colors"
                    disabled={updatingStatus}
                  >
                    {order.status === 'Kosz' ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-sm font-medium bg-danger/20 text-danger border border-danger/40">
                        <div className="w-2 h-2 rounded-full bg-danger" />
                        Kosz
                      </span>
                    ) : currentStatus ? (() => {
                      const resolvedColor = statusColor(currentStatus, theme);
                      const resolvedText = statusTextColor(currentStatus, theme);
                      return (
                        <span
                          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-sm font-medium border border-border-strong"
                          style={{
                            backgroundColor: `${resolvedColor}20`,
                            color: resolvedText,
                          }}
                        >
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: resolvedColor }}
                          />
                          {currentStatus.name}
                        </span>
                      );
                    })() : (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-sm font-medium bg-surface-muted text-text-secondary border border-border-strong">
                        <div className="w-2 h-2 rounded-full bg-text-muted" />
                        Nieprzypisany
                      </span>
                    )}
                    <ChevronDown className={`w-4 h-4 text-text-secondary transition-transform ${statusDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                );
              })()}

              {statusDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-surface-hover border border-border rounded-xl shadow-xl z-10 py-2 max-h-96 flex flex-col">
                  <div className="px-3 py-2 text-xs text-text-muted uppercase tracking-wider border-b border-border-subtle">
                    Zmień status
                  </div>
                  {/* Search input */}
                  <div className="px-2 py-2 border-b border-border-subtle">
                    <input
                      type="text"
                      placeholder="Szukaj statusu..."
                      value={statusSearch}
                      onChange={(e) => setStatusSearch(e.target.value)}
                      className="w-full px-3 py-1.5 bg-surface-raised border border-border rounded-lg text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-primary/50"
                      autoFocus
                    />
                  </div>
                  <div className="px-2 overflow-y-auto flex-1">
                    {/* Unassigned option at the top - show only if enabled in settings and no search or matches */}
                    {showUnassignedStatus && (!statusSearch || 'nieprzypisany'.includes(statusSearch.toLowerCase())) && (
                      <button
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleStatusChange('')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-2 mb-1 border-b border-border-subtle pb-2 ${
                          !order.status || order.status === '' || !findStatusInTree(statuses, order.status)
                            ? 'bg-gray-700/50 text-text-secondary'
                            : 'text-text-secondary hover:bg-surface-muted'
                        }`}
                      >
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-gray-500" />
                        Nieprzypisany
                      </button>
                    )}
                    {/* User-created statuses - filtered by search */}
                    {statuses.length > 0 ? (
                      <>
                        {getAllStatuses()
                          .filter(status => !statusSearch || status.name.toLowerCase().includes(statusSearch.toLowerCase()))
                          .map(status => (
                          <div key={status.id}>
                            {status.isTopLevel && !statusSearch && (
                              <div className="border-t border-border my-2" />
                            )}
                            <button
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleStatusChange(status.id)}
                              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${
                                order.statusId === status.id
                                  ? 'bg-accent-primary/20 text-accent-primary'
                                  : 'text-text-secondary hover:bg-surface-muted'
                              }`}
                              style={{ paddingLeft: `${12 + status.level * 12}px` }}
                            >
                              <div
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: status.color }}
                              />
                              {status.name}
                            </button>
                          </div>
                        ))}
                      </>
                    ) : (
                      <div className="px-1 py-3 text-center">
                        <p className="text-text-muted text-sm">Brak zdefiniowanych statusów</p>
                        <p className="text-text-disabled text-xs mt-1">Utwórz statusy w Administracja → Statusy zamówień</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1" />
            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                isPaid ? 'bg-success/20 text-success' : 'bg-surface-muted text-text-secondary'
              }`}>
                <CreditCard className="w-4 h-4" />
                <span className="text-sm">{isPaid ? 'Opłacone' : 'Nieopłacone'}</span>
              </div>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                (order.shipments || []).some(s => !['FAILED', 'RETURNED'].includes(s.status)) ? 'bg-accent-primary/20 text-accent-primary' : 'bg-surface-muted text-text-secondary'
              }`}>
                <Truck className="w-4 h-4" />
                <span className="text-sm">{(order.shipments || []).some(s => !['FAILED', 'RETURNED'].includes(s.status)) ? 'Nadane' : 'Do wysyłki'}</span>
              </div>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                order.invoices?.length ? 'bg-accent-secondary/20 text-accent-secondary' : 'bg-surface-muted text-text-secondary'
              }`}>
                <FileText className="w-4 h-4" />
                <span className="text-sm">{order.invoices?.length ? 'Faktura' : 'Brak FV'}</span>
              </div>
            </div>
          </div>


          {activeComplaintId && <ComplaintCasePanel complaintId={activeComplaintId} onUpdated={refreshComplaint} />}

          {/* Products - Enhanced Table */}
          <div className="card">
            <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-accent-primary" />
              Produkty
            </h3>
            {items && items.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-2 text-text-secondary font-medium">Produkt</th>
                      <th className="text-center py-3 px-2 text-text-secondary font-medium w-16">Ilość</th>
                      <th className="text-right py-3 px-2 text-text-secondary font-medium w-24">Cena</th>
                      <th className="text-right py-3 px-2 text-text-secondary font-medium w-20">VAT</th>
                      <th className="text-right py-3 px-2 text-text-secondary font-medium w-20">Waga</th>
                      <th className="text-right py-3 px-2 text-text-secondary font-medium w-28">Suma</th>
                      <th className="text-center py-3 px-2 text-text-secondary font-medium w-24">Akcje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => {
                      const totalNum = parseFloat(item.total?.replace(/[^\d.,]/g, '').replace(',', '.') || '0');
                      const calculatedPrice = totalNum / (item.quantity || 1);
                      const displayPrice = item.price
                        ? parseFloat(String(item.price).replace(/[^\d.,]/g, '').replace(',', '.')).toFixed(2)
                        : calculatedPrice.toFixed(2);
                      const isEditingThis = editingProductIndex === index;

                      if (isEditingThis) {
                        // Calculate VAT rate from original item for proportional recalculation
                        const origTotal = parseFloat(String(item.total).replace(/[^\d.,]/g, '').replace(',', '.') || '0');
                        const origTax = parseFloat(String(item.total_tax || '0').replace(/[^\d.,]/g, '').replace(',', '.') || '0');
                        const vatRate = origTotal > 0 ? origTax / origTotal : 0.23; // Default 23% VAT

                        // Calculate gross total (netto + VAT)
                        const editedTotal = parseFloat(editedProduct.total) || 0;
                        const editedTax = parseFloat(editedProduct.totalTax) || 0;
                        const grossTotal = editedTotal + editedTax;

                        // Editing mode
                        return (
                          <tr key={index} className="border-b border-border-subtle bg-surface-hover/50">
                            <td className="py-3 px-2">
                              <input
                                type="text"
                                value={editedProduct.name}
                                onChange={(e) => setEditedProduct(prev => ({ ...prev, name: e.target.value }))}
                                className="input text-sm w-full"
                                placeholder="Nazwa produktu"
                              />
                              <input
                                type="text"
                                value={editedProduct.sku}
                                onChange={(e) => setEditedProduct(prev => ({ ...prev, sku: e.target.value }))}
                                className="input text-xs w-full mt-1"
                                placeholder="SKU (opcjonalnie)"
                              />
                            </td>
                            <td className="py-3 px-2 text-center">
                              <input
                                type="number"
                                min="1"
                                value={editedProduct.quantity}
                                onChange={(e) => {
                                  const qty = parseInt(e.target.value) || 1;
                                  const price = parseFloat(editedProduct.price) || 0;
                                  const newTotal = price * qty;
                                  const newTax = newTotal * vatRate;
                                  setEditedProduct(prev => ({
                                    ...prev,
                                    quantity: qty,
                                    total: newTotal.toFixed(2),
                                    totalTax: newTax.toFixed(2),
                                  }));
                                }}
                                className="input text-sm w-16 text-center"
                              />
                            </td>
                            <td className="py-3 px-2 text-right">
                              <input
                                type="text"
                                value={editedProduct.price}
                                onChange={(e) => {
                                  const price = parseFloat(e.target.value) || 0;
                                  const newTotal = price * editedProduct.quantity;
                                  const newTax = newTotal * vatRate;
                                  setEditedProduct(prev => ({
                                    ...prev,
                                    price: e.target.value,
                                    total: newTotal.toFixed(2),
                                    totalTax: newTax.toFixed(2),
                                  }));
                                }}
                                className="input text-sm w-20 text-right"
                                placeholder="0.00"
                              />
                            </td>
                            <td className="py-3 px-2 text-right text-text-secondary">
                              <span className="text-xs">{editedProduct.totalTax} {currencyUnit}</span>
                            </td>
                            <td className="py-3 px-2 text-right text-text-secondary">
                              -
                            </td>
                            <td className="py-3 px-2 text-right">
                              <div className="flex flex-col items-end">
                                <span className="text-xs text-text-muted">netto: {editedProduct.total}</span>
                                <span className="text-success font-semibold">{grossTotal.toFixed(2)} {currencyUnit}</span>
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={handleSaveProduct}
                                  className="px-2 py-1 text-xs bg-success/20 text-success rounded hover:bg-success/30 transition-all"
                                >
                                  Zapisz
                                </button>
                                <button
                                  onClick={handleCancelProductEdit}
                                  className="px-2 py-1 text-xs bg-gray-600/20 text-text-secondary rounded hover:bg-gray-600/30 transition-all"
                                >
                                  Anuluj
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      }

                      // Calculate display values for normal mode
                      const itemTotalNet = parseFloat(String(item.total).replace(/[^\d.,]/g, '').replace(',', '.') || '0');
                      const itemTax = parseFloat(String(item.total_tax || '0').replace(/[^\d.,]/g, '').replace(',', '.') || '0');
                      const itemGross = itemTotalNet + itemTax;

                      return (
                        <tr key={index} className="border-b border-border-subtle hover:bg-surface-hover/30">
                          <td className="py-3 px-2">
                            <div>
                              <p className="text-text-primary font-medium">{item.name}</p>
                              {item.sku && <p className="text-xs text-text-muted">SKU: {item.sku}</p>}
                            </div>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className="text-accent-primary font-semibold">{item.quantity || 1}</span>
                          </td>
                          <td className="py-3 px-2 text-right text-text-secondary">
                            {displayPrice} {currencyUnit}
                          </td>
                          <td className="py-3 px-2 text-right text-text-secondary">
                            {itemTax > 0 ? itemTax.toFixed(2) : (item.tax || '0.00')} {currencyUnit}
                          </td>
                          <td className="py-3 px-2 text-right text-text-secondary">
                            {item.weight ? `${item.weight} kg` : '-'}
                          </td>
                          <td className="py-3 px-2 text-right">
                            <div className="flex flex-col items-end">
                              <span className="text-xs text-text-muted">netto: {itemTotalNet.toFixed(2)}</span>
                              <span className="text-success font-semibold">{itemGross.toFixed(2)} {currencyUnit}</span>
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            {order.status !== 'Kosz' && (
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => handleEditProduct(index, item)}
                                  className="p-1.5 rounded-lg text-text-secondary hover:text-accent-primary hover:bg-accent-primary/10 transition-all"
                                  title="Edytuj"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDuplicateProduct(index, item.name)}
                                  className="p-1.5 rounded-lg text-text-secondary hover:text-accent-secondary hover:bg-accent-secondary/10 transition-all"
                                  title="Duplikuj"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteProduct(index, item.name)}
                                  className="p-1.5 rounded-lg text-text-secondary hover:text-danger hover:bg-danger/10 transition-all"
                                  title="Usuń"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {/* Shipping cost row */}
                    {(() => {
                      const shipping = getShippingCost();
                      const shippingGross = shipping.total + shipping.tax;

                      if (isEditingShippingCost) {
                        const editedGross = parseFloat(editedShippingCost.total) + parseFloat(editedShippingCost.tax);
                        return (
                          <tr className="border-b border-border-subtle bg-surface-hover/50 border-t border-accent-primary/20">
                            <td className="py-3 px-2">
                              <div>
                                <p className="text-accent-primary font-medium flex items-center gap-2">
                                  <Truck className="w-4 h-4" />
                                  Koszt wysyłki
                                </p>
                                <p className="text-xs text-text-muted">{order.shippingMethod || 'Dostawa'}</p>
                              </div>
                            </td>
                            <td className="py-3 px-2 text-center text-text-muted">1</td>
                            <td className="py-3 px-2 text-right">
                              <input
                                type="text"
                                value={editedShippingCost.total}
                                onChange={(e) => setEditedShippingCost(prev => ({ ...prev, total: e.target.value }))}
                                className="input text-sm w-20 text-right"
                                placeholder="0.00"
                              />
                            </td>
                            <td className="py-3 px-2 text-right">
                              <input
                                type="text"
                                value={editedShippingCost.tax}
                                onChange={(e) => setEditedShippingCost(prev => ({ ...prev, tax: e.target.value }))}
                                className="input text-sm w-16 text-right"
                                placeholder="0.00"
                              />
                            </td>
                            <td className="py-3 px-2 text-right text-text-secondary">-</td>
                            <td className="py-3 px-2 text-right">
                              <div className="flex flex-col items-end">
                                <span className="text-xs text-text-muted">netto: {editedShippingCost.total}</span>
                                <span className="text-success font-semibold">{editedGross.toFixed(2)} {currencyUnit}</span>
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={handleSaveShippingCost}
                                  className="px-2 py-1 text-xs bg-success/20 text-success rounded hover:bg-success/30 transition-all"
                                >
                                  Zapisz
                                </button>
                                <button
                                  onClick={handleCancelShippingCostEdit}
                                  className="px-2 py-1 text-xs bg-gray-600/20 text-text-secondary rounded hover:bg-gray-600/30 transition-all"
                                >
                                  Anuluj
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      }

                      return (
                        <tr className="border-b border-border-subtle hover:bg-surface-hover/30 border-t border-accent-primary/20">
                          <td className="py-3 px-2">
                            <div>
                              <p className="text-accent-primary font-medium flex items-center gap-2">
                                <Truck className="w-4 h-4" />
                                Koszt wysyłki
                              </p>
                              <p className="text-xs text-text-muted">{order.shippingMethod || 'Dostawa'}</p>
                            </div>
                          </td>
                          <td className="py-3 px-2 text-center text-text-muted">1</td>
                          <td className="py-3 px-2 text-right text-text-secondary">
                            {shipping.total.toFixed(2)} {currencyUnit}
                          </td>
                          <td className="py-3 px-2 text-right text-text-secondary">
                            {shipping.tax.toFixed(2)} {currencyUnit}
                          </td>
                          <td className="py-3 px-2 text-right text-text-secondary">-</td>
                          <td className="py-3 px-2 text-right">
                            <div className="flex flex-col items-end">
                              <span className="text-xs text-text-muted">netto: {shipping.total.toFixed(2)}</span>
                              <span className="text-success font-semibold">{shippingGross.toFixed(2)} {currencyUnit}</span>
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            {order.status !== 'Kosz' && (
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={handleEditShippingCost}
                                  className="p-1.5 rounded-lg text-text-secondary hover:text-accent-primary hover:bg-accent-primary/10 transition-all"
                                  title="Edytuj koszt wysyłki"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-text-muted text-sm">Brak danych o produktach</p>
            )}
            <div className="mt-4 pt-4 border-t border-border-subtle flex justify-between items-center">
              <span className="text-text-secondary">Kwota brutto</span>
              <span className="text-xl font-bold text-success">
                {formatCurrency(order.totalAmount, order.currency)}
              </span>
            </div>
          </div>

          {/* Payment & Delivery - Full Width below Products */}
          <div className="card">
            <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-accent-primary" />
              Płatność i dostawa
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <DataList
                dense
                dividers={false}
                items={[
                  {
                    label: 'Metoda płatności',
                    value: <span className="text-text-secondary">{order.paymentMethod || '-'}</span>,
                  },
                  {
                    label: 'Status płatności',
                    value: (
                      <span className={isPaid ? 'text-success' : 'text-warning'}>
                        {isPaid ? 'Opłacone' : 'Oczekuje'}
                      </span>
                    ),
                  },
                ]}
              />
              <DataList
                dense
                dividers={false}
                items={[
                  {
                    label: 'Metoda dostawy',
                    value: <span className="text-text-secondary">{order.shippingMethod || '-'}</span>,
                  },
                  {
                    label: 'Koszt dostawy',
                    value: (() => {
                      const shipping = getShippingCost();
                      const gross = shipping.total + shipping.tax;
                      return <span className="text-text-secondary">{gross > 0 ? `${gross.toFixed(2)} ${currencyUnit}` : '-'}</span>;
                    })(),
                  },
                ]}
              />
              {/* Paczkomat Number - only show for paczkomat shipments */}
              {isPaczkomatShipment && (
                <DataList
                  dense
                  dividers={false}
                  items={[
                    {
                      label: (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          Nr paczkomatu
                        </span>
                      ),
                      value: (
                        <span className={`font-mono ${editedPaczkomat || paczkomatNumber ? 'text-accent-primary font-semibold' : 'text-text-muted'}`}>
                          {editedPaczkomat || paczkomatNumber || 'Brak'}
                        </span>
                      ),
                      hint: !editedPaczkomat && !paczkomatNumber ? (
                        <span className="text-warning">Zamówienie paczkomat bez numeru punktu</span>
                      ) : undefined,
                    },
                  ]}
                />
              )}
            </div>
          </div>

          {(financeRole === 'ADMIN' || financeRole === 'SUPER_ADMIN') && order.source === 'WOOCOMMERCE' && order.store?.id && (
            <details className="card" key={`finance-evidence-${order.id}`}>
              <summary className="cursor-pointer text-sm font-semibold text-text-primary">Źródła finansowe zamówienia</summary>
              <div className="mt-3"><FinanceOrderEvidence source={{ kind: 'FINANCE', storeId: order.store.id, orderId: order.id }}
                lineLabels={Object.fromEntries(order.items.filter(item => typeof item.id === 'number' && typeof item.name === 'string')
                  .map(item => [String(item.id), String(item.name)]))}
                shipmentLabels={Object.fromEntries((order.shipments ?? []).map((shipment, index) => [shipment.id,
                  shipment.trackingNumber ? `Paczka ${index + 1} · ${shipment.trackingNumber}` : `Paczka ${index + 1}`]))}/></div>
            </details>
          )}

          {/* Grid - Customer & Addresses */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Customer */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                  <User className="w-5 h-5 text-accent-primary" />
                  Klient
                </h3>
                {!isEditing && order.status !== 'Kosz' && (
                  <button
                    onClick={handleStartEdit}
                    className="p-1.5 rounded-lg text-text-muted hover:text-accent-primary hover:bg-surface-hover transition-all"
                    title="Edytuj dane klienta"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                )}
              </div>
              {isEditing ? (
                <div className="space-y-3">
                  <div>
                    <label className={`block text-xs mb-1 ${(activeErrorFields.has('firstName') || activeErrorFields.has('lastName')) ? 'text-danger font-medium' : 'text-text-muted'}`}>
                      Imię i nazwisko {(activeErrorFields.has('firstName') || activeErrorFields.has('lastName')) && '⚠'}
                    </label>
                    <input
                      type="text"
                      value={editedCustomer.name}
                      onChange={(e) => setEditedCustomer(prev => ({ ...prev, name: e.target.value }))}
                      className={`input text-sm ${(activeErrorFields.has('firstName') || activeErrorFields.has('lastName')) ? '!border-danger/50 !ring-red-500/20' : ''}`}
                      placeholder="Imię i nazwisko"
                    />
                  </div>
                  <div>
                    <label className={`block text-xs mb-1 ${activeErrorFields.has('email') ? 'text-danger font-medium' : 'text-text-muted'}`}>
                      Email {activeErrorFields.has('email') && '⚠'}
                    </label>
                    <input
                      type="email"
                      value={editedCustomer.email}
                      onChange={(e) => setEditedCustomer(prev => ({ ...prev, email: e.target.value }))}
                      className={`input text-sm ${activeErrorFields.has('email') ? '!border-danger/50 !ring-red-500/20' : ''}`}
                      placeholder="Email"
                    />
                  </div>
                  <div>
                    <label className={`block text-xs mb-1 ${activeErrorFields.has('phone') ? 'text-danger font-medium' : 'text-text-muted'}`}>
                      Telefon {activeErrorFields.has('phone') && '⚠'}
                    </label>
                    <input
                      type="tel"
                      value={editedCustomer.phone}
                      onChange={(e) => {
                        const phone = e.target.value;
                        setEditedCustomer(prev => ({ ...prev, phone }));
                        setEditedShipping(prev => ({ ...prev, phone }));
                      }}
                      className={`input text-sm ${activeErrorFields.has('phone') ? '!border-danger/50 !ring-red-500/20' : ''}`}
                      placeholder="Telefon"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <p className="text-text-primary font-medium">{order.customerName || '-'}</p>
                  </div>
                  {order.customerEmail && (
                    <div className={`flex items-center gap-2 ${activeErrorFields.has('email') ? 'text-danger' : 'text-text-secondary'}`}>
                      <Mail className="w-4 h-4" />
                      <span className="text-sm">{order.customerEmail} {activeErrorFields.has('email') && '⚠'}</span>
                    </div>
                  )}
                  {order.customerPhone && (
                    <div className={`flex items-center gap-2 ${activeErrorFields.has('phone') ? 'text-danger' : 'text-text-secondary'}`}>
                      <Phone className="w-4 h-4" />
                      <span className="text-sm">{order.customerPhone} {activeErrorFields.has('phone') && '⚠'}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Shipping Address */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                  <Truck className="w-5 h-5 text-accent-primary" />
                  Adres dostawy
                </h3>
                <div className="flex items-center gap-1">
                  {/* Google Maps button */}
                  {shipping && !isEditing && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        [cleanedAddressForMaps, shipping.postcode, shipping.city, shipping.country]
                          .filter(Boolean)
                          .join(', ')
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 rounded-lg text-text-muted hover:text-accent-primary hover:bg-surface-hover transition-all"
                      title="Otwórz w Google Maps"
                    >
                      <MapPin className="w-4 h-4" />
                    </a>
                  )}
                  {!isEditing && order.status !== 'Kosz' && (
                    <button
                      onClick={handleStartEdit}
                      className="p-1.5 rounded-lg text-text-muted hover:text-accent-primary hover:bg-surface-hover transition-all"
                      title="Edytuj adres dostawy"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              {isEditing ? (
                <div className="space-y-2">
                  {activeErrorFields.size > 0 && (
                    <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-danger/10 border border-danger/20 mb-1">
                      <AlertTriangle className="w-3.5 h-3.5 text-danger flex-shrink-0" />
                      <span className="text-xs text-danger">Pola oznaczone na czerwono spowodowały błąd nadania</span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={`block text-xs mb-1 ${activeErrorFields.has('firstName') ? 'text-danger font-medium' : 'text-text-muted'}`}>Imię {activeErrorFields.has('firstName') && '⚠'}</label>
                      <input
                        type="text"
                        value={editedShipping.firstName}
                        onChange={(e) => setEditedShipping(prev => ({ ...prev, firstName: e.target.value }))}
                        className={`input text-sm ${activeErrorFields.has('firstName') ? '!border-danger/50 !ring-red-500/20' : ''}`}
                        placeholder="Imię"
                      />
                    </div>
                    <div>
                      <label className={`block text-xs mb-1 ${activeErrorFields.has('lastName') ? 'text-danger font-medium' : 'text-text-muted'}`}>Nazwisko {activeErrorFields.has('lastName') && '⚠'}</label>
                      <input
                        type="text"
                        value={editedShipping.lastName}
                        onChange={(e) => setEditedShipping(prev => ({ ...prev, lastName: e.target.value }))}
                        className={`input text-sm ${activeErrorFields.has('lastName') ? '!border-danger/50 !ring-red-500/20' : ''}`}
                        placeholder="Nazwisko"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-text-muted mb-1">Firma</label>
                    <input
                      type="text"
                      value={editedShipping.company}
                      onChange={(e) => setEditedShipping(prev => ({ ...prev, company: e.target.value }))}
                      className="input text-sm"
                      placeholder="Firma (opcjonalnie)"
                    />
                  </div>
                  <div>
                    <label className={`block text-xs mb-1 ${activeErrorFields.has('address1') ? 'text-danger font-medium' : 'text-text-muted'}`}>Adres {activeErrorFields.has('address1') && '⚠'}</label>
                    <input
                      type="text"
                      value={editedShipping.address1}
                      onChange={(e) => setEditedShipping(prev => ({ ...prev, address1: e.target.value }))}
                      className={`input text-sm ${activeErrorFields.has('address1') ? '!border-danger/50 !ring-red-500/20' : ''}`}
                      placeholder="Ulica i numer"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={`block text-xs mb-1 ${activeErrorFields.has('postcode') ? 'text-danger font-medium' : 'text-text-muted'}`}>Kod pocztowy {activeErrorFields.has('postcode') && '⚠'}</label>
                      <input
                        type="text"
                        value={editedShipping.postcode}
                        onChange={(e) => setEditedShipping(prev => ({ ...prev, postcode: e.target.value }))}
                        className={`input text-sm ${activeErrorFields.has('postcode') ? '!border-danger/50 !ring-red-500/20' : ''}`}
                        placeholder="00-000"
                      />
                    </div>
                    <div>
                      <label className={`block text-xs mb-1 ${activeErrorFields.has('city') ? 'text-danger font-medium' : 'text-text-muted'}`}>Miasto {activeErrorFields.has('city') && '⚠'}</label>
                      <input
                        type="text"
                        value={editedShipping.city}
                        onChange={(e) => setEditedShipping(prev => ({ ...prev, city: e.target.value }))}
                        className={`input text-sm ${activeErrorFields.has('city') ? '!border-danger/50 !ring-red-500/20' : ''}`}
                        placeholder="Miasto"
                      />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-xs mb-1 ${activeErrorFields.has('phone') ? 'text-danger font-medium' : 'text-text-muted'}`}>Telefon {activeErrorFields.has('phone') && '⚠'}</label>
                    <input
                      type="tel"
                      value={editedShipping.phone}
                      onChange={(e) => {
                        const phone = e.target.value;
                        setEditedShipping(prev => ({ ...prev, phone }));
                        setEditedCustomer(prev => ({ ...prev, phone }));
                      }}
                      className={`input text-sm ${activeErrorFields.has('phone') ? '!border-danger/50 !ring-red-500/20' : ''}`}
                      placeholder="Telefon"
                    />
                  </div>
                  {/* Paczkomat field */}
                  <div className="pt-2 border-t border-border-subtle">
                    <FormField
                      label={
                        <span className={`flex items-center gap-1 ${activeErrorFields.has('paczkomat') ? 'text-danger font-medium' : ''}`}>
                          <MapPin className="w-3 h-3" />
                          Paczkomat / Punkt odbioru {activeErrorFields.has('paczkomat') && '⚠'}
                        </span>
                      }
                      helper="Kod paczkomatu InPost"
                    >
                      <Input
                        type="text"
                        value={editedPaczkomat}
                        onChange={(e) => setEditedPaczkomat(e.target.value.toUpperCase())}
                        placeholder="np. KRA01A"
                        error={activeErrorFields.has('paczkomat')}
                      />
                    </FormField>
                  </div>
                </div>
              ) : (
                <div className="space-y-1 text-sm text-text-secondary">
                  {activeErrorFields.size > 0 && (
                    <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-danger/10 border border-danger/20 mb-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-danger flex-shrink-0" />
                      <span className="text-xs text-danger">Błąd nadania - kliknij Edytuj, by poprawić oznaczone pola</span>
                    </div>
                  )}
                  {shipping ? (
                    <>
                      <p className={`font-medium ${(activeErrorFields.has('firstName') || activeErrorFields.has('lastName')) ? 'text-danger' : 'text-text-primary'}`}>
                        {shipping.first_name} {shipping.last_name} {(activeErrorFields.has('firstName') || activeErrorFields.has('lastName')) && '⚠'}
                      </p>
                      {shipping.company && <p>{shipping.company}</p>}
                      <p className={activeErrorFields.has('address1') ? 'text-danger' : ''}>{shipping.address_1} {activeErrorFields.has('address1') && '⚠'}</p>
                      <p>
                        <span className={activeErrorFields.has('postcode') ? 'text-danger' : ''}>{shipping.postcode} {activeErrorFields.has('postcode') && '⚠ '}</span>
                        <span className={activeErrorFields.has('city') ? 'text-danger' : ''}>{shipping.city} {activeErrorFields.has('city') && '⚠'}</span>
                      </p>
                      <p>{shipping.country}</p>
                      {(shipping.phone || order.customerPhone) && (
                        <div className={`flex items-center gap-1.5 mt-1 ${activeErrorFields.has('phone') ? 'text-danger' : 'text-text-secondary'}`}>
                          <Phone className="w-3.5 h-3.5" />
                          <span>{shipping.phone || order.customerPhone} {activeErrorFields.has('phone') && '⚠'}</span>
                        </div>
                      )}
                      {/* Show paczkomat only for paczkomat shipments */}
                      {isPaczkomatShipment && (editedPaczkomat || paczkomatNumber) && (
                        <div className="mt-2 pt-2 border-t border-border-subtle flex items-center gap-1.5 text-accent-primary">
                          <MapPin className="w-3.5 h-3.5" />
                          <span className="font-medium">{editedPaczkomat || paczkomatNumber}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <p>Brak danych</p>
                  )}
                </div>
              )}
            </div>

            {/* Billing Address */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                  <FileText className="w-5 h-5 text-accent-primary" />
                  Dane do faktury
                </h3>
                {!isEditing && order.status !== 'Kosz' && (
                  <button
                    onClick={handleStartEdit}
                    className="p-1.5 rounded-lg text-text-muted hover:text-accent-primary hover:bg-surface-hover transition-all"
                    title="Edytuj dane do faktury"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                )}
              </div>
              {isEditing ? (
                <div className="space-y-2">
                  {/* Copy from customer button */}
                  <button
                    onClick={handleCopyCustomerToBilling}
                    className="w-full px-3 py-2 mb-2 text-sm bg-surface-hover hover:bg-surface-muted text-text-secondary rounded-lg transition-all flex items-center justify-center gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    Skopiuj dane klienta
                  </button>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-text-muted mb-1">Imię</label>
                      <input
                        type="text"
                        value={editedBilling.firstName}
                        onChange={(e) => setEditedBilling(prev => ({ ...prev, firstName: e.target.value }))}
                        className="input text-sm"
                        placeholder="Imię"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text-muted mb-1">Nazwisko</label>
                      <input
                        type="text"
                        value={editedBilling.lastName}
                        onChange={(e) => setEditedBilling(prev => ({ ...prev, lastName: e.target.value }))}
                        className="input text-sm"
                        placeholder="Nazwisko"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-text-muted mb-1">Firma</label>
                    <input
                      type="text"
                      value={editedBilling.company}
                      onChange={(e) => setEditedBilling(prev => ({ ...prev, company: e.target.value }))}
                      className="input text-sm"
                      placeholder="Firma (opcjonalnie)"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-text-muted mb-1">Adres</label>
                    <input
                      type="text"
                      value={editedBilling.address1}
                      onChange={(e) => setEditedBilling(prev => ({ ...prev, address1: e.target.value }))}
                      className="input text-sm"
                      placeholder="Ulica i numer"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-text-muted mb-1">Kod pocztowy</label>
                      <input
                        type="text"
                        value={editedBilling.postcode}
                        onChange={(e) => setEditedBilling(prev => ({ ...prev, postcode: e.target.value }))}
                        className="input text-sm"
                        placeholder="00-000"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text-muted mb-1">Miasto</label>
                      <input
                        type="text"
                        value={editedBilling.city}
                        onChange={(e) => setEditedBilling(prev => ({ ...prev, city: e.target.value }))}
                        className="input text-sm"
                        placeholder="Miasto"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-text-muted mb-1">Email</label>
                    <input
                      type="email"
                      value={editedBilling.email}
                      onChange={(e) => setEditedBilling(prev => ({ ...prev, email: e.target.value }))}
                      className="input text-sm"
                      placeholder="Email"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-text-muted mb-1">Telefon</label>
                    <input
                      type="tel"
                      value={editedBilling.phone}
                      onChange={(e) => setEditedBilling(prev => ({ ...prev, phone: e.target.value }))}
                      className="input text-sm"
                      placeholder="Telefon"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-1 text-sm text-text-secondary">
                  {billing ? (
                    <>
                      <p className="text-text-primary font-medium">
                        {billing.first_name} {billing.last_name}
                      </p>
                      {billing.company && <p>{billing.company}</p>}
                      <p>{billing.address_1}</p>
                      <p>{billing.postcode} {billing.city}</p>
                      <p>{billing.country}</p>
                    </>
                  ) : (
                    <p>Brak danych</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {nativePickupPoints.length > 0 && <div className="card space-y-2">
            <h3 className="font-semibold text-text-primary">Punkty odbioru</h3>
            {nativePickupPoints.map(point => <div key={`${point.packageIndex}:${point.shippingMethod}`} className="text-sm text-text-secondary">
              <p className="font-medium text-text-primary">{point.name} · {point.code}</p>
              <p>{point.address}, {point.country}</p><p className="text-xs text-text-muted">InPost · Paczka {point.packageIndex + 1} · {point.shippingMethod}</p>
            </div>)}
          </div>}
          {/* Unified Save/Cancel buttons for all editable sections */}
          {isEditing && (
            <div className="card bg-surface-hover/30">
              <div className="flex gap-3">
                <Button
                  variant="primary"
                  onClick={handleSaveEdit}
                  disabled={savingOrder}
                  loading={savingOrder}
                  fullWidth
                >
                  {savingOrder ? 'Zapisywanie...' : 'Zapisz zmiany'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleCancelEdit}
                  disabled={savingOrder}
                  fullWidth
                >
                  Anuluj
                </Button>
              </div>
            </div>
          )}

          {/* Custom Actions Section - Manual Automations */}
          <div className="card">
            <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-accent-primary" />
              Własne akcje
            </h3>
            {manualAutomations.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {manualAutomations.map((automation) => {
                    const IconComponent = automation.buttonIcon
                      ? (LucideIcons as unknown as Record<string, React.ElementType>)[automation.buttonIcon] || LucideIcons.Play
                      : LucideIcons.Play;
                    const isExecuting = executingAutomation === automation.id;
                    return (
                      <button
                        key={automation.id}
                        onClick={() => handleExecuteManualAutomation(automation.id, automation.buttonText || automation.name)}
                        disabled={isExecuting}
                        className="flex items-center gap-2 px-3 py-2 bg-surface-hover hover:bg-surface-muted border border-border hover:border-accent-primary/50 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title={automation.description || automation.name}
                      >
                        {isExecuting ? (
                          <Loader2 className="w-4 h-4 text-accent-primary animate-spin" />
                        ) : (
                          <IconComponent className="w-4 h-4 text-accent-primary" />
                        )}
                        <span className="text-sm text-text-primary truncate">
                          {automation.buttonText || automation.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {automationError && (
                  <p className="text-danger text-sm mt-2">{automationError}</p>
                )}
                {automationSuccess && (
                  <p className="text-success text-sm mt-2">{automationSuccess}</p>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-6">
                <Zap className="w-10 h-10 text-text-disabled mb-3" />
                <p className="text-text-muted text-sm mb-2">Brak dostępnych akcji</p>
                <p className="text-xs text-text-disabled">
                  Skonfiguruj automatyzacje, aby dodać własne akcje do zamówień
                </p>
              </div>
            )}
          </div>

          {/* Przesyłki | Faktury — nad — Panel klienta | Zwroty: kwadrat 2x2.
              1 kolumna na mobile, 2 od md i szerzej. Karty wyrównane do tej samej
              wysokości przez auto-rows-fr + flex-col w karcie. Kolejność wizualna
              (Przesyłki, Faktury, Panel, Zwroty) ustawiona przez md:order-* —
              DOM-owy layout zostaje dla czytelności diffu. */}
          <div id={`order-${order.id}-settlement`} className="grid grid-cols-1 md:grid-cols-2 gap-6 auto-rows-fr scroll-mt-48">
            {/* 1. Przesyłki */}
            <div id={`order-${order.id}-shipments`} className="card flex flex-col md:order-1 scroll-mt-28">
              <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                <Truck className="w-5 h-5 text-accent-primary" />
                Przesyłki
              </h3>
              {order.shipments && order.shipments.length > 0 ? (
                <div className="space-y-3">
                  {order.shipments.map((shipment, index) => (
                    <div key={index} className="p-3 bg-surface-hover/50 rounded-lg">
                      {/* Row 1: Carrier name and status badge */}
                      <div className="flex items-center justify-between">
                        <p className="text-text-primary font-medium">
                          {shipment.carrier}
                        </p>
                        <span className={`badge ${
                          shipment.status === 'CREATED' ? 'badge-info' :
                          shipment.status === 'CONFIRMED' ? 'badge-info' :
                          shipment.status === 'DISPATCHED' ? 'badge-info' :
                          shipment.status === 'IN_TRANSIT' ? 'badge-warning' :
                          shipment.status === 'OUT_FOR_DELIVERY' ? 'badge-warning' :
                          shipment.status === 'DELIVERED' ? 'badge-success' :
                          shipment.status === 'RETURNED' ? 'badge-error' :
                          shipment.status === 'FAILED' ? 'badge-error' :
                          'badge-secondary'
                        }`}>
                          {shipment.status === 'CREATED' ? 'Utworzona' :
                           shipment.status === 'CONFIRMED' ? 'Gotowa' :
                           shipment.status === 'DISPATCHED' ? 'Nadana' :
                           shipment.status === 'IN_TRANSIT' ? 'W drodze' :
                           shipment.status === 'OUT_FOR_DELIVERY' ? 'W doręczeniu' :
                           shipment.status === 'DELIVERED' ? 'Dostarczona' :
                           shipment.status === 'RETURNED' ? 'Zwrócona' :
                           shipment.status === 'FAILED' ? 'Błąd' :
                           shipment.status}
                        </span>
                      </div>
                      {/* Row 2: Tracking number and date/time */}
                      <div className="flex items-center justify-between mt-2 text-sm">
                        <span className="text-text-secondary">
                          {shipment.trackingNumber || 'Brak numeru śledzenia'}
                        </span>
                        <span className="text-text-muted text-xs">
                          {shipment.createdAt ? (
                            new Date(shipment.createdAt).toLocaleString('pl-PL', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          ) : ''}
                        </span>
                      </div>
                      {activeComplaintId && shipment.carrier === 'INPOST' && shipment.trackingNumber && <ShipmentTimeline complaintId={activeComplaintId} shipmentId={shipment.id} />}
                      {/* Row 3: Actions */}
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        {shipment.status !== 'FAILED' && (
                          <button
                            onClick={async () => {
                              try {
                                const { data } = await shipmentApi.getLabel(shipment.id);
                                const byteCharacters = atob(data.data);
                                const byteNumbers = new Array(byteCharacters.length);
                                for (let i = 0; i < byteCharacters.length; i++) {
                                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                                }
                                const byteArray = new Uint8Array(byteNumbers);
                                const blob = new Blob([byteArray], { type: data.contentType });
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = data.filename || 'etykieta.pdf';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                              } catch (err) {
                                console.error('Failed to get label:', err);
                                showError(err, 'label');
                              }
                            }}
                            className="text-xs px-2 py-1 bg-accent-secondary/20 text-accent-secondary rounded hover:bg-accent-secondary/30 transition-colors flex items-center gap-1"
                          >
                            <Download className="w-3 h-3" />
                            Etykieta
                          </button>
                        )}
                        {shipment.trackingNumber && (
                          <a
                            href={`https://inpost.pl/sledzenie-przesylek?number=${shipment.trackingNumber}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs px-2 py-1 bg-surface-muted text-text-secondary rounded hover:bg-surface-overlay transition-colors flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Śledź
                          </a>
                        )}
                        <button
                          onClick={async () => {
                            if (!(await confirmDialog({
                              title: 'Usuń przesyłkę',
                              message: 'Czy na pewno chcesz usunąć tę przesyłkę?',
                              subtitle: 'Przesyłka zostanie anulowana w InPost.',
                              confirmLabel: 'Usuń przesyłkę',
                            }))) return;
                            try {
                              await shipmentApi.cancel(shipment.id);
                              const updatedOrder = await orderApi.getOne(order.id);
                              onOrderUpdated(updatedOrder.data);
                            } catch (err) {
                              console.error('Failed to delete shipment:', err);
                              showError(err, 'shipment');
                            }
                          }}
                          className="text-xs px-2 py-1 bg-danger/20 text-danger rounded hover:bg-danger/30 transition-colors flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" />
                          Usuń
                        </button>
                      </div>
                    </div>
                  ))}
                  {/* Buttons to create additional shipment */}
                  <div className="pt-3 border-t border-border">
                    {shipmentError && (
                      <div className="mb-3">
                        <p className="text-danger text-sm whitespace-pre-line">{shipmentError}</p>
                        <button
                          onClick={() => handleCreateShipment(true)}
                          disabled={creatingShipment}
                          className="mt-2 text-xs px-3 py-1.5 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded hover:bg-amber-500/30 transition-colors disabled:opacity-50"
                        >
                          {creatingShipment ? 'Tworzenie...' : 'Nadaj mimo to (pomiń walidację)'}
                        </button>
                      </div>
                    )}
                    {selectedShipmentService ? (
                      /* Configuration panel - shown after clicking service button */
                      <div className="animate-slide-down">
                        <p className="text-xs text-text-muted mb-3">
                          {selectedShipmentService === 'inpost_courier_standard' ? 'InPost Kurier' : 'InPost Paczkomat'} - Opcje przesyłki:
                        </p>
                        <div className="p-3 bg-surface-hover rounded-lg space-y-3 mb-3">
                          {/* Package size */}
                          <div>
                            <label className="text-xs text-text-secondary block mb-1">Gabaryt</label>
                            <div className="flex flex-wrap gap-2">
                              {[
                                { value: 'small', label: 'A (8×38×64 cm)' },
                                { value: 'medium', label: 'B (19×38×64 cm)' },
                                { value: 'large', label: 'C (41×38×64 cm)' },
                              ].map((size) => (
                                <button
                                  key={size.value}
                                  onClick={() => setShipmentConfig(prev => ({ ...prev, parcelSize: size.value as 'small' | 'medium' | 'large' }))}
                                  className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                                    shipmentConfig.parcelSize === size.value
                                      ? 'border-accent-primary bg-accent-primary/10 text-accent-primary'
                                      : 'border-dark-500 text-text-secondary hover:border-gray-500'
                                  }`}
                                >
                                  {size.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          {/* Paczkomat number - only for locker shipments */}
                          {selectedShipmentService === 'inpost_locker_standard' && (
                            <div>
                              <label className="text-xs text-text-secondary block mb-1 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                Paczkomat docelowy
                              </label>
                              <input
                                type="text"
                                placeholder="np. KRA01A"
                                value={editedPaczkomat}
                                onChange={(e) => setEditedPaczkomat(e.target.value.toUpperCase())}
                                className="input text-sm py-1.5 w-full font-mono"
                              />
                            </div>
                          )}
                          {/* COD amount - only for courier (lockers don't support COD) */}
                          {selectedShipmentService === 'inpost_courier_standard' && (
                            <div>
                              <label className="text-xs text-text-secondary block mb-1">Pobranie (PLN)</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                                value={shipmentConfig.codAmount}
                                onChange={(e) => setShipmentConfig(prev => ({ ...prev, codAmount: e.target.value }))}
                                className="input text-sm py-1.5 w-full"
                              />
                            </div>
                          )}
                          {/* Insurance */}
                          <div>
                            <label className="text-xs text-text-secondary block mb-1">Ubezpieczenie (PLN)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={shipmentConfig.insurance}
                              onChange={(e) => setShipmentConfig(prev => ({ ...prev, insurance: e.target.value }))}
                              className="input text-sm py-1.5 w-full"
                            />
                          </div>
                          {/* Content description / Uwagi nadawcy */}
                          <div>
                            <label className="text-xs text-text-secondary block mb-1">Uwagi nadawcy (na etykiecie)</label>
                            <input
                              type="text"
                              placeholder={order.orderNumber || 'np. numer zamówienia'}
                              value={shipmentConfig.comments}
                              onChange={(e) => {
                                // When user edits, use ONLY their new text (completely replace shortcode value)
                                setShipmentConfig(prev => ({ ...prev, comments: e.target.value, commentsModified: true }));
                              }}
                              className="input text-sm py-1.5 w-full"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            fullWidth
                            onClick={() => handleCreateShipment()}
                            disabled={creatingShipment}
                            loading={creatingShipment}
                          >
                            {creatingShipment ? 'Tworzenie...' : 'Nadaj etykietę'}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={cancelShipmentConfig}
                            disabled={creatingShipment}
                          >
                            Anuluj
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* Service selection buttons */
                      <>
                        <p className="text-xs text-text-muted mb-2">Utwórz kolejną przesyłkę:</p>
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => openShipmentConfig('inpost_courier_standard')}
                          >
                            + Kurier
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => openShipmentConfig('inpost_locker_standard')}
                          >
                            + Paczkomat
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col">
                  {shipmentError && (
                    <div className="mb-3 text-center">
                      <p className="text-danger text-sm whitespace-pre-line">{shipmentError}</p>
                      <button
                        onClick={() => handleCreateShipment(true)}
                        disabled={creatingShipment}
                        className="mt-2 text-xs px-3 py-1.5 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded hover:bg-amber-500/30 transition-colors disabled:opacity-50"
                      >
                        {creatingShipment ? 'Tworzenie...' : 'Nadaj mimo to (pomiń walidację)'}
                      </button>
                    </div>
                  )}

                  {selectedShipmentService ? (
                    /* Configuration panel - shown after clicking service button */
                    <div className="animate-slide-down">
                      <p className="text-sm text-text-primary mb-3 text-center">
                        {selectedShipmentService === 'inpost_courier_standard' ? 'InPost Kurier' : 'InPost Paczkomat'}
                      </p>
                      <div className="p-3 bg-surface-hover rounded-lg space-y-3 mb-4">
                        {/* Package size */}
                        <div>
                          <label className="text-xs text-text-secondary block mb-1">Gabaryt</label>
                          <div className="flex flex-wrap gap-2 justify-center">
                            {[
                              { value: 'small', label: 'A (8×38×64 cm)' },
                              { value: 'medium', label: 'B (19×38×64 cm)' },
                              { value: 'large', label: 'C (41×38×64 cm)' },
                            ].map((size) => (
                              <button
                                key={size.value}
                                onClick={() => setShipmentConfig(prev => ({ ...prev, parcelSize: size.value as 'small' | 'medium' | 'large' }))}
                                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                                  shipmentConfig.parcelSize === size.value
                                    ? 'border-accent-primary bg-accent-primary/10 text-accent-primary'
                                    : 'border-dark-500 text-text-secondary hover:border-gray-500'
                                }`}
                              >
                                {size.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        {/* Paczkomat number - only for locker shipments */}
                        {selectedShipmentService === 'inpost_locker_standard' && (
                          <div>
                            <label className="text-xs text-text-secondary block mb-1 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              Paczkomat docelowy
                            </label>
                            <input
                              type="text"
                              placeholder="np. KRA01A"
                              value={editedPaczkomat}
                              onChange={(e) => setEditedPaczkomat(e.target.value.toUpperCase())}
                              className="input text-sm py-1.5 w-full font-mono"
                            />
                          </div>
                        )}
                        {/* COD amount - only for courier (lockers don't support COD) */}
                        {selectedShipmentService === 'inpost_courier_standard' && (
                          <div>
                            <label className="text-xs text-text-secondary block mb-1">Pobranie (PLN)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={shipmentConfig.codAmount}
                              onChange={(e) => setShipmentConfig(prev => ({ ...prev, codAmount: e.target.value }))}
                              className="input text-sm py-1.5 w-full"
                            />
                          </div>
                        )}
                        {/* Insurance */}
                        <div>
                          <label className="text-xs text-text-secondary block mb-1">Ubezpieczenie (PLN)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            value={shipmentConfig.insurance}
                            onChange={(e) => setShipmentConfig(prev => ({ ...prev, insurance: e.target.value }))}
                            className="input text-sm py-1.5 w-full"
                          />
                        </div>
                        {/* Content description / Uwagi nadawcy */}
                        <div>
                          <label className="text-xs text-text-secondary block mb-1">Uwagi nadawcy (na etykiecie)</label>
                          <input
                            type="text"
                            placeholder={order.orderNumber || 'np. numer zamówienia'}
                            value={shipmentConfig.comments}
                            onChange={(e) => {
                              // When user edits, use ONLY their new text (completely replace shortcode value)
                              setShipmentConfig(prev => ({ ...prev, comments: e.target.value, commentsModified: true }));
                            }}
                            className="input text-sm py-1.5 w-full"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 justify-center">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleCreateShipment()}
                          disabled={creatingShipment}
                          loading={creatingShipment}
                        >
                          {creatingShipment ? 'Tworzenie...' : 'Nadaj etykietę'}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={cancelShipmentConfig}
                          disabled={creatingShipment}
                        >
                          Anuluj
                        </Button>
                      </div>
                      {selectedShipmentService === 'inpost_locker_standard' && (editedPaczkomat || paczkomatNumber) && (
                        <p className="text-xs text-text-muted mt-3 text-center">
                          <MapPin className="w-3 h-3 inline mr-1" />
                          {editedPaczkomat !== paczkomatNumber && editedPaczkomat ? 'Paczkomat (zmieniony): ' : 'Wykryty paczkomat: '}
                          <span className="text-accent-primary font-mono">{editedPaczkomat || paczkomatNumber}</span>
                        </p>
                      )}
                    </div>
                  ) : (
                    /* Service selection - EmptyState z actions (spójny styl z Notatki/Faktury). */
                    <>
                      <EmptyState
                        icon={Truck}
                        title="Brak nadanych przesyłek"
                        size="sm"
                        fillHeight
                        actions={
                          <div className="flex flex-wrap items-center justify-center gap-2">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => openShipmentConfig('inpost_courier_standard')}
                            >
                              InPost Kurier
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => openShipmentConfig('inpost_locker_standard')}
                            >
                              InPost Paczkomat
                            </Button>
                          </div>
                        }
                      />
                      {(editedPaczkomat || paczkomatNumber) && (
                        <p className="text-xs text-text-muted mt-3 text-center">
                          <MapPin className="w-3 h-3 inline mr-1" />
                          {editedPaczkomat !== paczkomatNumber && editedPaczkomat ? 'Paczkomat (zmieniony): ' : 'Wykryty paczkomat: '}
                          <span className="text-accent-primary font-mono">{editedPaczkomat || paczkomatNumber}</span>
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

          {/* 3. Panel klienta */}
          <div className="card flex flex-col md:order-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                <Users className="w-5 h-5 text-accent-primary" />
                Panel klienta
              </h3>
              {portalLink && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => window.open(portalLink, '_blank')}
                  leftIcon={<ExternalLink />}
                >
                  Panel klienta
                </Button>
              )}
            </div>

            <ResendOrderStatus key={order.id} orderId={order.id} parentOrderId={order.parentOrderId} />

            {/* Complaints */}
            {portalComplaints.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-text-muted mb-2 font-medium uppercase tracking-wider">Reklamacje ({portalComplaints.length})</p>
                <div className="space-y-2">
                  {portalComplaints.map((c) => (
                    <button type="button" key={c.id} onClick={() => { setActiveComplaintId(c.id); document.getElementById(`order-${order.id}-case-top`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} className="w-full text-left p-3 bg-surface-hover/50 rounded-lg hover:bg-surface-hover">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-text-primary text-sm font-medium">
                          {c.type === 'COMPLAINT' ? 'Reklamacja' : c.type === 'RETURN' ? 'Zwrot' : 'Wymiana'}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          c.status === 'SUBMITTED' ? 'bg-info/20 text-accent-info border border-info/30' :
                          c.status === 'IN_REVIEW' ? 'bg-warning/20 text-warning border border-warning/30' :
                          c.status === 'ACCEPTED' ? 'bg-success/20 text-success border border-success/30' :
                          c.status === 'REJECTED' ? 'bg-danger/20 text-danger border border-danger/30' :
                          'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                        }`}>
                          {c.status === 'SUBMITTED' ? 'Złożona' :
                           c.status === 'IN_REVIEW' ? 'W rozpatrywaniu' :
                           c.status === 'ACCEPTED' ? 'Zaakceptowana' :
                           c.status === 'REJECTED' ? 'Odrzucona' :
                           c.status === 'RESOLVED' ? 'Rozwiązana' : c.status}
                        </span>
                      </div>
                      <p className="text-text-primary/60 text-xs mb-1">{c.reason.substring(0, 100)}{c.reason.length > 100 ? '...' : ''}</p>
                      <p className="text-text-muted text-xs">{new Date(c.createdAt).toLocaleString('pl-PL')}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Portal activity history - R7 (2026-04-22): bez nagłówka, sam EmptyState /
                lista, żeby karta wyglądała spójnie ze Zwroty / Faktury (tylko h3 karty na górze). */}
            <div className="flex-1 flex flex-col">
              {loadingPortalActivities ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 text-text-disabled animate-spin" />
                </div>
              ) : portalActivities.length > 0 ? (
                <div className="space-y-2">
                  {portalActivities.map((activity) => (
                    <div key={activity.id} className="flex gap-3 p-2.5 bg-surface-hover/30 rounded-lg">
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                        activity.action === 'address_updated' ? 'bg-blue-400' :
                        activity.action === 'resend_requested' ? 'bg-orange-400' :
                        activity.action === 'complaint_submitted' ? 'bg-red-400' :
                        activity.action === 'invoice_downloaded' ? 'bg-success' :
                        'bg-gray-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-text-primary/80 text-sm">{activity.description}</p>
                        <p className="text-text-muted text-xs mt-0.5">
                          {activity.order?.orderNumber ? `#${activity.order.orderNumber} • ` : ''}
                          {new Date(activity.createdAt).toLocaleString('pl-PL')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={Users}
                  title="Brak aktywności klienta"
                  size="sm"
                  fillHeight
                />
              )}
            </div>

            {!order.customerEmail && (
              <p className="text-text-muted text-sm mt-2">Brak adresu email - panel klienta niedostępny.</p>
            )}
          </div>
            {/* 2. Faktury */}
            <div id={`order-${order.id}-invoices`} className="card flex flex-col md:order-2 scroll-mt-28">
              <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-accent-primary" />
                Faktury
              </h3>
              {/* Filter out deleted invoices */}
              {(() => {
                const activeInvoices = (order.invoices || []).filter(inv => inv.status !== 'DELETED');
                // Check for any regular invoice (VAT, INVOICE legacy, CORRECTION, ADVANCE, FINAL)
                // Exclude only PROFORMA and RECEIPT as they don't count as "main" invoices
                const hasActiveInvoice = activeInvoices.some(inv =>
                  inv.type === 'VAT' || inv.type === 'INVOICE' || inv.type === 'CORRECTION' ||
                  inv.type === 'ADVANCE' || inv.type === 'FINAL'
                );

                return activeInvoices.length > 0 ? (
                <div className="space-y-3">
                  {activeInvoices.map((invoice) => (
                    <div key={invoice.id} className="p-3 bg-surface-hover/50 rounded-lg">
                      {/* Row 1: Invoice number and status */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <p className="text-text-primary font-medium">
                            {invoice.invoiceNumber || 'Faktura'}
                          </p>
                          {/* Invoice type badge */}
                          {invoice.type === 'CORRECTION' && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded font-medium">
                              Korekta
                            </span>
                          )}
                          {invoice.type === 'PROFORMA' && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-info/20 text-accent-info rounded font-medium">
                              Proforma
                            </span>
                          )}
                          {invoice.type === 'ADVANCE' && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded font-medium">
                              Zaliczka
                            </span>
                          )}
                          {invoice.ksefStatus === 'OK' && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-success/20 text-success rounded">
                              KSeF
                            </span>
                          )}
                          {invoice.ksefStatus === 'PROCESSING' && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-warning/20 text-warning rounded">
                              KSeF...
                            </span>
                          )}
                        </div>
                        <span className={`badge ${invoice.status === 'ISSUED' ? 'badge-success' : invoice.status === 'PAID' ? 'badge-info' : 'badge-warning'}`}>
                          {invoice.status === 'ISSUED' ? 'Wystawiona' : invoice.status === 'PAID' ? 'Zapłacona' : invoice.status === 'SENT' ? 'Wysłana' : 'Robocza'}
                        </span>
                      </div>
                      {/* Row 2: Amount and date/time */}
                      <div className="flex items-center justify-between mt-2 text-sm">
                        <span className="text-text-secondary">
                          {invoice.grossAmount ? formatCurrency(invoice.grossAmount, invoice.currency || 'PLN') : 'Brak kwoty'}
                        </span>
                        <span className="text-text-muted text-xs">
                          {invoice.createdAt ? (
                            new Date(invoice.createdAt).toLocaleString('pl-PL', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          ) : invoice.issueDate ? (
                            new Date(invoice.issueDate).toLocaleDateString('pl-PL')
                          ) : ''}
                        </span>
                      </div>
                      {/* Row 3: Actions */}
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <button
                          onClick={() => handleDownloadInvoice(invoice.id, invoice.invoiceNumber || `faktura-${order.orderNumber}`)}
                          disabled={downloadingInvoice === invoice.id}
                          className="text-xs px-2 py-1 bg-accent-secondary/20 text-accent-secondary rounded hover:bg-accent-secondary/30 transition-colors flex items-center gap-1 disabled:opacity-50"
                        >
                          <Download className="w-3 h-3" />
                          {downloadingInvoice === invoice.id ? 'Pobieranie...' : 'PDF'}
                        </button>
                        {invoice.viewUrl && (
                          <a
                            href={invoice.viewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs px-2 py-1 bg-surface-muted text-text-secondary rounded hover:bg-surface-overlay transition-colors flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Zobacz
                          </a>
                        )}
                        {invoice.status !== 'PAID' && (
                          <button
                            onClick={() => handleMarkInvoiceAsPaid(invoice.id)}
                            className="text-xs px-2 py-1 bg-success/20 text-success rounded hover:bg-green-500/30 transition-colors flex items-center gap-1"
                          >
                            <Check className="w-3 h-3" />
                            Zapłacono
                          </button>
                        )}
                        {/* Correction button - only for VAT invoices, not for corrections themselves */}
                        {(invoice.type === 'VAT' || invoice.type === 'INVOICE') && (
                          <button
                            onClick={() => handleCreateCorrectionInvoice(invoice.id)}
                            disabled={creatingCorrectionInvoice === invoice.id}
                            className="text-xs px-2 py-1 bg-orange-500/20 text-orange-400 rounded hover:bg-orange-500/30 transition-colors flex items-center gap-1 disabled:opacity-50"
                          >
                            <RotateCcw className="w-3 h-3" />
                            {creatingCorrectionInvoice === invoice.id ? 'Tworzenie...' : 'Korekta'}
                          </button>
                        )}
                        {invoice.ksefStatus !== 'OK' && (
                          <button
                            onClick={() => handleDeleteInvoice(invoice.id, invoice.invoiceNumber || 'faktura')}
                            className="text-xs px-2 py-1 bg-danger/20 text-danger rounded hover:bg-danger/30 transition-colors flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" />
                            Usuń
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {/* Show invoice error if any */}
                  {invoiceError && (
                    <p className="text-danger text-sm mt-3">{invoiceError}</p>
                  )}
                  {/* Only show create buttons if no invoice exists */}
                  {!hasActiveInvoice && (
                    <div className="pt-3 border-t border-border">
                      <p className="text-xs text-text-muted mb-2">Wystaw dokument:</p>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleCreateInvoice('VAT')}
                          disabled={creatingInvoice}
                          loading={creatingInvoice}
                        >
                          {creatingInvoice ? 'Wystawianie...' : '+ Faktura'}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleCreateInvoice('RECEIPT')}
                          disabled={creatingInvoice}
                          loading={creatingInvoice}
                        >
                          {creatingInvoice ? 'Wystawianie...' : '+ Paragon'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex-1 flex flex-col">
                  <EmptyState
                    icon={FileText}
                    title="Brak wystawionych faktur"
                    size="sm"
                    fillHeight
                    actions={
                      <div className="flex flex-wrap items-center justify-center gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleCreateInvoice('VAT')}
                          disabled={creatingInvoice}
                          loading={creatingInvoice}
                        >
                          {creatingInvoice ? 'Wystawianie...' : 'Wystaw fakturę'}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleCreateInvoice('RECEIPT')}
                          disabled={creatingInvoice}
                          loading={creatingInvoice}
                        >
                          {creatingInvoice ? 'Wystawianie...' : 'Wystaw paragon'}
                        </Button>
                      </div>
                    }
                  />
                  {invoiceError && (
                    <p className="text-danger text-sm mt-2 text-center">{invoiceError}</p>
                  )}
                </div>
              );
              })()}
            </div>

          {/* 4. Zwroty */}
          <div id={`order-${order.id}-refund`} className="card flex flex-col md:order-4 scroll-mt-28">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-accent-primary" />
                Zwroty
              </h3>
              {!showRefundForm && refundInfo?.availableForRefund && refundInfo.availableForRefund > 0 && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleOpenRefundForm}
                  leftIcon={<RotateCcw />}
                >
                  Zwrot środków
                </Button>
              )}
            </div>

            {/* Refund Status Summary - always visible */}
            {loadingRefundInfo && !refundInfo ? (
              <div className="flex-1 flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 text-accent-primary animate-spin" />
              </div>
            ) : refundInfo ? (
              <>
                {/* Summary Info - only show if there are completed refunds */}
                {(() => {
                  const completedRefundAmount = refundInfo.refunds?.filter(r => r.status === 'COMPLETED').reduce((sum, r) => sum + r.amount, 0) || 0;
                  const hasAnyHistory = (refundInfo.refunds?.length || 0) > 0;
                  const observedRefundsGross = refundInfo.observedRefundsGross ?? 0;
                  // Gdy brak jakichkolwiek zwrotów, brak formularza i brak summary —
                  // nie zostawiajmy pustej karty, pokażmy EmptyState (R8, 2026-04-22).
                  if (!completedRefundAmount && !hasAnyHistory && !showRefundForm && !observedRefundsGross) {
                    return (
                      <EmptyState
                        icon={RotateCcw}
                        title="Brak zwrotów dla tego zamówienia"
                        size="sm"
                        fillHeight
                      />
                    );
                  }
                  return (completedRefundAmount > 0 || observedRefundsGross > 0) ? (
                    <div className="p-3 bg-surface-hover/30 rounded-lg mb-4">
                      {observedRefundsGross > 0 && (
                        <p className="text-xs text-text-secondary mb-2">
                          WooCommerce raportuje zwroty na kwotę {observedRefundsGross.toFixed(2)} {refundInfo.currency}.
                          Kalkulator finansów pomniejsza przychód tego zamówienia o tę kwotę.
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-text-secondary">Bramka:</span>
                          <span className={`font-medium ${refundInfo.gateway === 'PRZELEWY24' ? 'text-accent-primary' : 'text-text-secondary'}`}>
                            {refundInfo.gateway || 'Nieobsługiwana'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-text-secondary">Zwrócono:</span>
                          <span className="font-medium text-orange-400">
                            {completedRefundAmount.toFixed(2)} {refundInfo.currency}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-text-secondary">Dostępne:</span>
                          <span className={`font-medium ${refundInfo.availableForRefund > 0 ? 'text-success' : 'text-text-muted'}`}>
                            {refundInfo.availableForRefund.toFixed(2)} {refundInfo.currency}
                          </span>
                        </div>
                        {/* Historia zwrotów button in the bar */}
                        {refundInfo.refunds && refundInfo.refunds.length > 0 && (
                          <button
                            onClick={() => setShowRefundHistory(!showRefundHistory)}
                            className="ml-auto flex items-center gap-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors"
                          >
                            <History className="w-3.5 h-3.5" />
                            Historia zwrotów ({refundInfo.refunds.length})
                            {showRefundHistory ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                          </button>
                        )}
                      </div>
                      {/* Integration Info - helps diagnose IP issues */}
                      {refundInfo.gateway === 'PRZELEWY24' && refundInfo.integrationConfigured && (
                        <div className="text-xs text-text-muted flex gap-4 mt-2">
                          <span>
                            Integracja: {refundInfo.integrationSource === 'store' ? `sklepu "${refundInfo.storeName}"` : 'globalna'}
                          </span>
                          {refundInfo.integrationMerchantId && (
                            <span>Merchant ID: {refundInfo.integrationMerchantId}</span>
                          )}
                        </div>
                      )}
                    </div>
                  ) : null;
                })()}

                {/* Inline Refund Form */}
                {showRefundForm && (
                  <div className="p-4 bg-surface-hover/30 rounded-xl space-y-4 mb-4 border border-border">
                    {loadingRefundInfo ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="w-6 h-6 text-accent-primary animate-spin" />
                      </div>
                    ) : (
                      <>
                        {/* Can Refund Check */}
                        {!refundInfo.canRefund && (
                          <div className="p-3 bg-danger/10 border border-danger/30 rounded-lg">
                            <p className="text-danger text-sm">
                              Brak danych transakcji P24 w zamówieniu. Sprawdź czy zamówienie zostało opłacone przez Przelewy24.
                            </p>
                          </div>
                        )}

                        {!refundInfo.integrationConfigured && refundInfo.gateway === 'PRZELEWY24' && (
                          <div className="p-3 bg-warning/10 border border-warning/30 rounded-lg">
                            <p className="text-warning text-sm">
                              Integracja Przelewy24 nie jest skonfigurowana. Przejdź do Integracje → Bramki płatnicze.
                            </p>
                          </div>
                        )}

                        {refundInfo.canRefund && refundInfo.integrationConfigured && refundInfo.availableForRefund > 0 && (
                          <>
                            {/* Amount Input */}
                            <div>
                              <label className="block text-sm font-medium text-text-secondary mb-2">
                                Kwota zwrotu ({refundInfo.currency})
                              </label>
                              <div className="flex gap-2">
                                <div className="flex-1">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    max={refundInfo.availableForRefund}
                                    value={refundAmount}
                                    onChange={(e) => setRefundAmount(e.target.value)}
                                    placeholder="0.00"
                                  />
                                </div>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={handleRefundFullAmount}
                                  className="whitespace-nowrap"
                                >
                                  Całość
                                </Button>
                              </div>
                            </div>

                            {/* Description */}
                            <div>
                              <label className="block text-sm font-medium text-text-secondary mb-2">
                                Tytuł zwrotu (max 35 znaków)
                              </label>
                              <input
                                type="text"
                                maxLength={35}
                                value={refundDescription}
                                onChange={(e) => setRefundDescription(e.target.value)}
                                className="input w-full"
                                placeholder="Zwrot za zamówienie"
                              />
                              <p className="text-xs text-text-muted mt-1">{refundDescription.length}/35</p>
                            </div>
                          </>
                        )}

                        {/* Error */}
                        {refundError && (
                          <div className="p-3 bg-danger/10 border border-danger/30 rounded-lg">
                            <p className="text-danger text-sm">{refundError}</p>
                          </div>
                        )}

                        {/* Action Buttons — Primary LEWO, Cancel zaraz obok (konwencja 2026-04-21) */}
                        <div className="flex items-center gap-2 pt-2">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={handleProcessRefund}
                            disabled={!refundInfo?.canRefund || !refundInfo?.integrationConfigured || processingRefund || !refundAmount}
                            loading={processingRefund}
                          >
                            {processingRefund ? 'Przetwarzanie...' : 'Realizuj zwrot'}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleCloseRefundForm}
                          >
                            Anuluj
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Refund History - collapsible */}
                {showRefundHistory && refundInfo.refunds && refundInfo.refunds.length > 0 && (
                  <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    {refundInfo.refunds.map((refund) => (
                      <div key={refund.id} className="p-3 bg-surface-hover/50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-text-primary font-medium">
                              {refund.amount.toFixed(2)} {currencyUnit}
                            </p>
                            <p className="text-xs text-text-secondary">{refund.description}</p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ${
                            refund.status === 'COMPLETED' ? 'bg-success/20 text-success' :
                            refund.status === 'FAILED' ? 'bg-danger/20 text-danger' :
                            refund.status === 'PROCESSING' ? 'bg-warning/20 text-warning' :
                            'bg-gray-500/20 text-text-secondary'
                          }`}>
                            {refund.status === 'COMPLETED' ? 'Zrealizowany' :
                             refund.status === 'FAILED' ? 'Błąd' :
                             refund.status === 'PROCESSING' ? 'W trakcie' : refund.status}
                          </span>
                        </div>
                        <label className="mt-2 flex items-center gap-2 text-xs text-text-secondary cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!refund.isChargeback}
                            onChange={(e) => handleToggleChargeback(refund.id, e.target.checked)}
                            className="accent-current"
                          />
                          Chargeback (kalkulator doliczy opłatę z reguły prowizji)
                        </label>
                        {refund.errorMessage && (
                          <p className="text-xs text-danger mt-1">{refund.errorMessage}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <EmptyState
                icon={RotateCcw}
                title="Brak informacji o zwrotach"
                size="sm"
                fillHeight
              />
            )}
          </div>
          </div>{/* end grid */}

          {/* Email Section */}
          {activeComplaintId && <ComplaintCorrespondencePanel complaintId={activeComplaintId} />}
          {!activeComplaintId && <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                <Mail className="w-5 h-5 text-accent-primary" />
                Wiadomości email
              </h3>
              {order.customerEmail && !showEmailModal && (
                <div className="flex items-center gap-2">
                  <a
                    href={`/messages?customer=${encodeURIComponent(order.customerEmail)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-btn text-sm text-text-secondary hover:text-accent-primary hover:bg-surface-hover/50 transition-colors"
                    title={conversationCount === null
                      ? 'Otwórz moduł Wiadomości z filtrem na tego klienta (nowa karta)'
                      : conversationCount === 0
                        ? 'Brak wiadomości z tym klientem - otwórz moduł Wiadomości (nowa karta)'
                        : `${conversationCount} wiadomości z tym klientem - otwórz w nowej karcie`}
                  >
                    <MessageSquare className="w-4 h-4" />
                    Zobacz konwersację{conversationCount !== null && conversationCount > 0 ? ` (${conversationCount})` : ''}
                  </a>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowEmailModal(true)}
                    leftIcon={<Send />}
                  >
                    Wyślij email
                  </Button>
                </div>
              )}
            </div>

            {!order.customerEmail ? (
              <EmptyState
                icon={Mail}
                title="Brak adresu email klienta"
                size="sm"
              />
            ) : showEmailModal ? (
              /* Inline Email Composer */
              <div className="space-y-4">
                <div className="p-4 bg-surface-hover/30 rounded-xl space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-text-secondary">
                      Odbiorca: <span className="text-text-primary">{order.customerEmail}</span>
                    </p>
                    <button
                      onClick={() => {
                        setShowEmailModal(false);
                        setTemplateNotice('');
                        setEmailError(null);
                      }}
                      className="text-text-secondary hover:text-text-primary text-sm"
                    >
                      Anuluj
                    </button>
                  </div>

                  <label className="block text-sm font-medium text-text-secondary">
                    Temat
                    <input value={customEmailSubject} onChange={event => setCustomEmailSubject(event.target.value)} placeholder="Temat wiadomości" className="input w-full mt-1" disabled={sendingEmail || applyMessageTemplate.isPending} />
                  </label>
                  {messageTemplates.isError && <p className="text-xs text-danger">Nie udało się pobrać szablonów. <button className="underline" onClick={() => void messageTemplates.refetch()}>Ponów</button></p>}
                  <MessageTemplateEditor value={customEmailBody} onChange={setCustomEmailBody} templates={messageTemplates.data || []} busy={sendingEmail || applyMessageTemplate.isPending} onApply={async template => {
                    setTemplateNotice(''); setEmailError(null);
                    return (await applyMessageTemplate.mutateAsync(template.id)).bodyHtml;
                  }} />
                  {templateNotice && <p role="status" className="text-xs text-text-secondary">{templateNotice}</p>}

                  {/* Send errors */}
                  {emailError && (
                    <div className="p-3 bg-danger/10 border border-danger/20 rounded-lg">
                      <p className="text-danger text-sm">{emailError}</p>
                    </div>
                  )}

                  {/* Send Button — pojedynczy CTA, bez cancel (zamykanie przez X modalu) */}
                  <div className="flex items-center gap-2 pt-2">
                    <Button
                      variant="primary"
                      onClick={handleSendCustomEmail}
                      disabled={
                        sendingEmail ||
                        applyMessageTemplate.isPending || !customEmailSubject.trim() || !customEmailBody.replace(/<[^>]+>/g, '').trim()
                      }
                      loading={sendingEmail}
                      leftIcon={sendingEmail ? undefined : <Send />}
                    >
                      {sendingEmail ? 'Wysyłanie...' : 'Wyślij email'}
                    </Button>
                  </div>
                </div>

                {/* Email History below composer */}
                {orderEmails.length > 0 && (
                  <div className="pt-4 border-t border-border">
                    <p className="text-xs text-text-muted mb-2">Historia wysłanych emaili:</p>
                    <div className="space-y-2">
                      {orderEmails.slice(0, 3).map((email) => (
                        <div key={email.id} className="bg-surface-hover/50 rounded-lg overflow-hidden">
                          <div className="p-2 flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-text-primary text-sm truncate">{email.subject}</p>
                              <p className="text-xs text-text-muted">
                                {new Date(email.createdAt).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                              </p>
                              {email.status === 'failed' && (
                                <p className="text-xs text-danger mt-1 line-clamp-2" title={email.error || undefined}>
                                  {getEmailFailureMessage(email.error)}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 ml-2">
                              <button
                                onClick={() => setPreviewingEmail(previewingEmail?.id === email.id ? null : email)}
                                className={`p-1 rounded transition-colors ${previewingEmail?.id === email.id ? 'text-accent-primary bg-accent-primary/10' : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/60'}`}
                                title={previewingEmail?.id === email.id ? 'Zwiń podgląd' : 'Podgląd wiadomości'}
                              >
                                {previewingEmail?.id === email.id ? <ChevronUp className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                              </button>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                email.status === 'sent' ? 'bg-success/20 text-success' :
                                email.status === 'failed' ? 'bg-danger/20 text-danger' :
                                'bg-warning/20 text-warning'
                              }`}>
                                {email.status === 'sent' ? 'Wysłany' : email.status === 'failed' ? 'Błąd' : 'Oczekuje'}
                              </span>
                            </div>
                          </div>
                          {/* Inline email preview */}
                          {previewingEmail?.id === email.id && (
                            <div className="px-3 pb-3 border-t border-border-subtle">
                              <div className="mt-3 p-3 bg-surface-raised/50 rounded-lg">
                                <p className="text-xs text-text-muted mb-1">Treść wiadomości:</p>
                                <div
                                  className="prose prose-invert prose-sm max-w-none text-sm max-h-48 overflow-y-auto"
                                  dangerouslySetInnerHTML={{ __html: email.bodyHtml }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : loadingEmails ? (
              <div className="flex flex-col items-center justify-center py-6">
                <Loader2 className="w-6 h-6 text-text-muted animate-spin mb-2" />
                <p className="text-text-muted text-sm">Ładowanie...</p>
              </div>
            ) : orderEmails.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs text-text-muted mb-2">Historia wysłanych emaili:</p>
                {orderEmails.slice(0, 5).map((email) => (
                  <div key={email.id} className="bg-surface-hover/50 rounded-lg overflow-hidden">
                    <div className="p-2 flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-text-primary text-sm truncate">{email.subject}</p>
                        <div className="flex items-center gap-2 text-xs text-text-muted">
                          <span>{email.toEmail}</span>
                          <span>•</span>
                          <span>{new Date(email.createdAt).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        {email.status === 'failed' && (
                          <p className="text-xs text-danger mt-1 line-clamp-2" title={email.error || undefined}>
                            {getEmailFailureMessage(email.error)}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <button
                          onClick={() => setPreviewingEmail(previewingEmail?.id === email.id ? null : email)}
                          className={`p-1 rounded transition-colors ${previewingEmail?.id === email.id ? 'text-accent-primary bg-accent-primary/10' : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/60'}`}
                          title={previewingEmail?.id === email.id ? 'Zwiń podgląd' : 'Podgląd wiadomości'}
                        >
                          {previewingEmail?.id === email.id ? <ChevronUp className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          email.status === 'sent' ? 'bg-success/20 text-success' :
                          email.status === 'failed' ? 'bg-danger/20 text-danger' :
                          'bg-warning/20 text-warning'
                        }`}>
                          {email.status === 'sent' ? 'Wysłany' : email.status === 'failed' ? 'Błąd' : 'Oczekuje'}
                        </span>
                        {email.status === 'failed' && (
                          <button
                            onClick={() => handleResendEmail(email.id)}
                            className="text-xs text-accent-primary hover:underline"
                          >
                            Ponów
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Inline email preview */}
                    {previewingEmail?.id === email.id && (
                      <div className="px-3 pb-3 border-t border-border-subtle">
                        <div className="mt-3 p-3 bg-surface-raised/50 rounded-lg">
                          <p className="text-xs text-text-muted mb-1">Treść wiadomości:</p>
                          <div
                            className="prose prose-invert prose-sm max-w-none text-sm max-h-64 overflow-y-auto"
                            dangerouslySetInnerHTML={{ __html: email.bodyHtml }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={Mail}
                title="Brak wysłanych emaili"
                size="sm"
              />
            )}
          </div>}

          {/* Notes section */}
          <div id={`order-${order.id}-notes`} className="card scroll-mt-48">
            <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-accent-primary" />
              Notatki
            </h3>
            <div className="space-y-3">
              {/* Customer message */}
              {order.notes && (
                <div className="p-3 bg-surface-hover/30 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-accent-primary/20 text-accent-primary text-xs font-medium rounded">
                      Wiadomość klienta
                    </span>
                    <span className="text-xs text-text-muted">
                      {new Date(order.externalCreatedAt).toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw' })}
                    </span>
                  </div>
                  <p className="text-text-primary text-sm whitespace-pre-wrap">{order.notes}</p>
                </div>
              )}

              {/* Customer notes — scalone z Inbox po emailu klienta */}
              {customerNotes.map((note) => (
                editingNoteId === note.id ? (
                  <div key={note.id} className="p-3 bg-surface-hover/30 rounded-xl border border-border">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 bg-warning/20 text-warning text-xs font-medium rounded">
                        Edycja notatki
                      </span>
                    </div>
                    <textarea
                      value={editedNote}
                      onChange={(e) => setEditedNote(e.target.value)}
                      placeholder="Wpisz treść notatki..."
                      className="w-full bg-surface-raised border border-border rounded-lg p-3 text-text-primary text-sm placeholder-text-muted focus:outline-none focus:border-accent-primary/50 resize-none"
                      rows={4}
                      autoFocus
                    />
                    <div className="flex items-center gap-2 mt-3">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleUpdateNote(note.id)}
                        disabled={savingNote}
                        loading={savingNote}
                      >
                        {savingNote ? 'Zapisywanie...' : 'Zapisz'}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setEditingNoteId(null);
                          setEditedNote('');
                        }}
                        disabled={savingNote}
                      >
                        Anuluj
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div key={note.id} className="p-3 bg-surface-hover/30 rounded-xl group">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="px-2 py-0.5 bg-warning/20 text-warning text-xs font-medium rounded">
                          Notatka wewnętrzna
                        </span>
                        <span className="text-xs text-text-muted">
                          {note.userName} • {new Date(note.createdAt).toLocaleString('pl-PL')}
                        </span>
                        {note.conversationId && !note.orderId && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent-tertiary/10 text-accent-tertiary border border-accent-tertiary/20" title="Notatka dodana z wątku wiadomości">
                            z wątku Inbox
                          </span>
                        )}
                        {note.updatedAt !== note.createdAt && (
                          <span className="text-xs text-text-disabled">
                            (edytowano {new Date(note.updatedAt).toLocaleString('pl-PL')})
                          </span>
                        )}
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => {
                            setEditedNote(note.content);
                            setEditingNoteId(note.id);
                          }}
                          className="p-1.5 text-text-secondary hover:text-accent-primary hover:bg-accent-primary/10 rounded transition-colors"
                          title="Edytuj notatkę"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          disabled={savingNote}
                          className="p-1.5 text-text-secondary hover:text-danger hover:bg-danger/10 rounded transition-colors disabled:opacity-50"
                          title="Usuń notatkę"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-text-primary text-sm whitespace-pre-wrap">{note.content}</p>
                  </div>
                )
              ))}

              {/* Add new note form */}
              {isAddingNote && (
                <div className="p-3 bg-surface-hover/30 rounded-xl border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-warning/20 text-warning text-xs font-medium rounded">
                      Nowa notatka
                    </span>
                  </div>
                  <textarea
                    value={editedNote}
                    onChange={(e) => setEditedNote(e.target.value)}
                    placeholder="Wpisz treść notatki..."
                    className="w-full bg-surface-raised border border-border rounded-lg p-3 text-text-primary text-sm placeholder-text-muted focus:outline-none focus:border-accent-primary/50 resize-none"
                    rows={4}
                    autoFocus
                  />
                  <div className="flex items-center gap-2 mt-3">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleAddNote}
                      disabled={savingNote || !editedNote.trim()}
                      loading={savingNote}
                    >
                      {savingNote ? 'Zapisywanie...' : 'Dodaj'}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setIsAddingNote(false);
                        setEditedNote('');
                      }}
                      disabled={savingNote}
                    >
                      Anuluj
                    </Button>
                  </div>
                </div>
              )}

              {/* Empty state + CTA - EmptyState sam renderuje przycisk ze standardowym spacingiem.
                  R7 (2026-04-22): buttonVariant="secondary" dla lżejszego CTA w karcie. */}
              {!order.notes && customerNotes.length === 0 && !isAddingNote && !notesLoading && (
                <EmptyState
                  icon={MessageSquare}
                  title="Brak notatek"
                  size="sm"
                  buttonLabel="Dodaj notatkę"
                  buttonIcon={Plus}
                  buttonVariant="secondary"
                  onButtonClick={() => {
                    setEditedNote('');
                    setIsAddingNote(true);
                  }}
                />
              )}

              {/* Add note button - gdy są już notatki (po lewej, standard) */}
              {(order.notes || customerNotes.length > 0) && !isAddingNote && !editingNoteId && (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Plus />}
                  onClick={() => {
                    setEditedNote('');
                    setIsAddingNote(true);
                  }}
                >
                  Dodaj notatkę
                </Button>
              )}
            </div>
          </div>

          {/* Customer History & Change History - 50/50 layout, równe wysokości */}
          <div className="grid grid-cols-2 gap-6 auto-rows-fr">
            {/* Purchase History */}
            <div className="card flex flex-col">
              <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-accent-primary" />
                Historia zakupów
              </h3>
              {loadingCustomerHistory ? (
                <div className="flex flex-col items-center justify-center py-8 min-h-[200px]">
                  <RefreshCw className="w-6 h-6 text-text-muted animate-spin mb-2" />
                  <p className="text-text-muted text-sm">Ładowanie historii...</p>
                </div>
              ) : customerHistory && customerHistory.orders.length > 0 ? (
                <>
                  {/* LTV Summary */}
                  <div className="mb-4 p-3 bg-surface-hover/50 rounded-lg border border-accent-primary/20">
                    <div className="flex justify-between items-center">
                      <span className="text-text-secondary text-sm">Suma LTV ({customerHistory.orderCount} zam.)</span>
                      <span className="text-accent-primary font-semibold">{customerHistory.totalLTV.toFixed(2)} {customerHistory.currency}</span>
                    </div>
                  </div>
                  {/* Orders List */}
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {customerHistory.orders.map((historyOrder) => (
                      <div
                        key={historyOrder.id}
                        className="p-3 bg-surface-hover/50 rounded-lg border border-border-subtle hover:border-accent-primary/30 transition-all cursor-pointer"
                        onClick={async () => {
                          // Fetch and display the order from history — navigateTo bypasses stale update guard
                          try {
                            const { data: fetchedOrder } = await orderApi.getOne(historyOrder.id);
                            onOrderUpdated(fetchedOrder, { closeModal: false, navigateTo: true });
                          } catch (err) {
                            console.error('Failed to load order from history:', err);
                          }
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-text-primary font-medium text-sm">#{historyOrder.orderNumber}</p>
                            <p className="text-xs text-text-muted">
                              {new Date(historyOrder.createdAt).toLocaleDateString('pl-PL', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              })}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-success font-medium text-sm">
                              {historyOrder.totalAmount.toFixed(2)} {historyOrder.currency}
                            </p>
                            {historyOrder.status && (
                              <span
                                className="text-xs px-2 py-0.5 rounded"
                                style={{
                                  backgroundColor: `${historyOrder.status.color}20`,
                                  color: historyOrder.status.color,
                                }}
                              >
                                {historyOrder.status.name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <EmptyState
                  icon={ShoppingBag}
                  title="Brak innych zamówień od tego klienta"
                  size="sm"
                  fillHeight
                />
              )}
            </div>

            {/* Change History */}
            <div id={`order-${order.id}-history`} className="card flex flex-col scroll-mt-48">
              <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                <History className="w-5 h-5 text-accent-primary" />
                Historia zmian
              </h3>
              {loadingHistory ? (
              <div className="flex flex-col items-center justify-center py-8 min-h-[200px]">
                <RefreshCw className="w-6 h-6 text-text-muted animate-spin mb-2" />
                <p className="text-text-muted text-sm">Ładowanie historii...</p>
              </div>
            ) : history.length > 0 ? (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {history.map((entry) => {
                  // Format history entry in human-readable way
                  const formatHistoryEntry = () => {
                    // Extract oldValues and newValues from metadata (where backend stores them)
                    const metadata = entry.metadata as Record<string, unknown> | null;
                    const oldVals = (metadata?.oldValues ?? entry.oldValues) as Record<string, unknown> | null;
                    const newVals = (metadata?.newValues ?? entry.newValues) as Record<string, unknown> | null;

                    const complaintEntry = formatComplaintHistory(entry);
                    if (complaintEntry) return complaintEntry;
                    const resendEntry = formatResendHistory(entry);
                    if (resendEntry) return resendEntry;

                    switch (entry.action) {
                      case 'STATUS_CHANGE': {
                        const rawOld = oldVals?.status as string || 'Nieprzypisany';
                        const rawNew = newVals?.status as string || 'Nieprzypisany';
                        // Normalizuj wewnetrzne TRASH na user-friendly "Kosz" w UI.
                        const oldStatus = rawOld === 'TRASH' ? 'Kosz' : rawOld;
                        const newStatus = rawNew === 'TRASH' ? 'Kosz' : rawNew;
                        const movedToTrash = newStatus === 'Kosz' && oldStatus !== 'Kosz';
                        const restoredFromTrash = oldStatus === 'Kosz' && newStatus !== 'Kosz';
                        if (movedToTrash) {
                          return {
                            title: 'Przeniesiono do kosza',
                            description: `Z statusu „${oldStatus}"`,
                            color: 'text-danger',
                            icon: '🗑️',
                          };
                        }
                        if (restoredFromTrash) {
                          return {
                            title: 'Przywrócono z kosza',
                            description: `Na status „${newStatus}"`,
                            color: 'text-success',
                            icon: '↩️',
                          };
                        }
                        return {
                          title: 'Zmiana statusu',
                          description: `${oldStatus} → ${newStatus}`,
                          color: 'text-accent-primary',
                          icon: '🔄'
                        };
                      }
                      case 'UPDATE':
                      case 'ORDER_UPDATE': {
                        // Build list of changed fields
                        const changedFields: string[] = [];

                        // Helper to get address field changes
                        const getAddressFieldChanges = (
                          oldAddr: Record<string, unknown> | null | undefined,
                          newAddr: Record<string, unknown> | null | undefined
                        ): string[] => {
                          const changes: string[] = [];
                          if (!oldAddr || !newAddr) return changes;
                          const fields: Record<string, string> = {
                            'first_name': 'imię',
                            'last_name': 'nazwisko',
                            'company': 'firma',
                            'address_1': 'ulica',
                            'address_2': 'adres linia 2',
                            'city': 'miasto',
                            'postcode': 'kod pocztowy',
                            'country': 'kraj',
                            'phone': 'telefon',
                            'email': 'email',
                          };
                          for (const [key, label] of Object.entries(fields)) {
                            // Normalize values: treat undefined, null, and empty string as equivalent
                            const oldVal = (oldAddr[key] as string | undefined) || '';
                            const newVal = (newAddr[key] as string | undefined) || '';
                            // Only show change if values are actually different (after normalization)
                            if (newVal && oldVal !== newVal) {
                              changes.push(`${label}: "${oldVal}" → "${newVal}"`);
                            }
                          }
                          return changes;
                        };

                        if (newVals?.status !== undefined && oldVals?.status !== newVals.status) {
                          // P8 (2026-04-30): mapuj wewnętrzne TRASH na user-friendly "Kosz".
                          const humanizeStatus = (s: string) => s === 'TRASH' ? 'Kosz' : s;
                          const fromStatus = humanizeStatus((oldVals?.status as string) || 'Nieprzypisany');
                          const toStatus = humanizeStatus((newVals?.status as string) || 'Nieprzypisany');
                          if (toStatus === 'Kosz' && fromStatus !== 'Kosz') {
                            changedFields.push(`Przeniesiono do kosza (z „${fromStatus}")`);
                          } else if (fromStatus === 'Kosz' && toStatus !== 'Kosz') {
                            changedFields.push(`Przywrócono z kosza (na „${toStatus}")`);
                          } else {
                            changedFields.push(`Status: ${fromStatus} → ${toStatus}`);
                          }
                        }
                        if (newVals?.customerName !== undefined && oldVals?.customerName !== newVals.customerName) {
                          changedFields.push(`Imię klienta: ${oldVals?.customerName || '-'} → ${newVals.customerName}`);
                        }
                        if (newVals?.customerEmail !== undefined && oldVals?.customerEmail !== newVals.customerEmail) {
                          changedFields.push(`Email: ${oldVals?.customerEmail || '-'} → ${newVals.customerEmail}`);
                        }
                        // Normalize phone values to avoid showing change when value didn't actually change
                        const oldPhone = (oldVals?.customerPhone as string | undefined) || '';
                        const newPhone = (newVals?.customerPhone as string | undefined) || '';
                        if (newPhone && oldPhone !== newPhone) {
                          changedFields.push(`Telefon: ${oldPhone || '-'} → ${newPhone}`);
                        }
                        if (newVals?.shippingAddress !== undefined) {
                          const oldShipping = oldVals?.shippingAddress as Record<string, unknown> | null;
                          const newShipping = newVals.shippingAddress as Record<string, unknown> | null;
                          const addrChanges = getAddressFieldChanges(oldShipping, newShipping);
                          if (addrChanges.length > 0) {
                            changedFields.push(`Adres dostawy (${addrChanges.join(', ')})`);
                          }
                        }
                        if (newVals?.billingAddress !== undefined) {
                          const oldBilling = oldVals?.billingAddress as Record<string, unknown> | null;
                          const newBilling = newVals.billingAddress as Record<string, unknown> | null;
                          const addrChanges = getAddressFieldChanges(oldBilling, newBilling);
                          if (addrChanges.length > 0) {
                            changedFields.push(`Dane do faktury (${addrChanges.join(', ')})`);
                          }
                        }
                        if (newVals?.notes !== undefined && oldVals?.notes !== newVals.notes) {
                          changedFields.push('Notatka');
                        }
                        if (newVals?.internalNotes !== undefined && oldVals?.internalNotes !== newVals.internalNotes) {
                          changedFields.push('Notatka wewnętrzna');
                        }

                        // Fallback if no specific fields identified
                        if (changedFields.length === 0) {
                          changedFields.push('Zaktualizowano dane');
                        }

                        return {
                          title: 'Edycja zamówienia',
                          description: changedFields.join(', '),
                          color: 'text-accent-secondary',
                          icon: '✏️'
                        };
                      }
                      case 'CREATE':
                        return {
                          title: 'Utworzenie zamówienia',
                          description: 'Zamówienie zostało zaimportowane',
                          color: 'text-success',
                          icon: '➕'
                        };
                      case 'ORDER_CREATE_MANUAL':
                        return {
                          title: 'Utworzenie zamówienia',
                          description: 'Zamówienie utworzone ręcznie',
                          color: 'text-success',
                          icon: '✍️'
                        };
                      case 'order_created': {
                        // Handle lowercase order_created from OrderHistory
                        const historyMetadata = entry.metadata as Record<string, unknown> | null;
                        const source = historyMetadata?.source as string || '';
                        const isManual = source === 'MANUAL';
                        return {
                          title: 'Utworzenie zamówienia',
                          description: isManual ? 'Zamówienie utworzone ręcznie' : entry.description || 'Zamówienie zostało utworzone',
                          color: 'text-success',
                          icon: isManual ? '✍️' : '➕'
                        };
                      }
                      case 'SHIPMENT_CREATE':
                        const trackingNumber = newVals?.trackingNumber as string;
                        return {
                          title: 'Nadanie przesyłki',
                          description: trackingNumber ? `List przewozowy: ${trackingNumber}` : 'Utworzono przesyłkę',
                          color: 'text-accent-info',
                          icon: '📦'
                        };
                      case 'INVOICE_CREATE':
                        const invoiceNumber = newVals?.invoiceNumber as string;
                        return {
                          title: 'Wystawienie faktury',
                          description: invoiceNumber ? `Faktura: ${invoiceNumber}` : 'Wystawiono fakturę',
                          color: 'text-warning',
                          icon: '📄'
                        };
                      case 'INVOICE_MARK_PAID':
                        const paidInvoiceNumber = newVals?.invoiceNumber as string;
                        return {
                          title: 'Opłacenie faktury',
                          description: paidInvoiceNumber ? `Faktura: ${paidInvoiceNumber}` : 'Oznaczono jako opłaconą',
                          color: 'text-success',
                          icon: '💰'
                        };
                      case 'INVOICE_DELETE':
                        const deletedInvoiceNumber = oldVals?.invoiceNumber as string;
                        return {
                          title: 'Usunięcie faktury',
                          description: deletedInvoiceNumber ? `Faktura: ${deletedInvoiceNumber}` : 'Usunięto fakturę',
                          color: 'text-danger',
                          icon: '🗑️'
                        };
                      case 'REFUND':
                        return {
                          title: 'Zwrot',
                          description: 'Przetworzono zwrot zamówienia',
                          color: 'text-danger',
                          icon: '↩️'
                        };
                      case 'SHIPMENT_CANCEL':
                        const cancelledTracking = oldVals?.trackingNumber as string;
                        return {
                          title: 'Anulowanie przesyłki',
                          description: cancelledTracking ? `Anulowano list: ${cancelledTracking}` : 'Anulowano przesyłkę',
                          color: 'text-danger',
                          icon: '🚫'
                        };
                      case 'ITEM_UPDATE': {
                        const itemName = (newVals?.item as Record<string, unknown>)?.name as string || 'produkt';
                        return {
                          title: 'Edycja produktu',
                          description: `Zmieniono: ${itemName}`,
                          color: 'text-accent-secondary',
                          icon: '✏️'
                        };
                      }
                      case 'ITEM_DUPLICATE': {
                        const dupItemName = (newVals?.item as Record<string, unknown>)?.name as string || 'produkt';
                        return {
                          title: 'Duplikacja produktu',
                          description: `Zduplikowano: ${dupItemName}`,
                          color: 'text-accent-primary',
                          icon: '📋'
                        };
                      }
                      case 'ITEM_DELETE': {
                        const delItemName = (oldVals?.item as Record<string, unknown>)?.name as string || 'produkt';
                        return {
                          title: 'Usunięcie produktu',
                          description: `Usunięto: ${delItemName}`,
                          color: 'text-danger',
                          icon: '🗑️'
                        };
                      }
                      case 'ORDER_DUPLICATE': {
                        const origNum = newVals?.originalOrderNumber as string || '';
                        return {
                          title: 'Duplikacja zamówienia',
                          description: origNum ? `Kopia zamówienia #${origNum}` : 'Utworzono kopię zamówienia',
                          color: 'text-accent-primary',
                          icon: '📋'
                        };
                      }
                      case 'shipment_error': {
                        // Get error details from metadata
                        const metadata = entry.metadata as Record<string, unknown> | null;
                        const errorType = metadata?.errorType as string || 'unknown_error';
                        let errorMessage = metadata?.errorMessage as string || entry.description || 'Błąd tworzenia przesyłki';
                        const isUnavailableOffer = /offer(?:_selected)?_is_not_available|offer\.not_available/i.test(errorMessage);

                        // Map for translating raw InPost validation codes to Polish
                        const validationCodeLabels: Record<string, string> = {
                          'post_code_invalid': 'nieprawidłowy kod pocztowy',
                          'phone_invalid': 'nieprawidłowy numer telefonu',
                          'phone_required': 'numer telefonu jest wymagany',
                          'email_invalid': 'nieprawidłowy adres email',
                          'email_required': 'adres email jest wymagany',
                          'city_required': 'miasto jest wymagane',
                          'street_required': 'ulica jest wymagana',
                          'building_number_required': 'numer budynku jest wymagany',
                          'name_required': 'imię/nazwisko jest wymagane',
                          'name_invalid': 'nieprawidłowe imię/nazwisko',
                          'post_code_required': 'kod pocztowy jest wymagany',
                          'country_code_invalid': 'nieprawidłowy kod kraju',
                          'target_point_invalid': 'nieprawidłowy punkt docelowy',
                          'target_point_required': 'punkt docelowy jest wymagany',
                          // Offer / service availability
                          'offer_is_not_available': 'wybrana oferta przewozu jest niedostępna',
                          'offer_not_available': 'wybrana oferta przewozu jest niedostępna',
                          'service_not_available': 'usługa niedostępna dla tego adresu',
                          'point_disabled': 'paczkomat nieczynny',
                          'point_not_found': 'nie znaleziono paczkomatu',
                          'point_full': 'paczkomat zapełniony',
                          // Raw single-word codes from InPost API
                          'invalid': 'nieprawidłowa wartość',
                          'required': 'pole wymagane',
                          'too_short': 'za krótka wartość',
                          'too_long': 'za długa wartość',
                        };

                        // Field section labels (receiver, sender, parcels, etc.)
                        const sectionLabels: Record<string, string> = {
                          'receiver': 'Odbiorca',
                          'sender': 'Nadawca',
                          'parcels': 'Paczka',
                          'service': 'Usługa',
                          'cod': 'Pobranie',
                          'custom_attributes': 'Atrybuty',
                        };

                        // Clean up raw InPost API error messages
                        if (errorMessage.includes('InPost API error:') || errorMessage.includes('Błąd walidacji InPost:') || errorMessage.includes('Błąd walidacji:')) {
                          // Try to extract meaningful info from JSON error
                          try {
                            const jsonMatch = errorMessage.match(/\{.*\}/s);
                            if (jsonMatch) {
                              const errorJson = JSON.parse(jsonMatch[0]);
                              if (errorJson.details) {
                                // Extract field-specific errors
                                const fieldErrors: string[] = [];
                                for (const [section, fields] of Object.entries(errorJson.details)) {
                                  const sectionLabel = sectionLabels[section] || section;
                                  // Handle string values at section level, e.g. { offer: "offer_is_not_available" }.
                                  // Without this guard, Object.entries("offer_is_not_available") below iterates
                                  // characters and produces "offer - 0: o offer - 1: f …" noise (regression).
                                  if (typeof fields === 'string') {
                                    fieldErrors.push(`${sectionLabel}: ${validationCodeLabels[fields] || fields}`);
                                    continue;
                                  }
                                  if (Array.isArray(fields)) {
                                    for (const fieldObj of fields) {
                                      // Same guard for per-item string (e.g. fields: ["offer_is_not_available"]).
                                      if (typeof fieldObj === 'string') {
                                        fieldErrors.push(`${sectionLabel}: ${validationCodeLabels[fieldObj] || fieldObj}`);
                                        continue;
                                      }
                                      for (const [field, errors] of Object.entries(fieldObj as Record<string, string[]>)) {
                                        const fieldLabels: Record<string, string> = {
                                          phone: 'telefon',
                                          email: 'email',
                                          post_code: 'kod pocztowy',
                                          city: 'miasto',
                                          street: 'ulica',
                                          building_number: 'numer budynku',
                                          name: 'imię/nazwisko',
                                          target_point: 'paczkomat',
                                          country_code: 'kod kraju',
                                          address: 'adres',
                                          first_name: 'imię',
                                          last_name: 'nazwisko',
                                          company_name: 'nazwa firmy',
                                        };
                                        const fieldLabel = fieldLabels[field] || field;
                                        let translatedErrors: string[];
                                        if (Array.isArray(errors) && errors.every(e => typeof e === 'string')) {
                                          translatedErrors = (errors as string[]).map(e => validationCodeLabels[e] || e);
                                        } else if (Array.isArray(errors) && errors.some(e => typeof e === 'object')) {
                                          // Nested array of objects like [{ building_number: ["required"] }]
                                          translatedErrors = [];
                                          for (const item of errors) {
                                            if (typeof item === 'object' && item !== null) {
                                              for (const [subField, subErrors] of Object.entries(item as Record<string, unknown>)) {
                                                const subFieldLabel = fieldLabels[subField] || subField;
                                                if (Array.isArray(subErrors)) {
                                                  const translated = subErrors.map(e => typeof e === 'string' ? (validationCodeLabels[e] || e) : String(e));
                                                  translatedErrors.push(`${subFieldLabel}: ${translated.join(', ')}`);
                                                } else {
                                                  translatedErrors.push(`${subFieldLabel}: ${typeof subErrors === 'string' ? (validationCodeLabels[subErrors] || subErrors) : String(subErrors)}`);
                                                }
                                              }
                                            } else if (typeof item === 'string') {
                                              translatedErrors.push(validationCodeLabels[item] || item);
                                            }
                                          }
                                        } else if (typeof errors === 'object' && !Array.isArray(errors) && errors !== null) {
                                          // Nested object like { post_code: ["post_code_error"] }
                                          translatedErrors = Object.entries(errors as Record<string, unknown>).map(([subField, subErrors]) => {
                                            const subFieldLabel = fieldLabels[subField] || subField;
                                            if (Array.isArray(subErrors)) {
                                              const translated = subErrors.map(e => typeof e === 'string' ? (validationCodeLabels[e] || e) : String(e));
                                              return `${subFieldLabel}: ${translated.join(', ')}`;
                                            }
                                            return `${subFieldLabel}: ${typeof subErrors === 'string' ? (validationCodeLabels[subErrors] || subErrors) : String(subErrors)}`;
                                          });
                                        } else {
                                          translatedErrors = [String(errors)];
                                        }
                                        fieldErrors.push(`${sectionLabel} - ${fieldLabel}: ${translatedErrors.join(', ')}`);
                                      }
                                    }
                                  }
                                }
                                if (fieldErrors.length > 0) {
                                  errorMessage = fieldErrors.join('\n');
                                }
                              } else if (errorJson.message) {
                                errorMessage = errorJson.message;
                              }
                            } else {
                              // No JSON - try to parse "section: field_code" format like "receiver: post_code_invalid"
                              const plainMatch = errorMessage.match(/(?:InPost|walidacji)[^:]*:\s*(.+)$/i);
                              if (plainMatch) {
                                const rawParts = plainMatch[1].trim();
                                // Parse patterns like "receiver: post_code_invalid" or "receiver: post_code_invalid, phone_invalid"
                                const sectionMatch = rawParts.match(/^(\w+):\s*(.+)$/);
                                if (sectionMatch) {
                                  const section = sectionLabels[sectionMatch[1]] || sectionMatch[1];
                                  const codes = sectionMatch[2].split(/[,;]\s*/);
                                  const translated = codes.map(c => validationCodeLabels[c.trim()] || c.trim());
                                  errorMessage = `${section}: ${translated.join(', ')}`;
                                } else {
                                  // Just translate individual codes
                                  const codes = rawParts.split(/[,;]\s*/);
                                  const translated = codes.map(c => validationCodeLabels[c.trim()] || c.trim());
                                  errorMessage = translated.join(', ');
                                }
                              }
                            }
                          } catch {
                            // Keep original message if parsing fails
                            errorMessage = 'Błąd walidacji danych przesyłki';
                          }
                        }

                        // Also translate any remaining raw validation codes in the message
                        for (const [code, label] of Object.entries(validationCodeLabels)) {
                          if (errorMessage.includes(code)) {
                            errorMessage = errorMessage.replace(new RegExp(code.replace(/_/g, '_'), 'g'), label);
                          }
                        }

                        // Starsze wpisy historii zawierają angielski opis i kod połączone
                        // znakami punktora. Samo podmienienie kodu nadal zostawiało operatorowi
                        // surowy komunikat InPost, dlatego cały wpis zastępujemy instrukcją.
                        if (isUnavailableOffer) {
                          errorMessage = OFFER_NOT_AVAILABLE_MESSAGE;
                        }

                        // Strip "Błąd tworzenia przesyłki: " prefix from description if the title already says "Błąd przesyłki"
                        errorMessage = errorMessage.replace(/^Błąd tworzenia przesyłki:\s*/i, '');

                        // Map error types to user-friendly labels
                        const errorTypeLabels: Record<string, string> = {
                          'invalid_phone': 'Nieprawidłowy telefon',
                          'missing_phone': 'Brak telefonu',
                          'invalid_email': 'Nieprawidłowy email',
                          'missing_email': 'Brak emaila',
                          'invalid_postal_code': 'Nieprawidłowy kod pocztowy',
                          'missing_postal_code': 'Brak kodu pocztowego',
                          'invalid_city': 'Nieprawidłowe miasto',
                          'missing_city': 'Brak miasta',
                          'invalid_street': 'Nieprawidłowy adres',
                          'missing_street': 'Brak adresu',
                          'missing_building_number': 'Brak numeru budynku',
                          'invalid_name': 'Nieprawidłowe imię/nazwisko',
                          'missing_name': 'Brak imienia/nazwiska',
                          'invalid_locker': 'Nieprawidłowy paczkomat',
                          'missing_locker': 'Brak paczkomatu',
                          'locker_full': 'Paczkomat przepełniony',
                          'locker_disabled': 'Paczkomat niedostępny',
                          'parcel_too_big': 'Paczka zbyt duża',
                          'weight_exceeded': 'Przekroczono wagę',
                          'cod_limit_exceeded': 'Przekroczono limit pobrania',
                          'unknown_error': 'Nieznany błąd'
                        };

                        const errorLabel = isUnavailableOffer
                          ? 'Oferta InPost niedostępna'
                          : (errorTypeLabels[errorType] || errorType);

                        // Build a clear, user-friendly message
                        // If description duplicates the title info, show only description
                        const finalDescription = errorMessage && errorMessage !== errorLabel
                          ? errorMessage
                          : '';

                        return {
                          title: 'Błąd przesyłki',
                          description: finalDescription
                            ? `${errorLabel}: ${finalDescription}`
                            : errorLabel,
                          color: 'text-danger',
                          icon: '⚠️'
                        };
                      }
                      case 'status_changed': {
                        // Handle lowercase status_changed from OrderHistory
                        const statusMetadata = entry.metadata as Record<string, unknown> | null;
                        // L8: Translate WooCommerce statuses to Polish
                        const fromStatus = translateWooStatus(statusMetadata?.fromStatus as string || statusMetadata?.oldStatus as string || 'Nieprzypisany');
                        const toStatus = translateWooStatus(statusMetadata?.toStatus as string || statusMetadata?.newStatus as string || 'Nieprzypisany');
                        return {
                          title: 'Zmiana statusu',
                          description: entry.description || `${fromStatus} → ${toStatus}`,
                          color: 'text-accent-primary',
                          icon: '🔄'
                        };
                      }
                      case 'email_sent': {
                        // Handle email sent from OrderHistory
                        const emailMetadata = entry.metadata as Record<string, unknown> | null;
                        const toEmail = emailMetadata?.toEmail as string || emailMetadata?.email as string || '';
                        const templateName = emailMetadata?.templateName as string || '';
                        return {
                          title: 'Email wysłany',
                          description: entry.description || (templateName ? `${templateName}${toEmail ? ` do ${toEmail}` : ''}` : toEmail || 'Wysłano wiadomość'),
                          color: 'text-accent-info',
                          icon: '✉️'
                        };
                      }
                      case 'invoice_created': {
                        // Handle lowercase invoice_created from OrderHistory (automation)
                        const invMetadata = entry.metadata as Record<string, unknown> | null;
                        const invNumber = invMetadata?.invoiceNumber as string || '';
                        return {
                          title: 'Wystawiono fakturę',
                          description: entry.description || (invNumber ? `Faktura: ${invNumber}` : 'Utworzono fakturę VAT'),
                          color: 'text-warning',
                          icon: '📄'
                        };
                      }
                      case 'invoice_downloaded': {
                        const invoiceMetadata = entry.metadata as Record<string, unknown> | null;
                        const downloadedInvoiceNumber = invoiceMetadata?.invoiceNumber as string || '';
                        return {
                          title: 'Pobranie faktury przez klienta',
                          description: entry.description || (downloadedInvoiceNumber
                            ? `Klient pobrał dokument ${downloadedInvoiceNumber}`
                            : 'Klient pobrał dokument z panelu klienta'),
                          color: 'text-success',
                          icon: '📥'
                        };
                      }
                      case 'shipment_created': {
                        // Handle lowercase shipment_created from OrderHistory (automation)
                        const shipMetadata = entry.metadata as Record<string, unknown> | null;
                        const tracking = shipMetadata?.trackingNumber as string || '';
                        return {
                          title: 'Nadano przesyłkę',
                          description: entry.description || (tracking ? `List przewozowy: ${tracking}` : 'Utworzono przesyłkę'),
                          color: 'text-accent-info',
                          icon: '📦'
                        };
                      }
                      case 'note_added': {
                        // Handle note_added from OrderHistory
                        return {
                          title: 'Dodano notatkę',
                          description: entry.description || 'Dodano notatkę do zamówienia',
                          color: 'text-accent-secondary',
                          icon: '📝'
                        };
                      }
                      case 'tag_added': {
                        // Handle tag_added from OrderHistory
                        const tagMetadata = entry.metadata as Record<string, unknown> | null;
                        const tagName = tagMetadata?.tagName as string || '';
                        return {
                          title: 'Dodano etykietę',
                          description: tagName ? `Etykieta: ${tagName}` : entry.description || 'Dodano etykietę',
                          color: 'text-success',
                          icon: '🏷️'
                        };
                      }
                      case 'NOTE_ADDED': {
                        const noteMetadata = entry.metadata as Record<string, unknown> | null;
                        const preview = noteMetadata?.preview as string || '';
                        return {
                          title: 'Dodano notatkę wewnętrzną',
                          description: preview ? `"${preview}${preview.length >= 100 ? '...' : ''}"` : entry.description || '',
                          color: 'text-accent-secondary',
                          icon: '📝'
                        };
                      }
                      case 'NOTE_UPDATED': {
                        const noteMetadata = entry.metadata as Record<string, unknown> | null;
                        const preview = noteMetadata?.preview as string || '';
                        return {
                          title: 'Edytowano notatkę wewnętrzną',
                          description: preview ? `"${preview}${preview.length >= 100 ? '...' : ''}"` : entry.description || '',
                          color: 'text-accent-secondary',
                          icon: '✏️'
                        };
                      }
                      case 'NOTE_DELETED': {
                        const noteMetadata = entry.metadata as Record<string, unknown> | null;
                        const preview = noteMetadata?.preview as string || '';
                        return {
                          title: 'Usunięto notatkę wewnętrzną',
                          description: preview ? `"${preview}${preview.length >= 100 ? '...' : ''}"` : entry.description || '',
                          color: 'text-danger',
                          icon: '🗑️'
                        };
                      }
                      case 'REFUND_CREATED': {
                        const refundMetadata = entry.metadata as Record<string, unknown> | null;
                        const refundAmount = refundMetadata?.amount as number;
                        const refundDesc = refundMetadata?.description as string || '';
                        return {
                          title: 'Zwrot zrealizowany',
                          description: entry.description || (refundAmount ? `${refundAmount.toFixed(2)} ${order.currency}${refundDesc ? ` - ${refundDesc}` : ''}` : 'Zwrot środków'),
                          color: 'text-success',
                          icon: '💸'
                        };
                      }
                      case 'REFUND_FAILED': {
                        const refundMetadata = entry.metadata as Record<string, unknown> | null;
                        const refundAmount = refundMetadata?.amount as number;
                        const refundError = refundMetadata?.error as string || '';
                        return {
                          title: 'Błąd zwrotu',
                          description: entry.description || (refundError ? `${refundError}` : (refundAmount ? `Próba zwrotu ${refundAmount.toFixed(2)} ${order.currency}` : 'Błąd przetwarzania zwrotu')),
                          color: 'text-danger',
                          icon: '❌'
                        };
                      }
                      case 'REFUND_PROCESSING': {
                        const refundMetadata = entry.metadata as Record<string, unknown> | null;
                        const refundAmount = refundMetadata?.amount as number;
                        return {
                          title: 'Zwrot w trakcie',
                          description: entry.description || (refundAmount ? `Przetwarzanie zwrotu ${refundAmount.toFixed(2)} ${order.currency}` : 'Przetwarzanie zwrotu'),
                          color: 'text-warning',
                          icon: '⏳'
                        };
                      }
                      case 'address_corrected':
                        return {
                          title: 'Korekta adresu',
                          description: entry.description || 'Adres został automatycznie skorygowany',
                          color: 'text-orange-400',
                          icon: '📍'
                        };
                      case 'woo_status_synced': {
                        const wooMeta = entry.metadata as Record<string, unknown> | null;
                        const wooStatus = wooMeta?.wooStatus as string || '';
                        // L8: Translate WooCommerce status to Polish
                        const translatedWooStatus = wooStatus ? translateWooStatus(wooStatus) : '';
                        return {
                          title: 'Synchronizacja statusu WooCommerce',
                          description: entry.description || (translatedWooStatus ? `Status "${translatedWooStatus}" zsynchronizowany do WooCommerce` : 'Status zsynchronizowany do WooCommerce'),
                          color: 'text-purple-400',
                          icon: '🔄'
                        };
                      }
                      case 'blacklist_added': {
                        // R5.2 fix: user-friendly message (poprzednio widać było raw action).
                        const blMeta = entry.metadata as Record<string, unknown> | null;
                        const identifierType = blMeta?.identifierType as string || '';
                        const typeLabel = identifierType === 'email' ? 'adres email' : identifierType === 'phone' ? 'numer telefonu' : 'adres';
                        return {
                          title: 'Klient dodany do czarnej listy',
                          description: entry.description || `Dodano ${typeLabel} klienta do czarnej listy - zamówienie pominięte przy masowym nadawaniu.`,
                          color: 'text-danger',
                          icon: '🚫'
                        };
                      }
                      case 'blacklist_removed': {
                        const blMeta = entry.metadata as Record<string, unknown> | null;
                        const identifierType = blMeta?.identifierType as string || '';
                        const typeLabel = identifierType === 'email' ? 'adres email' : identifierType === 'phone' ? 'numer telefonu' : 'adres';
                        return {
                          title: 'Klient usunięty z czarnej listy',
                          description: entry.description || `Usunięto ${typeLabel} klienta z czarnej listy - zamówienie znów bierze udział w nadawaniu.`,
                          color: 'text-success',
                          icon: '✅'
                        };
                      }
                      case 'HOLD_ON':
                        return {
                          title: 'Wstrzymano (poczekalnia)',
                          description: 'Zamówienie pominięte przy masowym nadawaniu listów',
                          color: 'text-warning',
                          icon: '⏸️',
                        };
                      case 'HOLD_OFF':
                        return {
                          title: 'Wznowiono z poczekalni',
                          description: 'Zamówienie znów bierze udział w masowym nadawaniu',
                          color: 'text-success',
                          icon: '▶️',
                        };
                      case 'SHIPPING_UPDATE': {
                        const oldShip = oldVals?.shippingTotal;
                        const newShip = newVals?.shippingTotal;
                        const fmt = (v: unknown) => {
                          const n = typeof v === 'number' ? v : Number(v ?? 0);
                          return Number.isFinite(n) ? n.toFixed(2) : '0.00';
                        };
                        return {
                          title: 'Zmiana kosztu wysyłki',
                          description: oldShip !== undefined && newShip !== undefined
                            ? `${fmt(oldShip)} → ${fmt(newShip)} ${order.currency}`
                            : 'Zaktualizowano koszt wysyłki',
                          color: 'text-accent-secondary',
                          icon: '🚚',
                        };
                      }
                      default:
                        return {
                          title: entry.action,
                          description: '',
                          color: 'text-text-secondary',
                          icon: '📌'
                        };
                    }
                  };

                  const formatted = formatHistoryEntry();

                  return (
                    <div
                      key={entry.id}
                      className="p-3 bg-surface-hover/50 rounded-lg border border-border-subtle overflow-hidden"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-base">{formatted.icon}</span>
                            <span className={`text-sm font-medium ${formatted.color}`}>
                              {formatted.title}
                            </span>
                          </div>
                          {formatted.description && (
                            <p className="text-sm text-text-secondary ml-6 break-words overflow-hidden">
                              {formatted.description}
                            </p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs text-text-muted">
                            {new Date(entry.createdAt).toLocaleDateString('pl-PL', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                          {entry.user && (
                            <p className="text-xs text-text-disabled mt-0.5">
                              {entry.user.firstName || entry.user.lastName
                                ? `${entry.user.firstName || ''} ${entry.user.lastName || ''}`.trim()
                                : entry.user.email}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState
                icon={History}
                title="Brak historii zmian dla tego zamówienia."
                size="sm"
              />
            )}
            </div>
          </div>
        </div>
      </div>

    </div>,
    document.body,
  );
}
